/**
 * PostBody — Mora's canonical post inspector body. v5 contract, revised for
 * the composer/chips fusion (design-polish pass, 2026-08-15 — see
 * HANDOFF-EDITOR-UNIFICATION-2026-08-12 §8.9/§8.14).
 *
 * Layout:
 *
 *   ┌────────────────────────────┬─────────────────────────────┐
 *   │                            │ Brand · Platform post · meta [X]
 *   │                            ├─────────────────────────────┤
 *   │   POST PREVIEW STAGE       │ Caption editor card         │
 *   │   (PlatformPreview clean,  │ ✨ chips + Tell Mora what   │
 *   │    centered, max-w-[420],  │    to change (ONE composer) │
 *   │    full vertical bleed —   │ Readiness sentence / pill   │
 *   │    no top stripe)          │ Scheduled date row           │
 *   │ [IG][TT][LI] · 1:1 4:5     │ (scrollable middle)          │
 *   │                            ├─────────────────────────────┤
 *   │                            │ icons | Approve & schedule  │
 *   └────────────────────────────┴─────────────────────────────┘
 *
 * v5 contract (locked at /style-guide/compositions/unified-media-studio,
 * revised where the fusion below superseded it):
 *   - ONE-LINE header: brand+format title (NOT caption excerpt) + status
 *     meta as shrink-0 inline + close. No eyebrow row.
 *   - Asset stays clean — no isolated platform-tab top stripe. Icon-only
 *     platform switcher sits inline next to the AspectRatioPicker at the
 *     bottom of the asset pane.
 *   - AI suggestion chips are FUSED into `MediaStudioCustomPromptInput`'s own
 *     `quickActions` slot (`composerBlock`/`captionAiQuickActions` below) —
 *     they render INSIDE the composer's rounded card (`rounded-3xl border
 *     border-border/60 bg-card`), not as an uncontained cluster above it.
 *     This resolved a founder objection that the freeform prompt and the
 *     verb chips were two doors to the same job ("rewrite this caption").
 *     Measured live: the chips are `rgb(113,113,122)` (muted-foreground) at
 *     11px/500 with no border — plain text, not skill-purple; only the
 *     leading sparkle icon carries the AI signal now, not the chips
 *     themselves.
 *   - Composer has NO border-t — its own rounded card border is the
 *     visual edge; doubling fragmented the rail.
 *   - Status-aware right primary CTA in the toolbar:
 *       Dirty     → "Save draft"
 *       Draft     → "Saved" state + split "Post now" action / schedule caret
 *       Scheduled → "Saved" state + split "Post now" action / reschedule caret
 *       Published → "View live"            (ArrowSquareOut)
 *     Add-to-project moves to the icon cluster as a secondary affordance.
 */
import {
  ArrowCounterClockwiseIcon,
  ArrowLeftIcon,
  ArrowsOutCardinalIcon,
  ArticleIcon,
  CalendarIcon,
  CalendarXIcon,
  CardsThreeIcon,
  CaretDownIcon,
  ChartLineUpIcon,
  CheckIcon,
  ClockIcon,
  CopyIcon,
  DotsThreeIcon,
  FrameCornersIcon,
  MagnifyingGlassIcon,
  PaperPlaneTiltIcon,
  PencilSimpleIcon,
  PlugsConnectedIcon,
  PlusIcon,
  SealCheckIcon,
  SparkleIcon,
  TrashIcon,
  WarningCircleIcon,
  WarningIcon,
  XIcon,
} from '@phosphor-icons/react';
import type { TikTokPostSettings } from '@shared/tiktok-post-params';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import {
  type PostingReadinessResult,
  type PostingReadinessState,
  evaluatePlatformReadiness,
} from '../../../../../shared/posting-readiness';

import { PostRevenueCard } from './PostRevenueCard';
import { ToneControl } from './ToneControl';
import { AspectRatioPicker } from './post/AspectRatioPicker';
import {
  type DestinationAccountOption,
  DestinationAccountPicker,
} from './post/DestinationAccountPicker';
import { FailureDetailsLink } from './post/FailureDetailsLink';
import { PlacementPicker } from './post/PlacementPicker';
import { PostMediaManager, type PostMediaManagerHandle } from './post/PostMediaManager';
import { RepostAfterCheckAction } from './post/RepostAfterCheckAction';
import { TargetCoverEditor, type TargetCoverSummary } from './post/TargetCoverEditor';
import { TikTokSettingsCard } from './post/TikTokSettingsCard';
import { UnsentPlatformsAction } from './post/UnsentPlatformsAction';
import {
  applyChildCommand,
  readChildMediaState,
  reconcileChildMediaUrls,
} from './post/childMediaState';
import {
  MINOR_CROP_THRESHOLD,
  cropSignalFor,
  edgeCropFraction,
  parseAspectValue,
} from './post/cropSignal';
import {
  type CommitResult,
  type MediaKeysCommitQueue,
  type QueuedCommitInput,
  createMediaKeysCommitQueue,
  decrementSaving,
  incrementSaving,
  isMergeRpcMissingError,
} from './post/mediaKeysCommitQueue';
import {
  type EntryPatch,
  type MediaKeysBag,
  type PerPlatformMap,
  applyPerPlatformPatches,
  entryPatchToJsonPatch,
  placementPatch,
  publishOptionsPatch,
  readTikTokSettings,
  tiktokSettingsPatch,
} from './post/mediaKeysWriter';
import {
  POST_PERFORMANCE_HREF,
  canSeePostPerformance,
  canSendPostForApproval,
  planHref,
  sourceArticleHref,
} from './post/postLinks';
import { resolveMediaProps } from './post/resolveMediaProps';

import { CaptionDisplay } from '@/components/create/v2/CaptionDisplay';
import { getPlatformBrandIcon } from '@/components/icons/platform-brand-icon';
import { PlatformPlugMenu } from '@/components/platforms/PlatformPlugMenu';
import { LinkedInTagsField } from '@/components/posts/LinkedInTagsField';
import { MetaPublishOptions } from '@/components/posts/MetaPublishOptions';
import { PublishedMoment } from '@/components/posts/PublishedMoment';
import { SaveToProjectMenu } from '@/components/projects/SaveToProjectMenu';
import { NewApprovalDialog } from '@/components/reach/approvals/NewApprovalDialog';
import { candidateFromPost } from '@/components/reach/approvals/postCandidate';
import { SchedulePicker } from '@/components/schedule/SchedulePicker';
import { useSettingsDialog } from '@/components/settings/SettingsDialogProvider';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { CircleIconButton } from '@/components/ui/circle-icon-button';
import { Confirm } from '@/components/ui/confirm';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { QualityChip } from '@/components/ui/inspector/QualityChip';
import { InspectorBreadcrumb } from '@/components/ui/inspector/bodies/InspectorBreadcrumb';
import { useInspector } from '@/components/ui/inspector/hooks/useInspector';
import type { InspectorBreadcrumbItem } from '@/components/ui/inspector/types';
import { platformLabel, platformToKind } from '@/components/ui/platform-preview';
import { EditorPlatformPreview } from '@/components/ui/platform-preview/EditorPlatformPreview';
import { SafeZoneOverlay } from '@/components/ui/platform-preview/SafeZoneOverlay';
import {
  platformNaturalMaxWidth,
  platformPreviewChromeHeight,
  rendersFullBleedStage,
} from '@/components/ui/platform-preview/types';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SegmentedToggle } from '@/components/ui/segmented-toggle';
import { SharedByChip } from '@/components/ui/shared-by-chip';
import { Skeleton } from '@/components/ui/skeleton';
import { Slider } from '@/components/ui/slider';
import { Spinner } from '@/components/ui/spinner';
import { StatusBadge, type StatusTone } from '@/components/ui/status-badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useTeamAuthorNames } from '@/hooks/account/useTeamAuthorNames';
import { usePostPlanLink } from '@/hooks/projects/usePostPlanLink';
import { useAuth } from '@/hooks/use-auth';
import { useIsMobile } from '@/hooks/use-mobile';
import { useBrandKit } from '@/hooks/useBrandKit';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useInstagramPlannerTarget } from '@/hooks/useInstagramProfilePlanner';
import { useInvalidatePostSurfaces } from '@/hooks/useInvalidatePostSurfaces';
import { useLinkedInLinkPreview } from '@/hooks/useLinkedInLinkPreview';
import { invalidateNeedsAttention } from '@/hooks/useNeedsAttention';
import { usePlatformIntegrations } from '@/hooks/usePlatformIntegrations';
import { usePost } from '@/hooks/usePost';
import { usePostMetrics } from '@/hooks/usePostMetrics';
import { usePublishNow } from '@/hooks/usePublishNow';
import { useSuggestedPostTime } from '@/hooks/useSuggestedPostTime';
import { useTikTokCreatorInfo } from '@/hooks/useTikTokCreatorInfo';
import { useUnresolvedPublishTargets } from '@/hooks/useUnresolvedPublishTargets';
import { useVideoFacts } from '@/hooks/useVideoFacts';
import {
  EXPLICIT_TITLE_MAX_WORDS,
  buildArtifactTitle,
  truncateToWords,
} from '@/lib/artifact-title';
import {
  buildDestinationOptions,
  isAwaitingLinkedInDestination,
} from '@/lib/connections/destination-options';
import { resolveAutoScheduleSlot } from '@/lib/content-engine/auto-schedule-post';
import { type QualityBag, type QualityFix, gradeArtifact } from '@/lib/content-quality';
import { friendlyMessageWithRetry } from '@/lib/errors/friendly-errors';
import { isTargetNativeCoverEditorEnabled } from '@/lib/feature-flags';
import { LINKEDIN_AGENT_SOURCE_KIND, linkedInArticleCard } from '@/lib/linkedin-agent/draft';
import { inferMediaType } from '@/lib/media/infer-media-type';
import { computeMaxZoom } from '@/lib/platform-matrix/crop-geometry';
import type { Placement } from '@/lib/platform-matrix/placement-matrix';
import {
  type MediaKind,
  connectionPublishablePlacements,
  isPlacementOverrideHonored,
  resolvePlacement,
} from '@/lib/platform-matrix/placement-resolution';
import { getPlatformLabel } from '@/lib/platforms/identity';
import type { PlatformId } from '@/lib/platforms/registry';
import { STATUS_DOT_COLORS, STATUS_LABELS, STATUS_PRIMARY_CTA } from '@/lib/post-status';
import { captionChipIsDisabled, captionChipIsSpinning } from '@/lib/posts/caption-action-state';
import { captionEditModeForStatus } from '@/lib/posts/caption-edit-mode';
import { resolveCaptionQuickActions } from '@/lib/posts/caption-quick-actions';
import { toRewritePlatform } from '@/lib/posts/caption-writing-tools';
import {
  failedPublishablePlatforms,
  pickDefaultPlatform as pickDefaultPlatformFor,
} from '@/lib/posts/default-platform';
import { defaultPublishAction } from '@/lib/posts/default-publish-action';
import {
  LOCKED_CAPTION_TOAST_DESCRIPTION,
  isLockedEditError,
  showLockedEditToast,
} from '@/lib/posts/locked-edit-toast';
import {
  type PlatformCaptions,
  hasPlatformOverride,
  parsePlatformCaptions,
  resolvePlatformCaption,
} from '@/lib/posts/platform-caption';
import {
  enabledPlatforms as computeEnabledPlatforms,
  getDisabledPlatforms,
  withPlatformEnabled,
} from '@/lib/posts/platform-enablement';
import {
  effectivePlatformStatus,
  effectiveSendTime,
  isPlatformLocked as isPlatformLockedHelper,
  parsePlatformSchedules,
} from '@/lib/posts/platform-schedule';
import { postBreadcrumbLabel, postHeaderTitle } from '@/lib/posts/post-breadcrumb-label';
import { firstVisualMediaUrl, isDocumentMediaUrl } from '@/lib/posts/post-media-visual';
import { meaningfulPostTitle } from '@/lib/posts/post-title';
import { railLinesFor, reconcileVerdictWithReadiness } from '@/lib/posts/reconcile-readiness';
import { runCaptionEdit as runCaptionEdit_ } from '@/lib/posts/run-caption-edit';
import { runCaptionFanOut } from '@/lib/posts/run-caption-fan-out';
import { type SchedulePartition, describeScheduleOutcome } from '@/lib/posts/schedule-partition';
import { createSaveSerializer } from '@/lib/posts/serialized-save';
import {
  DEFAULT_PLANNING_TIME,
  exactTimeValueFromDate,
  formatWithZone,
} from '@/lib/posts/time-select';
import { listPlatformNames, partialDelivery } from '@/lib/posts/unsent-platforms';
import {
  isUnsupportedPublishPlatform,
  withUnsupportedPublishBlock,
} from '@/lib/posts/unsupported-publish';
import { PREVIEW_ACCOUNT_MESSAGE, useIsPreviewAccount } from '@/lib/preview-account';
import { explainPostFailure } from '@/lib/publishing/publish-failures';
import {
  BRAND_TONE_OPTION_ID,
  resolveToneInstruction,
  toneInstructionFor,
  toneLabelFor,
  useBrandTone,
} from '@/lib/tone';
import {
  ARTIFACT_PREVIEW_CARD,
  ARTIFACT_PREVIEW_STAGE,
  MENU_ITEM_MOBILE,
  QUIET_ACTION,
  TEXT_FADE_RIGHT,
} from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import { postService, targetCoverService, unresolvedPostCoverIssue } from '@/services/data';
import type { LinkedInMention } from '@/services/data/LinkedInTagsService';
import { userPreferencesService } from '@/services/data/UserPreferencesService';
import { rewriteCaption } from '@/services/media/caption-rewrite';
import { suggestCaptionsBatch } from '@/services/media/caption-suggest';
import { showInfoToast } from '@/utils/toast';

interface PostBodyProps {
  postId: string;
  targetId?: string;
  onClose: () => void;
  breadcrumbs?: InspectorBreadcrumbItem[];
  studioPanel?: React.ReactNode;
  backToProject?: { id: string; name: string; onReturn: () => void };
}

// PerPlatformMap/PerPlatformEntry (aspectRatio/focalX/focalY/zoom/variantUrl/
// placement) now live in ./post/mediaKeysWriter — the single canonical shape
// for media_keys.perPlatform[platform], shared with the merge-writer (Task A3).

function normalizePostPlatforms(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter(
        (platform): platform is string =>
          typeof platform === 'string' && platform.trim().length > 0,
      )
    : [];
}

const READINESS_META: Record<
  PostingReadinessState,
  { tone: StatusTone; label: string; icon: React.ReactNode }
> = {
  ready: {
    tone: 'success',
    label: 'Ready',
    icon: <CheckIcon className="size-3" weight="bold" />,
  },
  warning: {
    tone: 'warning',
    label: 'Review',
    icon: <WarningIcon className="size-3" weight="regular" />,
  },
  blocked: {
    tone: 'destructive',
    label: 'Blocked',
    icon: <WarningCircleIcon className="size-3" weight="fill" />,
  },
  paused: {
    tone: 'neutral',
    label: 'Paused',
    icon: <ClockIcon className="size-3" weight="regular" />,
  },
  locked_live: {
    tone: 'neutral',
    label: 'Live',
    icon: <CheckIcon className="size-3" weight="bold" />,
  },
  retrying: {
    tone: 'info',
    label: 'Retrying',
    icon: <ClockIcon className="size-3" weight="regular" />,
  },
  terminal_failed: {
    tone: 'destructive',
    label: 'Failed',
    icon: <WarningCircleIcon className="size-3" weight="fill" />,
  },
};

// ── Platform tab readiness badge ────────────────────────────────────────────
// ONE badge slot per platform tab, and SILENCE means ready. Badging the
// healthy default is decoration, and a second overlay (a divergence diamond,
// a "has its own send time" clock dot) would put MORE encodings on a 16px
// mark than the row of unlabelled circles this strip replaces — which is the
// failure being fixed, not a pattern to re-import.
type PlatformTabBadgeKind = 'destructive' | 'spinner' | 'warning' | 'success';

// Readiness and delivery are two independent state machines. They are composed
// by PHASE, not fused: before anything is attempted only readiness can speak;
// the moment a delivery FACT exists for a platform it dominates, because "it
// failed at Instagram" outranks "its caption is long". `scheduled` and
// `not_attempted` are deliberately absent from this map — nothing has been
// attempted for those, so readiness is still the honest thing to show.
const DELIVERY_BADGE_KIND: Record<string, PlatformTabBadgeKind> = {
  published: 'success',
  publishing: 'spinner',
  failed: 'destructive',
  failed_connection_removed: 'destructive',
  unverified: 'warning',
};

// Pre-send readiness → badge. `ready` and `paused` are absent on purpose:
// ready is silence, and paused is a whole-tab dim (a property of the tab, not
// an event on it) so it never competes for the badge slot.
const READINESS_BADGE_KIND: Partial<Record<PostingReadinessState, PlatformTabBadgeKind>> = {
  blocked: 'destructive',
  terminal_failed: 'destructive',
  retrying: 'spinner',
  warning: 'warning',
  locked_live: 'success',
};

// Strict precedence, applied over the UNION of whatever the two machines
// produced. Resolving by precedence rather than by "whichever branch ran
// last" means the answer can't change if the phase rule is ever loosened.
const PLATFORM_TAB_BADGE_PRECEDENCE: readonly PlatformTabBadgeKind[] = [
  'destructive',
  'spinner',
  'warning',
  'success',
];

function resolvePlatformTabBadgeKind(
  readinessState: PostingReadinessState,
  deliveryStatus: string,
): PlatformTabBadgeKind | null {
  const candidates = [DELIVERY_BADGE_KIND[deliveryStatus], READINESS_BADGE_KIND[readinessState]];
  return PLATFORM_TAB_BADGE_PRECEDENCE.find((kind) => candidates.includes(kind)) ?? null;
}

// The badge NODE is styled here, by the caller — `SegmentedToggle` is
// deliberately colour-agnostic so the primitive can never trip the
// no-hardcoded-colours gate on a consumer's behalf. `aria-hidden` because the
// tab's tooltip already spells the same state out in words; a screen reader
// should not hear a glyph read twice. `data-platform-badge` is the seam tests
// assert on — the visual encoding is a colour, which jsdom cannot judge.
// Dark `--destructive` is `0 62.8% 30.6%` (#7F1D1D) — shadcn's red-900, a
// destructive SURFACE colour built to carry light text. As a FOREGROUND glyph
// on the rail's near-black panel (#101012) it measures 1.9:1, well under the
// 3:1 WCAG 1.4.11 floor for non-text UI. Under silence-is-ready the badge is
// the ENTIRE severity signal on a tab, so in dark a blocked platform was
// indistinguishable from a ready one — a correctness bug, not a polish one.
// RETIRED the `dark:text-project-red` borrow this comment used to describe:
// `text-destructive` itself now resolves to `--destructive-text` in dark
// (index.css) — the same `--project-red` value, sourced from the token this
// problem actually belongs to instead of a borrowed identity-colour name.
const DESTRUCTIVE_ON_SURFACE = 'text-destructive';

// Subordinate to the platform mark on purpose: the brand icon renders at 12px
// (`ICON_SIZES.sm`), so an equal-sized badge made the tab read as three peers
// flanking the word. Brand > label > status.
function platformTabBadge(kind: PlatformTabBadgeKind | null): React.ReactNode {
  if (!kind) return null;
  const glyph =
    kind === 'destructive' ? (
      <WarningCircleIcon className={cn('size-2.5', DESTRUCTIVE_ON_SURFACE)} weight="fill" />
    ) : kind === 'spinner' ? (
      <Spinner size="sm" className="size-2.5" aria-label="" />
    ) : kind === 'warning' ? (
      <WarningIcon className="size-2.5 text-warning" weight="regular" />
    ) : (
      <CheckIcon className="size-2.5 text-success" weight="bold" />
    );
  return (
    <span data-platform-badge={kind} aria-hidden className="inline-flex items-center">
      {glyph}
    </span>
  );
}

// Lifecycle states (`paused`/`locked_live`/`retrying`/`terminal_failed`)
// short-circuit `evaluatePlatformReadiness` with EMPTY blockingIssues/
// warningIssues (shared/posting-readiness.ts's `lifecycleState` returns
// before the issue-collection pass ever runs) — they still fail
// `canPublish`, but with no message a `disabledActionTitle`/
// `disabledScheduleTitle` built purely from `blockingIssues[0]` can read,
// leaving a disabled button with no tooltip explaining why (review round 2,
// MINOR 3). This fills that gap without touching the shared resolver.
const LIFECYCLE_DISABLED_TITLE: Partial<Record<PostingReadinessState, string>> = {
  paused: 'is paused — resume it before publishing',
  locked_live: 'already published — this platform is locked',
  retrying: 'is retrying a previous send',
  terminal_failed: 'failed to send and needs a retry',
};

function readinessFallbackTitle(
  result: PostingReadinessResult | null | undefined,
): string | undefined {
  if (!result) return undefined;
  const reason = LIFECYCLE_DISABLED_TITLE[result.state];
  return reason ? `${platformLabel(result.platform)} ${reason}.` : undefined;
}

// `post_now` sends every enabled (attached, non-paused) platform — see the
// confirm-button `onClick` below — and `PublishService` silently partitions
// that list down to the ready subset server-side. This previews that
// partition BEFORE the request fires, reusing the SAME `platformReadinessMap`
// entry the tab chip already shows for that platform (never a second
// `evaluatePlatformReadiness` call), so the popover can never disagree with
// the chip about why a platform won't send.
function excludedPostNowReason(
  platformId: string,
  readiness: PostingReadinessResult | null | undefined,
  isPaused: boolean,
): string | null {
  if (isPaused) return `${platformLabel(platformId)} is paused.`;
  if (!readiness || readiness.canPublish) return null;
  return (
    readiness.blockingIssues[0]?.message ??
    readinessFallbackTitle(readiness) ??
    `${platformLabel(platformId)} isn't ready to post.`
  );
}

const METRIC_LABELS: Record<string, string> = {
  impressions: 'Impressions',
  reach: 'Reach',
  views: 'Views',
  likes: 'Likes',
  likes_count: 'Likes',
  comments: 'Comments',
  comments_count: 'Comments',
  shares: 'Shares',
  shares_count: 'Shares',
  saves: 'Saves',
  clicks: 'Clicks',
  engagement_rate: 'Engagement',
};

/** Shared mutation-vars shape for schedulePostMutation/planDraftMutation:
 *  both write an id+scheduledAt pair and accept the same `silent` escape
 *  hatch (suppress the mutation's own generic toast when the caller — e.g.
 *  handleAutoSchedule's "Scheduled for …" toast — already confirmed the
 *  write with a more specific message, so a single click never stacks two
 *  success toasts). */
interface ScheduleMutationVars {
  id: string;
  scheduledAt: string;
  silent?: boolean;
  reportSkips?: boolean;
}

function formatMetricNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function MetricsGrid({ metrics }: { metrics: Record<string, unknown> }) {
  const displayMetrics = Object.entries(metrics).filter(
    ([key, value]) => key in METRIC_LABELS && typeof value === 'number',
  ) as [string, number][];

  if (displayMetrics.length === 0) return null;

  return (
    <div className="grid grid-cols-2 gap-2">
      {displayMetrics.map(([key, value]) => (
        <div key={key} className="rounded-lg bg-muted/50 px-3 py-2">
          <span className="text-detail text-muted-foreground">{METRIC_LABELS[key]}</span>
          <p className="text-sm font-medium text-foreground">
            {key === 'engagement_rate' ? `${(value * 100).toFixed(1)}%` : formatMetricNumber(value)}
          </p>
        </div>
      ))}
    </div>
  );
}

function DestinationMetrics({
  destinations,
  metricsByDestination,
  platform,
}: {
  destinations: Array<{ targetId: string; platform: string; label: string }>;
  metricsByDestination: Record<string, Record<string, unknown>>;
  platform: string;
}) {
  const reportingDestinations = destinations.filter(
    (destination) =>
      destination.platform === platform &&
      Object.keys(metricsByDestination[destination.targetId] ?? {}).length > 0,
  );

  if (reportingDestinations.length < 2) return null;

  return (
    <div className="space-y-2 border-t border-border/40 pt-3">
      <p className="text-detail text-muted-foreground/70">By destination</p>
      {reportingDestinations.map((destination) => (
        <div key={destination.targetId} className="rounded-lg bg-muted/50 px-3 py-2">
          <p className="text-caption font-medium text-foreground/85">{destination.label}</p>
          <MetricsGrid metrics={metricsByDestination[destination.targetId]} />
        </div>
      ))}
    </div>
  );
}

function PostBodyLoadingSkeleton(): JSX.Element {
  return (
    <div className="flex h-full min-h-0 bg-background" aria-label="Loading post">
      <div className="hidden min-h-0 flex-1 items-center justify-center p-6 md:flex">
        <Skeleton className="h-full max-h-full w-full max-w-sm rounded-xl" />
      </div>
      <div className="flex min-h-0 flex-1 flex-col md:border-l md:border-border/40">
        <div className="flex h-12 shrink-0 items-center gap-2 border-b border-border/40 px-4">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="ml-auto size-2 rounded-full" />
          <Skeleton className="size-8 rounded-full" />
        </div>
        <div className="flex flex-1 flex-col gap-5 overflow-hidden px-4 py-5">
          <div className="flex items-center gap-2">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
          <Skeleton className="h-24 w-full rounded-lg" />
          <div className="flex items-center gap-2">
            <Skeleton className="size-14 rounded-lg" />
            <Skeleton className="size-14 rounded-lg" />
          </div>
        </div>
        <div className="shrink-0 px-4 py-3">
          <Skeleton className="h-10 w-full rounded-full" />
        </div>
        <div className="flex h-12 shrink-0 items-center justify-between border-t border-border/40 px-4">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-8 w-28 rounded-full" />
        </div>
      </div>
    </div>
  );
}

function joinPlatformNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export const PostBody: React.FC<PostBodyProps> = ({
  postId,
  targetId,
  onClose,
  breadcrumbs,
  studioPanel,
  backToProject,
}) => {
  const { openSettings } = useSettingsDialog();
  const { post, isLoading, isFetching, error: postError, refetch } = usePost(postId);
  const {
    data: loadedTarget,
    isLoading: isTargetLoading,
    isError: isTargetError,
  } = useInstagramPlannerTarget(targetId);
  const exactTarget = loadedTarget?.postId === postId ? loadedTarget : null;
  // BI-audit fix #3: resolve the POST'S OWN kit (social_posts.brand_kit_id,
  // stamped at write-time by BI-audit fix #4) rather than an arbitrary
  // "effective" kit. useBrandKit(kitId) already ownership-scopes the lookup
  // (.eq('user_id', userId).eq('id', kitId)) and falls back to the prior
  // arbitrary-effective-kit resolution when kitId is absent/invalid (legacy
  // pre-migration posts) — so this is a pure correctness fix with no
  // behavior change for rows that predate fix #4's column.
  const { brandKit } = useBrandKit(post?.brand_kit_id);
  // The account's active brand voice, resolved from the POST'S OWN kit for the
  // same reason `useBrandKit` is above — a caption rewritten "in the brand
  // voice" must use the voice this post was written against, not an arbitrary
  // effective one. Feeds BOTH the tone control's default row and the tone
  // clause appended to every other caption verb below.
  const { brandTone } = useBrandTone(post?.brand_kit_id ?? null);
  const { connectedAccounts } = usePlatformIntegrations();
  const { session } = useAuth();
  // TEAM-1: object-level "who made this" — only meaningful (and only
  // rendered) for a team member viewing a teammate's post; the account
  // owner's own view is unchanged. See useTeamAuthorNames for the full
  // gating (owner-view / self-authored / unresolved all render nothing).
  const { resolveAuthorLabel } = useTeamAuthorNames();
  // authored_by is the real author on a shared account (user_id is always
  // the owner); fall back to user_id only for legacy/pre-migration rows.
  const postAuthorLabel = resolveAuthorLabel(post?.authored_by ?? post?.user_id);
  const isMobile = useIsMobile();
  // Mobile-only: Preview / Edit segmented view (replaces one long vertical
  // scroll — see the isMobile branch below for the full rationale).
  /**
   * The measured height of everything the preview column renders UNDER the
   * card — the crop warning, the placement and ratio pickers, the zoom slider.
   * Declared here, above every early return, because it is a hook: an earlier
   * pass put it next to the arithmetic that consumes it and tripped
   * rules-of-hooks.
   *
   * Why it exists at all is in the comment on `previewStageReservedHeight`.
   * The short version: that reserve used to be a sum of hand-maintained pixel
   * constants, each shadowing a class string elsewhere in this file, and three
   * separate incidents are recorded there where one drifted and the pane
   * silently began to scroll. The founder reported a fourth on 2026-08-21.
   *
   * Self-measuring on purpose. The row gap comes from the PARENT's own
   * computed `rowGap` rather than from a constant passed in, so this observer
   * has no dependency on any value computed later in the render and its deps
   * array is genuinely empty. One less number that can drift.
   */
  const stageExtrasRef = useRef<HTMLDivElement | null>(null);
  const [measuredExtrasHeight, setMeasuredExtrasHeight] = useState<number | null>(null);

  useEffect(() => {
    const el = stageExtrasRef.current;
    if (!el) return;
    // `getBoundingClientRect` rather than `offsetHeight`: these rows carry
    // sub-pixel heights at some zoom levels, and rounding DOWN is precisely
    // the direction that reintroduces the overflow this replaces.
    const sync = () => {
      const height = el.getBoundingClientRect().height;
      const parentGap = el.parentElement
        ? parseFloat(getComputedStyle(el.parentElement).rowGap || '0') || 0
        : 0;
      setMeasuredExtrasHeight(height > 0 ? height + parentGap : 0);
    };
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const [mobileTab, setMobileTab] = useState<'preview' | 'edit'>('preview');
  const postMediaManagerRef = useRef<PostMediaManagerHandle>(null);
  const [coverEditorOpen, setCoverEditorOpen] = useState(false);
  const [targetCoverSummary, setTargetCoverSummary] = useState<TargetCoverSummary | null>(null);
  const queryClient = useQueryClient();
  const invalidatePostSurfacesFor = useInvalidatePostSurfaces();
  const [captionSavedAt, setCaptionSavedAt] = useState<number | null>(null);
  const [composerDraft, setComposerDraft] = useState('');
  const [describeOpen, setDescribeOpen] = useState(false);
  const [draftingCaption, setDraftingCaption] = useState(false);
  // Tone popover for the caption Writing-Tools chip row (§5.1).
  const [toneOpen, setToneOpen] = useState(false);
  // The post's CHOSEN tone. Null is not "no tone" — it means "inherit the
  // brand voice", which is why the trigger keeps its own default label rather
  // than naming a preset (see toneLabelFor).
  const toneOverride = post?.tone_override ?? null;
  const [operatingAction, setOperatingAction] = useState<string | null>(null);
  const isOperating = operatingAction !== null;
  // One counter (not a boolean) behind the header's Draft/Saving indicator —
  // caption autosave, a blur commit, a title save, and a media/platform
  // commit can all be in flight at once, and the indicator must stay on
  // "Saving…" until the LAST of them settles, not flicker back to the
  // status label the moment any single one finishes.
  const [autosaveCount, setAutosaveCount] = useState(0);
  const beginAutosave = useCallback(() => setAutosaveCount((n) => n + 1), []);
  const endAutosave = useCallback(() => setAutosaveCount((n) => Math.max(0, n - 1)), []);
  const isAutosaving = autosaveCount > 0;
  // Staged caption rewrite for scheduled posts (spec §5.3, Task 4).
  // When `captionEditModeForStatus` returns 'stage', the revised text lands
  // here instead of being saved immediately. The inline compare panel reads
  // this to show Original ⇄ Revised + Keep / Revert.
  const [captionRevision, setCaptionRevision] = useState<{
    original: string;
    revised: string;
  } | null>(null);
  // Safe-zone overlay is on by default for chrome formats (the point of
  // previewing a Reel); the control-row toggle lets the creator hide it.
  const [showSafeZones, setShowSafeZones] = useState(true);
  // Source media's intrinsic ratio, for the honest crop signal. Images are
  // probed (no persisted dims); video uses the persisted video_aspect_ratio.
  const [sourceRatio, setSourceRatio] = useState<number | null>(null);
  // Native multi-image posts may deliberately contain mixed ratios (notably
  // LinkedIn and Facebook). Keep each successful browser probe so advancing a
  // carousel slide does not keep rendering child zero's frame. This is
  // display-only metadata; the immutable target snapshot remains the source
  // of delivery truth.
  const [sourceRatiosByUrl, setSourceRatiosByUrl] = useState<Record<string, number>>({});
  // The editor owns the active child. Keeping this state here, rather than
  // hiding it inside a presentation shell, lets the stage reserve follow the
  // same child whose frame is visible. This is intentionally an index over the
  // current URL order; child identity/persisted per-child framing is a separate
  // schema problem and is not inferred from array position.
  const [carouselActiveIndex, setCarouselActiveIndex] = useState(0);
  // Natural pixel WIDTH of the probed image — feeds computeMaxZoom so the zoom
  // slider never lets a creator zoom the on-screen preview past native
  // resolution (visibly soft). Height isn't stored separately: it's always
  // derivable as sourceNaturalWidth / sourceRatio (both come from the same
  // probe below), and storing both would be two state slots that must stay in
  // sync. Video has no zoom control, so this stays null there.
  const [sourceNaturalWidth, setSourceNaturalWidth] = useState<number | null>(null);
  // Rendered size of the preview frame (not just its aspect) — the other input
  // computeMaxZoom needs. Tracked live via ResizeObserver so the cap stays
  // correct across breakpoint/pane-width changes, not just the initial paint.
  const previewFrameRef = useRef<HTMLDivElement>(null);
  const [frameSize, setFrameSize] = useState<{ w: number; h: number } | null>(null);
  useLayoutEffect(() => {
    const element = previewFrameRef.current;
    if (!element) return;
    const update = () => {
      const rect = element.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setFrameSize((cur) =>
          cur && cur.w === rect.width && cur.h === rect.height
            ? cur
            : { w: rect.width, h: rect.height },
        );
      }
    };
    update();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    observer?.observe(element);
    return () => observer?.disconnect();
  }, []);
  // Last genuinely-known effective media ratio, retained across the async gap
  // between "new media attached" and "probe resolved its real aspect" so the
  // preview never has to render at a wrong guess in between (see
  // effectiveMediaRatio below). Defaults to square only for the very first
  // paint of a component instance that has never known a real ratio.
  //
  // Reset on post IDENTITY change (post?.id), not on every probeUrl change —
  // the Inspector reuses one PostBody instance across different posts
  // (openSubject swaps the subject without unmounting), so without this the
  // ref would carry a PREVIOUS post's ratio into a newly-opened post's probe
  // gap, reintroducing the exact flash this fix targets (review, 2026-07-01).
  // Mirrors perPlatformOverride's identical post?.id-keyed reset below.
  const lastKnownMediaRatioRef = useRef<number>(1);
  useEffect(() => {
    lastKnownMediaRatioRef.current = 1;
  }, [post?.id]);

  const probeUrl = firstVisualMediaUrl(post?.media_urls) ?? null;
  useEffect(() => {
    setSourceRatio(null);
    setSourceNaturalWidth(null);
    if (!probeUrl) return;
    const isVid = post?.media_type === 'video' || /\.(mp4|mov|webm|m4v)(\?|$)/i.test(probeUrl);
    if (isVid) {
      setSourceRatio(parseAspectValue(post?.video_aspect_ratio));
      return;
    }
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled && img.naturalWidth > 0 && img.naturalHeight > 0) {
        setSourceRatio(img.naturalWidth / img.naturalHeight);
        setSourceNaturalWidth(img.naturalWidth);
      }
    };
    img.onerror = () => {
      /* unknown ratio → no crop warning (graceful, like a CORS-blocked probe) */
    };
    img.src = probeUrl;
    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [probeUrl, post?.media_type, post?.video_aspect_ratio]);

  const mediaProbeKey = (post?.media_urls ?? []).join('\u0000');
  const mediaProbeUrls = useMemo(
    () =>
      mediaProbeKey
        ? mediaProbeKey
            .split('\u0000')
            .filter((url) => !/\.(mp4|mov|webm|m4v)(\?|$)/i.test(url) && !isDocumentMediaUrl(url))
        : [],
    [mediaProbeKey],
  );
  useEffect(() => {
    setSourceRatiosByUrl({});
    if (mediaProbeUrls.length === 0) return;

    let cancelled = false;
    for (const url of mediaProbeUrls) {
      const image = new Image();
      image.onload = () => {
        if (!cancelled && image.naturalWidth > 0 && image.naturalHeight > 0) {
          const ratio = image.naturalWidth / image.naturalHeight;
          setSourceRatiosByUrl((current) =>
            current[url] === ratio ? current : { ...current, [url]: ratio },
          );
        }
      };
      image.src = url;
    }
    return () => {
      cancelled = true;
    };
  }, [mediaProbeUrls]);

  useEffect(() => {
    if (captionSavedAt == null) return;
    const t = window.setTimeout(() => setCaptionSavedAt(null), 1500);
    return () => window.clearTimeout(t);
  }, [captionSavedAt]);

  const { data: scheduledDateStrings = [] } = useQuery({
    queryKey: ['scheduled-post-dates', session?.user?.id || 'unauthenticated', post?.id ?? null],
    queryFn: async () => {
      const result = await postService.getScheduledPostDates(post?.id);
      if (result.error) throw new Error(result.error.message);
      return result.data ?? [];
    },
    enabled: !!session?.user?.id,
    staleTime: 60_000,
  });

  // Dates that already hold a scheduled post (excluding this one) — dotted on
  // the schedule calendar so the user can avoid unintentionally stacking a day.
  const scheduledDates = useMemo(
    () => scheduledDateStrings.map((date) => new Date(date)),
    [scheduledDateStrings],
  );

  // Task 14: "Next open slot" — the user's own posting-window preference,
  // not analytics-derived best-time (see auto-schedule-post.ts doc comment).
  const { data: schedulingPrefs } = useQuery({
    queryKey: ['user-preferences', session?.user?.id ?? 'unauthenticated'],
    queryFn: async () => {
      const result = await userPreferencesService.getUserPreferences();
      if (result.error) throw new Error(result.error.message);
      return result.data ?? null;
    },
    enabled: !!session?.user?.id,
    staleTime: 5 * 60_000,
  });

  const autoScheduleHint = schedulingPrefs?.scheduling_preferences
    ? 'Uses your posting window'
    : 'Uses your default posting window · Change in Settings';

  // Final-review Fix 2: the "Scheduled for …" toast used to fire synchronously
  // right after `.mutate()`, before the network write actually landed — a
  // failed write still showed "Scheduled for Tue, Jul 28 at 9:00 AM" followed
  // moments later by an unrelated error toast. `write` now receives a second
  // `onScheduled` callback that names the resolved slot; every call site
  // fires it from inside the mutation's own `onSuccess` (alongside the
  // `silent: true` flag that suppresses that mutation's generic toast), so
  // exactly one toast fires, and only once the write is confirmed.
  const handleAutoSchedule = useCallback(
    (write: (isoDate: string, onScheduled: () => void) => void) => {
      const slot = resolveAutoScheduleSlot({
        prefs: schedulingPrefs?.scheduling_preferences ?? null,
        taken: scheduledDates,
        now: new Date(),
      });
      if (!slot) {
        toast.error(
          'No open slot found in your posting window — pick a time, or widen it in Settings.',
        );
        return;
      }
      write(slot.toISOString(), () =>
        toast.success(`Scheduled for ${format(slot, 'EEE, MMM d')} at ${format(slot, 'h:mm a')}`),
      );
    },
    [schedulingPrefs, scheduledDates],
  );

  const schedulePostMutation = useMutation({
    mutationFn: async ({ id, scheduledAt, reportSkips }: ScheduleMutationVars) => {
      let partition: SchedulePartition | null = null;
      const response = reportSkips
        ? await postService.schedulePost(id, scheduledAt, {
            onSkipped: (value) => {
              partition = value;
            },
          })
        : await postService.schedulePost(id, scheduledAt);
      if (response.error) throw new Error(response.error.message);
      return { row: response.data, partition: partition as SchedulePartition | null };
    },
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['post', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['scheduled-post-dates'] });
      // This mutation is also what handleRetry uses to re-queue a failed
      // post — invalidate needs-attention so the sidebar badge,
      // NeedsAttentionBanner, and CalendarTabs' Failed count all drop the
      // just-retried post instead of showing a stale count until an
      // unrelated remount.
      void invalidateNeedsAttention(queryClient);
      if (result.partition) {
        const outcome = describeScheduleOutcome(result.partition);
        toast.success(outcome.title, { description: outcome.skippedLines.join(' ') });
      } else if (!variables.silent) {
        toast.success('Post scheduled successfully');
      }
    },
    onError: (error: Error) => {
      if (isAwaitingLinkedInDestination(error)) return;
      toast.error(error.message || 'Failed to schedule post');
    },
  });
  const planDraftMutation = useMutation({
    mutationFn: async ({ id, scheduledAt }: ScheduleMutationVars) => {
      const response = await postService.planDraftOnCalendar(id, scheduledAt);
      if (response.error) throw new Error(response.error.message);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['post', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['scheduled-post-dates'] });
      if (!variables.silent) {
        const plannedAt = new Date(variables.scheduledAt);
        toast.success(
          'Draft planned',
          Number.isNaN(plannedAt.getTime())
            ? undefined
            : { description: `Placed on ${formatWithZone(plannedAt)}` },
        );
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to plan draft');
    },
  });
  const unschedulePostMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await postService.unschedulePost(id);
      if (response.error) throw new Error(response.error.message);
      return response.data;
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['post', id] });
      queryClient.invalidateQueries({ queryKey: ['scheduled-post-dates'] });
      toast.success('Moved to drafts');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to unschedule post');
    },
  });
  const schedulePost = schedulePostMutation.mutate;
  const planDraft = planDraftMutation.mutate;
  const unschedulePost = unschedulePostMutation.mutate;
  const isScheduling = schedulePostMutation.isPending;
  const isPlanningDraft = planDraftMutation.isPending;
  const isUnscheduling = unschedulePostMutation.isPending;

  // The 4 onAutoSchedule call sites below only differ in which mutation
  // (schedulePost vs planDraft) they wire and what wraps the call (caption
  // save, panel close) — the "write" closure itself (silent write-back +
  // refetch-then-signal-onScheduled) is identical every time. Factored here
  // so a future 5th call site can't drift the silent/refetch/onScheduled shape.
  const makeAutoScheduleWrite = useCallback(
    (mutation: typeof schedulePost | typeof planDraft) => (iso: string, onScheduled: () => void) =>
      mutation(
        { id: post?.id ?? postId, scheduledAt: iso, silent: true },
        {
          onSuccess: () => {
            refetch();
            onScheduled();
          },
        },
      ),
    // `post` can be undefined on the render before usePost(postId) resolves —
    // this hook runs unconditionally (Rules of Hooks), so `post.id` in the
    // deps array threw "Cannot read properties of undefined (reading 'id')"
    // on that first render and crashed the whole dialog (caught in prod: a
    // published post opened from the calendar failed every time its query
    // wasn't already warm). `postId` (the prop, always defined) is both the
    // real dep and the safe fallback value.
    [post?.id, postId, refetch],
  );

  // Per-platform staggered timing (Phase 3, Rev 2). Setting a time populates
  // platform_schedules[platform]; clearing it prunes back toward the shared
  // row-level scheduled_at. Collapse/navigation NEVER clears — only the chip ✕.
  const setPlatformTimeMutation = useMutation({
    mutationFn: async ({
      id,
      platform,
      scheduledAt,
    }: {
      id: string;
      platform: string;
      scheduledAt: string;
    }) => {
      const response = await postService.setPlatformScheduleTime(id, platform, scheduledAt);
      if (response.error) throw new Error(response.error.message);
      return response.data;
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to set platform time');
    },
  });
  const clearPlatformTimeMutation = useMutation({
    mutationFn: async ({ id, platform }: { id: string; platform: string }) => {
      const response = await postService.clearPlatformScheduleTime(id, platform);
      if (response.error) throw new Error(response.error.message);
      return response.data;
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to clear platform time');
    },
  });

  // A3 — "Post now": real platform dispatch (PublishService), NOT the
  // status-only PostService.publishPost. Irreversible, so the toolbar button
  // confirms via a Popover before firing.
  const { publishNow, isPublishing } = usePublishNow();
  const { unresolved: unresolvedTargets, owedPlatforms } = useUnresolvedPublishTargets(
    post?.id,
    !['draft', 'published'].includes(post?.status ?? 'draft'),
  );
  // The publish confirmed in THIS session for this post (design sheet "After
  // Post now"): drives the one-time check + burst. A post reopened later
  // shows the same line without the celebration.
  const [celebratedPostId, setCelebratedPostId] = useState<string | null>(null);
  // Company pages tagged for LinkedIn only (social_posts.linkedin_mentions). The
  // caption stays plain; the dispatcher applies tags when the post goes to
  // LinkedIn. LinkedInTagsField saves each change itself.
  const storedLinkedInMentions = (post as { linkedin_mentions?: unknown } | null | undefined)
    ?.linkedin_mentions;
  const [linkedInMentions, setLinkedInMentions] = useState<LinkedInMention[]>([]);
  useEffect(() => {
    setLinkedInMentions(
      Array.isArray(storedLinkedInMentions) ? (storedLinkedInMentions as LinkedInMention[]) : [],
    );
  }, [post?.id, storedLinkedInMentions]);
  const inspectorOpenSubject = useInspector((s) => s.openSubject);
  const inspectorPresentation = useInspector((s) => s.presentation);
  const inspectorBreadcrumbs = useInspector((s) => s.breadcrumbs);
  const originSessionId =
    (post as { origin_session_id?: string | null } | null | undefined)?.origin_session_id ?? null;
  const { data: sessionSiblings = [] } = useQuery({
    queryKey: ['post-session-siblings', originSessionId, post?.id ?? null],
    queryFn: async () => {
      if (!originSessionId || !post?.id) return [];
      const result = await postService.getSessionSiblings(originSessionId, post.id);
      if (result.error) throw new Error(result.error.message);
      return result.data ?? [];
    },
    enabled: Boolean(originSessionId && post?.id),
    staleTime: 30_000,
  });
  // `isPublishing` (mutation.isPending) only flips true once `publishNow` is
  // actually CALLED, but the "Post now" click handler below awaits a caption
  // save FIRST — a real network round trip when the draft is dirty. A fast
  // double-click races two calls through that await, both pass the
  // `disabled={isPublishing || …}` check (React hasn't re-rendered yet), and
  // both dispatch publishNow, posting the same content twice. This ref is
  // synchronous and closes that window; isPublishing alone cannot.
  const isPostNowSubmittingRef = useRef(false);

  // Inline title edit state — toggles the header heading between display
  // and an `<input>` that writes to social_posts.title (added in
  // 20260501141330_artifact_naming_smells_v5). Null = display mode;
  // string = editing with this draft value.
  const [titleDraft, setTitleDraft] = useState<string | null>(null);
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  /* One control per OUTCOME, not per surface. Publishing on a schedule is a
     commit and belongs to this split button — it is the only path that runs
     the readiness gate (canSchedule / disabledScheduleTitle) before writing a
     send time. Parking a draft on the calendar is a different outcome and
     belongs to the rail's "Plan on calendar" row. `plan_draft` used to be a
     third mode HERE as well, rendering under the identical condition as that
     rail row and performing the identical write: two calendars, one job. */
  type PublishActionMode = 'post_now' | 'schedule_publish';
  const [publishActionMode, setPublishActionMode] = useState<PublishActionMode>('post_now');
  const [readinessIssuesExpanded, setReadinessIssuesExpanded] = useState(false);
  const captionSaveSerializer = useMemo(() => createSaveSerializer(), [postId]);
  const publishActionDefaultedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!post || publishActionDefaultedForRef.current === post.id) return;
    publishActionDefaultedForRef.current = post.id;
    setPublishActionMode(defaultPublishAction(post));
  }, [post]);
  const [publishActionPanel, setPublishActionPanel] = useState<PublishActionMode | null>(null);
  const [draftPlanningTime, setDraftPlanningTime] = useState(DEFAULT_PLANNING_TIME);
  const [publishScheduleTime, setPublishScheduleTime] = useState(DEFAULT_PLANNING_TIME);
  const [platformOverrideTime, setPlatformOverrideTime] = useState(DEFAULT_PLANNING_TIME);
  useEffect(() => {
    const syncedTime = post?.scheduled_at
      ? exactTimeValueFromDate(new Date(post.scheduled_at))
      : DEFAULT_PLANNING_TIME;
    setDraftPlanningTime(syncedTime);
    setPublishScheduleTime(syncedTime);
  }, [post?.id, post?.scheduled_at]);
  // "Add to project" picker, opened from the footer ⋯ overflow menu.
  const [saveProjectOpen, setSaveProjectOpen] = useState(false);
  const afterMenuActionRef = useRef<'project' | 'rename' | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const isPreviewAccount = useIsPreviewAccount();
  // "Send for approval" — opens NewApprovalDialog with this post preselected.
  const [approvalOpen, setApprovalOpen] = useState(false);
  const planSlotId = (post as { plan_slot_id?: string | null } | null | undefined)?.plan_slot_id;
  const { data: postPlanLink } = usePostPlanLink(planSlotId);
  const approvalCandidate = useMemo(() => (post ? candidateFromPost(post) : null), [post]);
  // Reposition (focal) drag — live value while dragging, committed on release.
  // Declared up here (before the loading early-return) to satisfy rules-of-hooks.
  const [dragFocal, setDragFocal] = useState<{ x: number; y: number } | null>(null);
  const [zoomDraft, setZoomDraft] = useState<number | null>(null);
  // Reposition & zoom is occasional chrome, not standing chrome (founder
  // review, 2026-07-25) — the slider stays collapsed behind this toggle
  // instead of always occupying a row under the preview.
  const [zoomPanelOpen, setZoomPanelOpen] = useState(false);
  const dragState = useRef<{
    startX: number;
    startY: number;
    baseX: number;
    baseY: number;
    /** Pannable overflow in px on each axis (0 = that axis isn't cropped). */
    overflowX: number;
    overflowY: number;
  } | null>(null);

  const serverPlatforms = normalizePostPlatforms(post?.platforms);
  const serverPlatformsKey = serverPlatforms.join('|');
  // Platform selection is intentionally a keep-open multi-select. Two choices
  // can arrive before React Query refetches the first write, so render-time
  // `post.platforms` is not a safe accumulator. Mirror the latest optimistic
  // selection synchronously while commits are queued; only resync from server
  // props after the queue drains (or the Inspector moves to another post).
  const [platforms, setPlatforms] = useState<string[]>(serverPlatforms);
  const platformsKey = platforms.join('|');
  const platformSelectionRef = useRef<string[]>(serverPlatforms);
  const confirmedPlatformSelectionRef = useRef<string[]>(serverPlatforms);
  const platformSelectionPostIdRef = useRef<string | null>(post?.id ?? null);
  const platformSelectionCommitsRef = useRef(0);
  useLayoutEffect(() => {
    const nextPostId = post?.id ?? null;
    if (platformSelectionPostIdRef.current !== nextPostId) {
      platformSelectionPostIdRef.current = nextPostId;
      platformSelectionCommitsRef.current = 0;
    }
    if (platformSelectionCommitsRef.current === 0) {
      platformSelectionRef.current = serverPlatformsKey.split('|').filter(Boolean);
      confirmedPlatformSelectionRef.current = platformSelectionRef.current;
      setPlatforms(platformSelectionRef.current);
    }
  }, [serverPlatformsKey, post?.id]);

  // Per-platform caption overrides race the SAME way platform attach/detach
  // does (P0-5 critical fix, review round 2): a caption save (handleActive-
  // CaptionSave/handleResetPlatformCaption, unqueued) and a platform detach
  // (queued via enqueueMediaKeysCommit) can both read the row's
  // `platform_captions` before either write lands — a render-closure
  // snapshot silently drops whichever write finishes second, or resurrects a
  // just-dropped override (the exact `platforms` accumulator bug this file
  // already documents above, on a second column). Mirror the latest known
  // map synchronously exactly like `platforms`/`platformSelectionRef`: every
  // write reads `platformCaptionsRef.current`, mutates it BEFORE its first
  // await (so a same-tick second write always sees the first write's
  // result), and the map only resyncs from server truth once every in-flight
  // caption write has settled.
  const serverPlatformCaptions = parsePlatformCaptions(post?.platform_captions);
  const serverPlatformCaptionsKey = JSON.stringify(serverPlatformCaptions);
  const [platformCaptions, setPlatformCaptionsState] =
    useState<PlatformCaptions>(serverPlatformCaptions);
  const platformCaptionsRef = useRef<PlatformCaptions>(serverPlatformCaptions);
  const confirmedPlatformCaptionsRef = useRef<PlatformCaptions>(serverPlatformCaptions);
  const platformCaptionsPostIdRef = useRef<string | null>(post?.id ?? null);
  const platformCaptionsCommitsRef = useRef(0);
  useLayoutEffect(() => {
    const nextPostId = post?.id ?? null;
    if (platformCaptionsPostIdRef.current !== nextPostId) {
      platformCaptionsPostIdRef.current = nextPostId;
      platformCaptionsCommitsRef.current = 0;
    }
    if (platformCaptionsCommitsRef.current === 0) {
      platformCaptionsRef.current = serverPlatformCaptions;
      confirmedPlatformCaptionsRef.current = serverPlatformCaptions;
      setPlatformCaptionsState(serverPlatformCaptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverPlatformCaptionsKey, post?.id]);
  // Call BEFORE the write's first await: mirrors the ref+state pair
  // optimistically so any OTHER caption write that starts before this one
  // settles reads the up-to-date map, not a stale render-closure snapshot.
  const beginPlatformCaptionsCommit = useCallback((next: PlatformCaptions) => {
    platformCaptionsRef.current = next;
    setPlatformCaptionsState(next);
    platformCaptionsCommitsRef.current += 1;
  }, []);
  // Call after the write settles. Multiple writes can overlap (two rapid
  // detaches, or a save racing a detach); only when the LAST of them settles
  // (commit count hits 0) does the ref/state get pinned — to that commit's
  // own result on success, or back to the last CONFIRMED map on failure —
  // exactly the `platforms`/`platformSelectionRef` settle pattern above.
  const settlePlatformCaptionsCommit = useCallback(
    (targetPostId: string, ok: boolean, next: PlatformCaptions) => {
      if (platformCaptionsPostIdRef.current !== targetPostId) return;
      if (ok) confirmedPlatformCaptionsRef.current = next;
      platformCaptionsCommitsRef.current = Math.max(0, platformCaptionsCommitsRef.current - 1);
      if (platformCaptionsCommitsRef.current === 0) {
        const settled = ok ? next : confirmedPlatformCaptionsRef.current;
        platformCaptionsRef.current = settled;
        setPlatformCaptionsState(settled);
      }
    },
    [],
  );
  // Fallback only — never overrides a tab the user actually clicked (both
  // call sites below only fire when there is no valid current selection).
  // Prefers a CONNECTED platform so a post attached to Instagram-by-default
  // plus a genuinely connected LinkedIn doesn't open on the one tab that can
  // only ever show "not connected for publishing" — reported live,
  // 2026-09-16: LinkedIn was the connected, ready-to-post platform and
  // Instagram (unconnected) still won every time because it happened to be
  // first in `platforms`, which reflects attach order, not connection state.
  // Falls back to `list[0]` when nothing is connected, so a brand-new
  // workspace with zero integrations still opens on something.
  const failedPlatformsAtOpen = useMemo(() => {
    if (!post || (post.status !== 'failed' && post.status !== 'failed_connection_removed')) {
      return [];
    }
    const schedules = parsePlatformSchedules(
      (post as { platform_schedules?: unknown }).platform_schedules,
    );
    const evidence = (post as { platform_response?: unknown }).platform_response ?? null;
    return failedPublishablePlatforms(
      platforms,
      (platformId) =>
        effectivePlatformStatus(platformId, schedules, post.status ?? 'failed', evidence) ===
        'failed',
    );
  }, [post, platforms]);
  const pickDefaultPlatform = (list: string[]): string | null =>
    pickDefaultPlatformFor({
      platforms: list,
      connected: connectedAccounts.map((account) => account.platform),
      failed: failedPlatformsAtOpen,
    });
  const [activePlatform, setActivePlatform] = useState<string | null>(
    pickDefaultPlatform(platforms),
  );
  const suggestedTime = useSuggestedPostTime(
    activePlatform,
    scheduledDates,
    (post as { brand_kit_id?: string | null } | null | undefined)?.brand_kit_id ?? null,
  );
  // Connections load async (usePlatformIntegrations), so the very first
  // pickDefaultPlatform() call above can run before connectedAccounts has
  // resolved and land on platforms[0] anyway. This ref marks a REAL click on
  // a tab (handlePlatformTabChange) so the one-time "upgrade" effect below
  // knows never to yank a platform the user deliberately opened.
  const userPickedPlatformRef = useRef(false);
  useEffect(() => {
    userPickedPlatformRef.current = false;
  }, [post?.id]);
  // Local-only preview platforms — see handleTogglePreviewPlatform below.
  // Session/per-post UI state, never persisted; resets like the other
  // transient panel state (zoomPanelOpen, coverEditorOpen) on post switch.
  const [previewPlatforms, setPreviewPlatforms] = useState<string[]>([]);
  useEffect(() => {
    setPreviewPlatforms([]);
  }, [post?.id]);

  useEffect(() => {
    if (!platforms.length) {
      setActivePlatform(null);
      return;
    }
    if (!activePlatform || !platforms.includes(activePlatform)) {
      setActivePlatform(pickDefaultPlatform(platforms));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformsKey]);

  // One-time upgrade once real connection data lands: if the initial guess
  // landed on an unconnected platform (connectedAccounts was still empty at
  // mount) and a connected one is now available, switch to it — but only
  // before the user has actually clicked a tab themselves.
  const connectedPlatformsKey = connectedAccounts
    .map((a) => a.platform)
    .sort()
    .join('|');
  useEffect(() => {
    if (userPickedPlatformRef.current) return;
    if (!activePlatform) return;
    if (connectedAccounts.some((a) => a.platform === activePlatform)) return;
    const better = pickDefaultPlatform(platforms);
    if (better && better !== activePlatform) setActivePlatform(better);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedPlatformsKey]);

  useEffect(() => {
    // A slide index has no meaning after the post, target platform, or media
    // sequence changes. Resetting here also prevents a newly opened two-slide
    // post from starting on the old post's final slide.
    setCarouselActiveIndex(0);
  }, [post?.id, activePlatform, mediaProbeKey]);

  useEffect(() => {
    if (exactTarget?.platform && platformsKey.split('|').includes(exactTarget.platform)) {
      setActivePlatform(exactTarget.platform);
    }
  }, [exactTarget?.platform, platformsKey]);

  useEffect(() => {
    setCoverEditorOpen(false);
    setTargetCoverSummary(null);
  }, [post?.id, targetId]);

  // The reveal is a transient UI state, not a saved preference — reopening
  // the Inspector on a different post (openSubject swaps the subject without
  // unmounting this instance) shouldn't carry a previous post's open panel.
  useEffect(() => {
    setZoomPanelOpen(false);
  }, [post?.id]);

  const videoCoverEditorEnabled = isTargetNativeCoverEditorEnabled();
  const isVideoPost = Boolean(
    post?.media_type === 'video' ||
    post?.media_urls?.some((url) => /\.(mp4|mov|webm|m4v)(?:$|\?)/i.test(url)),
  );
  const coverTargetsQuery = useQuery({
    queryKey: [
      'post-cover-targets',
      post?.id ?? null,
      post?.target_first_ready ?? false,
      serverPlatformsKey,
    ],
    enabled: Boolean(
      videoCoverEditorEnabled &&
      !targetId &&
      session?.user?.id &&
      post?.id &&
      isVideoPost &&
      ['draft', 'scheduled'].includes(post?.status ?? ''),
    ),
    queryFn: async () => {
      if (!post) return [];
      if (post.target_first_ready !== true) {
        const materialized = await targetCoverService.materializePostTargets(post.id);
        if (materialized.error) throw new Error(materialized.error.message);
        if (materialized.data && materialized.data.targets.length === 0) {
          throw new Error(
            materialized.data.readinessIssue ?? 'Exact cover destination is unavailable.',
          );
        }
      }
      const result = await targetCoverService.getPostTargets(post.id);
      if (result.error) throw new Error(result.error.message);
      return result.data ?? [];
    },
    retry: 1,
    staleTime: 15_000,
  });
  const coverTargetId =
    targetId ??
    coverTargetsQuery.data?.find((target) => target.platform === activePlatform)?.id ??
    null;
  const coverResolutionIssue = coverTargetsQuery.isError
    ? coverTargetsQuery.error instanceof Error
      ? coverTargetsQuery.error.message
      : 'Exact cover destination is unavailable.'
    : null;
  const coverReadinessIssue = unresolvedPostCoverIssue(coverTargetsQuery.data ?? []);

  // Whether the active platform is currently showing/editing an override
  // (vs. the shared master) must agree with `resolvePlatformCaption` — the
  // function PublishService and the schedule-post edge function actually use
  // to pick what publishes. It can never be derived from `platforms[0]` /
  // array position: detaching the platform that used to be first reshuffles
  // `platforms`, and an index-based flag would then hide a still-live
  // override behind the master-caption view with no way to see or clear it
  // (P0-5). A platform is "shared" only when it has NO saved override; that
  // holds regardless of how many platforms are attached or where this one
  // sits in the array.
  //
  // Review round 2 (MINOR 4): the EDIT-TARGET decision below uses raw key
  // existence + non-empty, matching `resolvePlatformCaption`'s OWN check
  // exactly (`overrides[platform]` non-empty ⇒ that's what publishes) — NOT
  // `hasPlatformOverride`, which also requires `override !== content`. Those
  // agree until the user edits the SHARED master to match a still-saved
  // override's text, at which point `hasPlatformOverride` flips to `false`
  // (nothing to show as "different") while the override key is still what
  // `resolvePlatformCaption` sends. Using `hasPlatformOverride` here would
  // silently flip the editor back to "shared", so a later master edit writes
  // `content` while the untouched override key still wins at publish time.
  // `hasPlatformOverride` is kept below ONLY for `activeIsCustomized` (the
  // "Custom · reset" badge), where "is this visibly different from the
  // master" is exactly the right question.
  const isMultiPlatformDraft = platforms.length > 1;
  const activePlatformOverrideExists = (() => {
    if (!activePlatform) return false;
    const raw = platformCaptions[activePlatform];
    return typeof raw === 'string' && raw.trim().length > 0;
  })();
  const activePlatformHasOverride =
    !!post &&
    !!activePlatform &&
    hasPlatformOverride(post.content, platformCaptions, activePlatform);
  const editingSharedDraft =
    !activePlatform ||
    (!activePlatformOverrideExists && (!isMultiPlatformDraft || activePlatform === platforms[0]));
  const activeCaptionSource = post
    ? editingSharedDraft
      ? (post.content ?? '')
      : resolvePlatformCaption(post.content, platformCaptions, activePlatform!)
    : '';
  const activeCaptionKey = `${post?.id ?? 'loading'}:${editingSharedDraft ? 'shared' : activePlatform}`;
  const [captionDraft, setCaptionDraft] = useState(activeCaptionSource);
  useEffect(() => {
    setCaptionDraft(activeCaptionSource);
  }, [activeCaptionKey, activeCaptionSource]);
  // Bridges the gap between a successful shared-caption save and the React
  // Query cache actually reflecting it. `handleCaptionSave` (the
  // `editingShared` branch below) has no optimistic cache write — unlike the
  // per-platform branch (`platformCaptions`/`beginPlatformCaptionsCommit`),
  // which updates its own local state before its network call even starts —
  // so `activeCaptionSource` (derived straight from `post.content`) stays
  // STALE for the round trip between `setCaptionSavedAt` (which flips the
  // "Saved" indicator on immediately) and the `refetch()` it fires resolving.
  // Without this, `activeCaptionIsDirty` reads true during that window and
  // the readiness banner shows "Save the caption before publishing." right
  // beside "Saved" — two contradictory signals about the same caption.
  // Cleared whenever the source key changes (new post/platform) so a stale
  // confirmed value from a previous context can never suppress a real dirty
  // state; naturally stops mattering once `post.content` itself catches up,
  // since both sides of the dirty check agree at that point anyway.
  const [confirmedSharedCaption, setConfirmedSharedCaption] = useState<string | null>(null);
  useEffect(() => {
    setConfirmedSharedCaption(null);
  }, [activeCaptionKey]);
  // Drop any pending Original⇄Revised compare when the user switches platform
  // tab or post — a revision belongs to one (post × platform) context only.
  useEffect(() => {
    setCaptionRevision(null);
  }, [activeCaptionKey]);

  // True autosave: save once typing pauses, instead of requiring the
  // textarea to lose focus first. Previously the ONLY caption save trigger
  // was CaptionDisplay's onBlur (or Cmd/Ctrl+Enter) — a user who kept typing,
  // or read back over what they wrote before clicking away, had nothing
  // persisted the whole time. The blur/Cmd+Enter commit stays in place as an
  // immediate-save path for closing the editor before this debounce fires;
  // this is what makes "just start typing" itself save the draft, which is
  // also what makes the new platform's caption-seed on `handleTogglePlatform`
  // safe to read from `captionDraft` — it's rarely more than a second stale.
  //
  // The actual save function (`handleActiveCaptionSave`) is defined further
  // down, past this component's early "loading" returns — a hook can't be
  // called after a conditional return, so the trigger effect has to live up
  // here instead. `activeCaptionSaveRef` bridges the two: a plain ref
  // assignment (not a hook) right after `handleActiveCaptionSave`'s own
  // declaration keeps it pointed at the current save function on every
  // render that reaches that point.
  const activeCaptionSaveRef = useRef<((caption: string) => Promise<boolean>) | null>(null);
  const debouncedCaptionDraft = useDebouncedValue(captionDraft, 900);
  // A link pasted into a LinkedIn caption draws as an article card, as it does on LinkedIn. Read through the same
  // `fetchArticlePreview` the publisher uses, so the card here is the card that posts.
  const linkCardEnabled = activePlatform === 'linkedin' && (post?.media_urls?.length ?? 0) === 0;
  const pastedLink = useLinkedInLinkPreview(debouncedCaptionDraft, linkCardEnabled);
  useEffect(() => {
    if (debouncedCaptionDraft !== captionDraft) return; // still mid-pause, not settled
    if (debouncedCaptionDraft === activeCaptionSource) return; // nothing to save
    void activeCaptionSaveRef.current?.(debouncedCaptionDraft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCaptionDraft]);

  const initialPerPlatform =
    (post as { media_keys?: { perPlatform?: PerPlatformMap } } | null | undefined)?.media_keys
      ?.perPlatform ?? {};
  const [perPlatformOverride, setPerPlatformOverride] =
    useState<PerPlatformMap>(initialPerPlatform);
  // Call-time framing echo: the override map is only mutated INSIDE the
  // serialized queue run (see commitFraming below), so rapid drag-releases /
  // keyboard nudges would otherwise compute their next step from a
  // not-yet-updated focal and the preview would briefly snap back while a
  // commit is queued. pendingFraming is presentation-only state layered over
  // savedFocal at call time and cleared once the LAST outstanding framing
  // commit settles.
  const [pendingFraming, setPendingFraming] = useState<{
    focalX?: number;
    focalY?: number;
    zoom?: number;
  } | null>(null);
  // Ref mirror of perPlatformOverride — the commit queue's runs are async and
  // must read/snapshot the LATEST override value, not a render closure. The
  // ref is written synchronously by the same three mutators that set the
  // state (post-change reset below, applyOverridePatches, restoreOverride),
  // so the two can never drift.
  const perPlatformOverrideRef = useRef<PerPlatformMap>(initialPerPlatform);
  // Latest-post-id ref for the queue's identity guard: the Inspector reuses
  // one PostBody instance across posts (openSubject swaps the subject), so a
  // queued commit can outlive the post it was meant for. Synced in the SAME
  // effect that resets the override state so async runs always observe the
  // two consistently (old id + old override, or new id + new override).
  const postIdRef = useRef<string | null>(post?.id ?? null);
  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  });
  // Re-initialise only when the post identity changes, not on every render.
  // initialPerPlatform is derived from post.media_keys and creates a new object
  // reference each render — including it would cause an infinite reset loop.

  useEffect(() => {
    postIdRef.current = post?.id ?? null;
    perPlatformOverrideRef.current = initialPerPlatform;
    setPerPlatformOverride(initialPerPlatform);
    setPendingFraming(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post?.id]);

  // Optimistic-state mutators for the commit queue. applyOverridePatches uses
  // a FUNCTIONAL updater (review finding #2) so two commits dispatched before
  // React flushes the intervening update can't drop each other's change; the
  // ref mirror is updated synchronously for the queue's snapshot reads.
  const applyOverridePatches = useCallback((patches: { platform: string; patch: EntryPatch }[]) => {
    perPlatformOverrideRef.current =
      applyPerPlatformPatches({ perPlatform: perPlatformOverrideRef.current }, patches)
        .perPlatform ?? {};
    setPerPlatformOverride(
      (prev) => applyPerPlatformPatches({ perPlatform: prev }, patches).perPlatform ?? {},
    );
  }, []);
  const restoreOverride = useCallback((snapshot: PerPlatformMap) => {
    perPlatformOverrideRef.current = snapshot;
    setPerPlatformOverride(snapshot);
  }, []);

  // THE canonical media_keys writer: every media_keys mutation in this file
  // (aspect ratio, framing, placement, platform pause sidecar, and — via the
  // committer prop threaded to usePostMediaEdit — the edit-history archiver)
  // funnels through ONE serialized commit queue. Each run refetches server
  // truth, deep-merges its patches into the FRESH keys (never whole-object
  // replaces), writes once, reverts its optimistic state on failure, and
  // invalidates the post query on success. Runs execute strictly one at a
  // time, so two rapid commits can no longer interleave their refetch/write
  // windows and silently clobber each other (review findings #1/#8). See
  // mediaKeysCommitQueue.ts for the full algorithm + finding map.
  //
  // NOTE: this closes same-client races only. Cross-session/cron writers can
  // still last-write-wins this JSONB column — the root fix is a server-side
  // atomic JSONB merge RPC (merge_post_media_keys), tracked for Task A4.
  const commitQueueRef = useRef<MediaKeysCommitQueue | null>(null);
  if (commitQueueRef.current === null) {
    commitQueueRef.current = createMediaKeysCommitQueue({
      getCurrentPostId: () => postIdRef.current,
      fetchFreshMediaKeys: async () => {
        const fresh = await refetchRef.current();
        // A failed/empty refetch ABORTS the commit (review finding #9) — a
        // row whose media_keys is legitimately null is an empty bag, but a
        // refetch that errored (react-query keeps serving the stale cache)
        // must never masquerade as server truth.
        if (fresh.error != null || fresh.data == null) return null;
        return ((fresh.data as { media_keys?: MediaKeysBag }).media_keys ?? {}) as MediaKeysBag;
      },
      persistUpdate: async (postId, updates) => {
        const { error } = await postService.updatePost(postId, updates as never);
        return { error };
      },
      readOverride: () => perPlatformOverrideRef.current,
      applyOverridePatches,
      restoreOverride,
      afterSuccessfulWrite: () => refetchRef.current(),
      // Task A4: atomic per-platform merge via the merge_post_media_keys
      // RPC. The queue feature-detects unavailability — either PostgREST's
      // schema-cache miss (code `PGRST202`, the shape actually returned when
      // an RPC hasn't been added to the schema cache yet) or the raw
      // Postgres SQLSTATE (`42883`, undefined_function, in case a caller
      // ever sees the unwrapped Postgres error) — and falls back to the
      // legacy whole-object write automatically. Any OTHER error code
      // (network failure, RLS/500, etc.) must NOT be treated as "missing":
      // it falls through to the plain `{ ok: false, error }` failure path
      // below, which reverts the optimistic write instead of silently
      // pinning the queue to the legacy path. See mediaKeysCommitQueue.ts's
      // `mergeEntryViaRpc` handling for the full contract.
      mergeEntryViaRpc: async (postId, platform, patch) => {
        const { data, error } = await postService.mergeMediaKeysEntry(
          postId,
          platform,
          entryPatchToJsonPatch(patch),
        );
        if (error) {
          if (isMergeRpcMissingError(error)) {
            return { ok: false, missing: true };
          }
          return { ok: false, error };
        }
        if (data == null) {
          // RLS matched zero rows (post not found, or not owned/administered
          // by the caller) — treat identically to any other write failure
          // rather than silently no-op-ing.
          return { ok: false, error: new Error('merge_post_media_keys matched no row') };
        }
        return { ok: true };
      },
    });
  }
  const commitQueue = commitQueueRef.current;

  // Per-platform in-flight commit counts (review finding #6): the pickers'
  // isSaving indicator is scoped to the platform a commit actually targets,
  // so switching tabs mid-commit never shows the wrong platform as saving.
  const [savingPlatforms, setSavingPlatforms] = useState<Record<string, number>>({});
  // Which-account-does-this-post-as-write-in-progress flag. Kept as plain
  // boolean (not per-platform, unlike savingPlatforms above) since the picker
  // that reads it only ever exists for the single activePlatform at a time.
  const [isSavingDestinationAccount, setIsSavingDestinationAccount] = useState(false);
  const enqueueMediaKeysCommit = useCallback(
    (input: QueuedCommitInput, platformsSaving: string[]): Promise<CommitResult> => {
      setSavingPlatforms((prev) => incrementSaving(prev, platformsSaving));
      beginAutosave();
      const result = commitQueue.enqueue(input);
      const release = () => {
        setSavingPlatforms((prev) => decrementSaving(prev, platformsSaving));
        endAutosave();
      };
      result.then(release, release);
      return result;
    },
    [commitQueue, beginAutosave, endAutosave],
  );
  const notifyCommitFailure = useCallback((result: CommitResult, fallback: string) => {
    if (result.ok) return;
    // Cast: this tsconfig runs strict:false, where boolean-literal
    // discriminant narrowing doesn't kick in on the union.
    const failure = result as Extract<CommitResult, { ok: false }>;
    // A commit abandoned because the Inspector moved to a different post
    // (review finding #7) is named honestly, not blamed on the network.
    toast.error(failure.reason === 'post-changed' ? 'Post changed — edit not saved' : fallback);
  }, []);
  const isSavingActivePlatform = !!activePlatform && (savingPlatforms[activePlatform] ?? 0) > 0;

  // Serialized top-level media_keys transform for the media-edit history
  // archiver (review finding #4): threaded to usePostMediaEdit via
  // PostMediaManager so the archive write merges pushMediaHistory into FRESH
  // server keys inside the same queue every other media_keys writer uses —
  // never a whole-object write of the hook's stale lastKeysRef snapshot.
  const commitMediaKeysTransform = useCallback(
    (transform: (keys: MediaKeysBag) => MediaKeysBag) =>
      enqueueMediaKeysCommit({ transformKeys: transform }, []),
    [enqueueMediaKeysCommit],
  );

  const selectedChildForPost = useCallback(() => {
    const children = readChildMediaState(
      post?.media_urls ?? [],
      (post as { media_keys?: unknown } | null)?.media_keys,
    );
    return children[Math.max(0, carouselActiveIndex)] ?? null;
  }, [carouselActiveIndex, post]);

  const destinationKeyFor = useCallback(
    (platform: string) =>
      `${platform}:${perPlatformOverrideRef.current[platform]?.placement ?? 'feed'}`,
    [],
  );

  const updateSelectedChildCommand = useCallback(
    async (
      command: { aspectRatio?: string; focalX?: number; focalY?: number; zoom?: number } | null,
      targets: string[],
    ) => {
      const selectedChild = selectedChildForPost();
      if (!selectedChild) return { ok: false, reason: 'no-child' } as const;
      const result = await commitMediaKeysTransform((keys) => {
        const currentChildren = readChildMediaState(post?.media_urls ?? [], keys);
        const child = currentChildren.find((item) => item.childId === selectedChild.childId);
        if (!child) return keys;
        // Ratio broadcasts to every child, framing stays on the selected one,
        // and a framing command never invents a ratio. See applyChildCommand.
        const nextChildren = targets.reduce(
          (items, platform) =>
            applyChildCommand(items, selectedChild.childId, destinationKeyFor(platform), command),
          currentChildren,
        );
        return { ...keys, childManifest: nextChildren };
      });
      return result;
    },
    [commitMediaKeysTransform, destinationKeyFor, post, selectedChildForPost],
  );

  const handleAltTextChange = useCallback(
    async (index: number, next: string) => {
      const result = await commitMediaKeysTransform((keys) => {
        const children = readChildMediaState(post?.media_urls ?? [], keys);
        if (!children[index]) return keys;
        const nextChildren = children.map((child, i) => {
          if (i !== index) return child;
          const { altText: _previous, ...rest } = child;
          return next ? { ...rest, altText: next } : rest;
        });
        return { ...keys, childManifest: nextChildren };
      });
      notifyCommitFailure(result, 'Failed to save alt text');
    },
    [commitMediaKeysTransform, notifyCommitFailure, post],
  );

  const handleAspectRatioChange = useCallback(
    async (next: string | undefined) => {
      if (!activePlatform) return;
      const result = await updateSelectedChildCommand(
        next ? { aspectRatio: next, focalX: 0.5, focalY: 0.5, zoom: 1 } : null,
        [activePlatform],
      );
      notifyCommitFailure(result, 'Failed to save crop');
    },
    [activePlatform, notifyCommitFailure, updateSelectedChildCommand],
  );

  const framingCommitsOutstanding = useRef(0);

  // Persist a reposition (focal point) or an "Apply to all" framing broadcast
  // in ONE serialized commit (every target platform merged before the single
  // updatePost call). One generic framing persist — focal drag, zoom, reset,
  // keyboard nudge and apply-to-all all funnel through here. `targets`
  // defaults to the active platform; "Apply to all" passes every platform.
  // Never invents a ratio: native media remains native until the creator
  // explicitly chooses a crop. A later child-aware rendition implementation
  // will bind this same operation to the selected child ID.
  const commitFraming = useCallback(
    async (patch: { focalX?: number; focalY?: number; zoom?: number }, targets?: string[]) => {
      const platformsToWrite = targets ?? (activePlatform ? [activePlatform] : []);
      if (platformsToWrite.length === 0) return;
      setPendingFraming((prev) => ({ ...(prev ?? {}), ...patch }));
      framingCommitsOutstanding.current += 1;
      const result = await updateSelectedChildCommand(patch, platformsToWrite);
      framingCommitsOutstanding.current = Math.max(0, framingCommitsOutstanding.current - 1);
      if (framingCommitsOutstanding.current === 0) setPendingFraming(null);
      notifyCommitFailure(result, 'Failed to save framing');
    },
    [activePlatform, notifyCommitFailure, updateSelectedChildCommand],
  );

  // Persist a placement change (PlacementPicker, Task A3). ANY placement
  // change — to a specific surface OR back to `undefined`/derived — clears
  // that platform's aspectRatio override + stale variantUrl (placementPatch,
  // mediaKeysWriter.ts): both belonged to whichever surface was effective
  // BEFORE this change and aren't guaranteed valid for the new one.
  // Optimistic with revert-on-failure via the commit queue; the pickers'
  // spinner/disabled contract runs off the per-platform saving map above.
  const commitPlacement = useCallback(
    async (next: Placement | undefined) => {
      if (!activePlatform) return;
      const result = await enqueueMediaKeysCommit(
        { entries: [{ platform: activePlatform, patch: placementPatch(next) }] },
        [activePlatform],
      );
      notifyCommitFailure(result, 'Failed to save placement');
    },
    [activePlatform, enqueueMediaKeysCommit, notifyCommitFailure],
  );

  // TikTok settings card (audit rules: audience with no default, interactions,
  // disclosure, consent). Stored per post under media_keys.perPlatform.tiktok so
  // a scheduled post carries them; creator options are re-queried each time a
  // TikTok post opens.
  const tiktokAccount = connectedAccounts.find((account) => account.platform === 'tiktok');
  const tiktokCreator = useTikTokCreatorInfo({
    connectionId: tiktokAccount?.accountId ?? null,
    seed: (tiktokAccount?.rawData as { metadata?: { tiktokCreatorInfo?: unknown } } | undefined)
      ?.metadata?.tiktokCreatorInfo,
    enabled: activePlatform === 'tiktok' && Boolean(tiktokAccount),
  });
  const tiktokVideoFacts = useVideoFacts(
    post?.media_urls?.[0] ?? null,
    activePlatform === 'tiktok' && isVideoPost,
  );
  const tiktokStoredSettings = readTikTokSettings(perPlatformOverride);
  const tiktokPreviewSound: 'tiktok-picks' | 'none' | 'drafts' =
    tiktokStoredSettings?.deliveryMode === 'drafts'
      ? 'drafts'
      : tiktokStoredSettings && !tiktokStoredSettings.autoAddMusic
        ? 'none'
        : 'tiktok-picks';
  const tiktokPreviewCoverLabel =
    targetCoverSummary && targetCoverSummary.mode !== 'provider_default'
      ? targetCoverSummary.label
      : null;
  const commitTikTokSettings = useCallback(
    async (next: TikTokPostSettings) => {
      const result = await enqueueMediaKeysCommit(
        { entries: [{ platform: 'tiktok', patch: tiktokSettingsPatch(next) }] },
        ['tiktok'],
      );
      notifyCommitFailure(result, 'Failed to save TikTok settings');
    },
    [enqueueMediaKeysCommit, notifyCommitFailure],
  );

  // Instagram collaborators, place and audio, and Facebook link-preview
  // overrides. Stored beside the crop settings in media_keys.perPlatform and
  // sent on the container at publish time (shared/instagram-publish-options.ts).
  const commitPublishOptions = useCallback(
    async (next: Parameters<typeof publishOptionsPatch>[0]) => {
      if (!activePlatform) return;
      const result = await enqueueMediaKeysCommit(
        { entries: [{ platform: activePlatform, patch: publishOptionsPatch(next) }] },
        [activePlatform],
      );
      notifyCommitFailure(result, 'Failed to save post options');
    },
    [activePlatform, enqueueMediaKeysCommit, notifyCommitFailure],
  );

  // v5 title via the canonical `buildArtifactTitle` helper. Falls through
  // priority: post.title (explicit override) → brand+format identity →
  // 'Untitled post'. Memoized BEFORE the loading early-return so hooks
  // order is stable; keyed on stable string deps.
  const brandKitName = brandKit?.name;
  const explicitTitle = meaningfulPostTitle(
    post && 'title' in post ? ((post as { title?: string | null }).title ?? null) : null,
  );
  const derivedTitle = useMemo(
    () =>
      buildArtifactTitle({
        kind: 'post',
        title: explicitTitle,
        platforms: platformsKey ? platformsKey.split('|') : [],
        brandKitName,
      }),
    [explicitTitle, platformsKey, brandKitName],
  );
  // The INSPECTOR header drops the "· Instagram post" format suffix that
  // buildArtifactTitle adds — the platform already shows in the preview strip,
  // the caption label, and the preview replica, so repeating it in the title is
  // pure noise. Just the brand (or an explicit title). Library/calendar keep the
  // fuller derived title via buildArtifactTitle (no platform toggle there).
  // explicitTitle is capped the same way buildArtifactTitle caps it internally
  // (see EXPLICIT_TITLE_MAX_WORDS) — this reads post.title directly rather
  // than through buildArtifactTitle, so it needs the same guard against a
  // stray multi-paragraph value (e.g. a generation prompt landing in the
  // title column) rendering whole into the header.
  const cappedExplicitTitle = explicitTitle?.trim()
    ? truncateToWords(explicitTitle, EXPLICIT_TITLE_MAX_WORDS)
    : '';
  const headerTitle =
    cappedExplicitTitle ||
    (post
      ? postHeaderTitle({
          title: null,
          content: post.content,
          created_at: (post as { created_at?: string | null }).created_at ?? null,
        })
      : derivedTitle);

  // Memoize the metrics narrow — runs every render otherwise, and PostBody
  // re-renders on every keystroke in the caption editor / composer. Hoisted
  // above the loading early-return for stable hooks-order.
  const metrics = useMemo(() => {
    const raw = (post as { metrics?: unknown } | null | undefined)?.metrics;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    return raw as Record<string, unknown>;
  }, [post]);

  // F-134: real per-post analytics from post_metrics_daily. Only fetched for
  // published posts; falls back to the social_posts.metrics jsonb below when
  // the daily snapshot hasn't been populated yet. Called before the early
  // return so hooks order stays stable.
  const postStatus = (post as { status?: string } | null | undefined)?.status;
  const {
    metrics: dailyMetrics,
    byPlatform: dailyMetricsByPlatform,
    byDestination: dailyMetricsByDestination,
    destinations: metricDestinations,
    coverage: metricsCoverage,
    hasData: hasDailyMetrics,
  } = usePostMetrics(postId, {
    enabled: postStatus === 'published' || postStatus === 'partially_published',
  });

  // A deleted/stale post (opened from a library card or ?editPost= deep link)
  // used to spin on "Loading post…" forever — usePost settles with error and no
  // post, but the loading guard only checked `!post`. Surface an honest
  // not-found state with a close action, and drop the stale card from the recall
  // surfaces so it disappears.
  if (postError || (!isLoading && !isFetching && !post)) {
    return (
      <div className="flex h-full min-h-0 flex-col items-center justify-center gap-3 bg-background px-6 text-center">
        <p className="text-sm font-medium text-foreground/90">This post no longer exists</p>
        <p className="text-detail text-muted-foreground">
          It may have been deleted. Close this and pick another one.
        </p>
        <Button variant="outline" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>
    );
  }

  if (isLoading || !post) {
    return <PostBodyLoadingSkeleton />;
  }

  if (targetId && isTargetLoading) {
    return <PostBodyLoadingSkeleton />;
  }

  if (targetId && (isTargetError || !exactTarget)) {
    return (
      <div className="flex h-full min-h-0 flex-col items-center justify-center gap-3 bg-background px-6 text-center">
        <p className="text-sm font-medium text-foreground/90">
          This Instagram target is unavailable
        </p>
        <p className="text-detail text-muted-foreground">
          The post still exists, but Mora could not verify the exact account destination.
        </p>
        <Button variant="outline" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>
    );
  }

  const exactTargetStatus = exactTarget
    ? ['published', 'published_with_warnings'].includes(exactTarget.deliveryStatus)
      ? 'published'
      : exactTarget.deliveryStatus === 'scheduled'
        ? 'scheduled'
        : exactTarget.deliveryStatus === 'failed'
          ? 'failed'
          : 'draft'
    : null;
  const status = exactTargetStatus ?? post.status ?? 'draft';
  const isEditable = !targetId && (status === 'draft' || status === 'scheduled');
  const isPlannedDraft = status === 'draft' && Boolean(post.scheduled_at);
  const plannedDraftDate = isPlannedDraft && post.scheduled_at ? new Date(post.scheduled_at) : null;
  const isPublished = status === 'published';
  // F-135: a partially-published post is live on the platforms that succeeded —
  // it has real permalinks + metrics, so it shows "View live", the published
  // date, and the analytics card just like a fully published post.
  const isLiveOrPartial = isPublished || status === 'partially_published';
  // Neither isEditable nor isLiveOrPartial -- a failed dispatch fell through
  // every branch in this file with zero recovery action: no CTA, no Delete,
  // no Move-to-drafts, stuck forever (no cron re-touches these rows either).
  const isFailed = status === 'failed' || status === 'failed_connection_removed';
  const partial = partialDelivery({
    status,
    platforms: post.platforms,
    platform_response: (post as { platform_response?: unknown }).platform_response,
    platform_schedules: (post as { platform_schedules?: unknown }).platform_schedules,
  });
  const activeUnsent = partial?.unsent.find((entry) => entry.platform === activePlatform) ?? null;
  const activeUnresolved = unresolvedTargets.some((target) => target.platform === activePlatform);
  // With an unknown outcome on the row, what is left to send comes from the targets, never the one being checked.
  const sendablePlatforms: string[] = [
    ...new Set([...owedPlatforms, ...(partial?.unsent.map((entry) => entry.platform) ?? [])]),
  ].filter(
    (platform) =>
      !isUnsupportedPublishPlatform(platform) &&
      !unresolvedTargets.some((target) => target.platform === platform),
  );
  const partialLine = !partial
    ? null
    : activeUnsent
      ? activeUnsent.reason === 'never_sent'
        ? `This post was never sent to ${platformLabel(activeUnsent.platform)}. It went out on ${listPlatformNames(partial.sent.map(platformLabel))}.`
        : `${platformLabel(activeUnsent.platform)} did not accept this post. ${
            explainPostFailure(activeUnsent.error, [activeUnsent.platform]).sentence
          }`
      : `Not sent to ${listPlatformNames(partial.unsent.map((entry) => platformLabel(entry.platform)))}.`;
  const hasMedia = (post.media_urls?.length ?? 0) > 0;

  // F-136: fan every post mutation out to all surfaces that render it. Now a
  // thin bind over the leaf useInvalidatePostSurfaces hook (which also covers
  // ['scheduled-post-dates', …], getPost refetch, and the cross-tab broadcast).
  const invalidatePostSurfaces = () => {
    const uid = session?.user?.id;
    if (!uid || !post) return;
    void invalidatePostSurfacesFor({ userId: uid, postId: post.id });
  };

  const handleDuplicate = async () => {
    const { error } = await postService.duplicatePost(post.id);
    if (error) toast.error('Failed to duplicate post');
    else {
      toast.success('Post duplicated');
      refetch();
      invalidatePostSurfaces();
    }
  };

  const handleDelete = async () => {
    const { error } = await postService.deletePost(post.id);
    if (error) toast.error('Failed to delete post');
    else {
      toast.success('Post deleted');
      invalidatePostSurfaces();
      onClose();
    }
  };

  const handleCaptionSave = (newCaption: string): Promise<boolean> =>
    captionSaveSerializer.run(post.updated_at, async (expectedUpdatedAt) => {
      beginAutosave();
      try {
        const { data, error } = await postService.updatePost(post.id, {
          content: newCaption,
          expectedUpdatedAt,
        });
        if (error) {
          if (error.code === 'CONFLICT') {
            toast.error('This post was edited elsewhere', {
              description: 'Reload to see the latest before saving your changes.',
            });
          } else if (isLockedEditError(error)) {
            showLockedEditToast(LOCKED_CAPTION_TOAST_DESCRIPTION);
          } else {
            toast.error('Failed to save caption');
          }
          return { result: false };
        }
        setConfirmedSharedCaption(newCaption);
        setCaptionSavedAt(Date.now());
        refetch();
        invalidatePostSurfaces();
        return { result: true, updatedAt: data?.updated_at };
      } finally {
        endAutosave();
      }
    });

  // Per-platform captions. Model (Buffer-style "master + customize"):
  //   • 1 platform, or the PRIMARY platform tab (platforms[0]), with no saved
  //     override yet → edits the shared `content` (the master caption every
  //     platform inherits).
  //   • a SECONDARY platform tab, or ANY platform that already has a saved
  //     override (however it got there) → edits that platform's override in
  //     `platform_captions`. Clearing it (empty, or back to the master text)
  //     removes the override so the platform re-inherits the master.
  // Resolution everywhere is `override ?? content`, so existing posts and any
  // un-customized platform are unchanged. See `editingSharedDraft` above for
  // why this can't be a plain `activePlatform === platforms[0]` check.
  const isMultiPlatform = platforms.length > 1;
  const editingShared = editingSharedDraft;
  const activeCaption = captionDraft;
  const activeCaptionIsDirty =
    captionDraft !== activeCaptionSource &&
    !(editingShared && confirmedSharedCaption != null && captionDraft === confirmedSharedCaption);
  const activeIsCustomized = !editingShared && !!activePlatform && activePlatformHasOverride;
  // Hoisted above its original declaration (previously right before
  // `platformEvidence` below) so the fan-out visibility gate can use the
  // real send-eligible set instead of the raw `platforms` attached list —
  // see resolveCaptionQuickActions' doc comment (final-review Fix 5).
  const enabledSendPlatforms = computeEnabledPlatforms(
    platforms,
    (post as { media_keys?: { disabledPlatforms?: unknown } }).media_keys,
  );
  const captionQuickActions = resolveCaptionQuickActions({
    editingShared,
    activePlatform,
    platformLabel,
    fanOutEligibleCount: enabledSendPlatforms.length,
    captionEditMode: captionEditModeForStatus(post.status),
  });

  // Which connected account this post targets, per platform. Storage
  // contract: `social_posts.destination_connection_ids` jsonb, shape
  // `{"instagram": "<user_connections.id>", ...}` — a missing key or an
  // explicit JSON null means "no override, resolve normally" (column
  // comment, verified live). Read-only from here: the parallel write path
  // is `handleDestinationAccountChange` below.
  const destinationOverrides =
    (post.destination_connection_ids as Record<string, string | null> | null) ?? {};
  const destinationResourceOverrides =
    (post.destination_resource_ids as Record<string, string | null> | null) ?? {};
  const destinationAccountsForActivePlatform = activePlatform
    ? connectedAccounts.filter((account) => account.platform === activePlatform)
    : [];
  const activeDestinationOverrideId = activePlatform
    ? (destinationOverrides[activePlatform] ?? undefined)
    : undefined;
  const activeDestinationResourceId = activePlatform
    ? (destinationResourceOverrides[activePlatform] ?? undefined)
    : undefined;
  const activeDestinationOverrideAccount = activeDestinationOverrideId
    ? destinationAccountsForActivePlatform.find(
        (account) => account.accountId === activeDestinationOverrideId,
      )
    : undefined;
  const destinationOptionsForActivePlatform: DestinationAccountOption[] = buildDestinationOptions(
    connectedAccounts,
    activePlatform,
  );
  const activeDestinationOption = destinationOptionsForActivePlatform.find(
    (option) =>
      option.accountId === activeDestinationOverrideId &&
      (activeDestinationResourceId === undefined ||
        option.resourceId === activeDestinationResourceId),
  );
  // A stored id that no longer matches any ACTIVE connection for this
  // platform (the account was disconnected since) reads as `undefined` here
  // — `DestinationAccountPicker` re-derives the stale case itself from
  // `options`/`value` and renders the explicit warning; the read side
  // (publish) refuses on exactly this, per the write-side contract above.

  // Preview identity — prefer the REAL connected account for the active platform
  // (so the replica shows the user's actual @handle + avatar, like the live post
  // will): an already-resolved exact target wins (immutable, it already shipped),
  // then the user's chosen destination override, then whichever single
  // connected account exists, then the brand kit, then a generic placeholder.
  const activeAccount = activePlatform
    ? (destinationAccountsForActivePlatform.find(
        (account) => exactTarget && account.accountId === exactTarget.connectionId,
      ) ??
      activeDestinationOverrideAccount ??
      (exactTarget ? undefined : destinationAccountsForActivePlatform[0]))
    : undefined;
  // Two or more accounts to post as and none chosen yet: the preview must not
  // borrow one of them (the connection's own name is the Company Page). It says
  // so, and the picker below the card is where the person decides.
  const destinationUnchosen =
    !exactTarget && destinationOptionsForActivePlatform.length > 1 && !activeDestinationOption;
  const previewBrandName = destinationUnchosen
    ? 'Choose who posts this'
    : activeDestinationOption?.displayName ||
      activeAccount?.displayName ||
      brandKit?.name ||
      'Your Brand';
  const previewAvatarUrl = destinationUnchosen
    ? undefined
    : activeDestinationOption?.imageUrl ||
      activeAccount?.imageUrl ||
      brandKit?.logo_url ||
      undefined;
  const previewHandle = destinationUnchosen
    ? undefined
    : activeDestinationOption?.identifier || activeAccount?.identifier || undefined;

  const handleDestinationAccountChange = async (accountId: string, resourceId?: string) => {
    if (!activePlatform || isSavingDestinationAccount) return;
    setIsSavingDestinationAccount(true);
    const next = { ...destinationOverrides, [activePlatform]: accountId };
    const nextResources = {
      ...destinationResourceOverrides,
      [activePlatform]: resourceId ?? null,
    };
    const { error } = await postService.updatePost(post.id, {
      destination_connection_ids: next,
      destination_resource_ids: nextResources,
    });
    setIsSavingDestinationAccount(false);
    if (error) {
      toast.error('Failed to change which account this posts as');
    } else {
      refetch();
      invalidatePostSurfaces();
    }
  };

  const handleActiveCaptionSave = async (newCaption: string): Promise<boolean> => {
    if (newCaption === activeCaptionSource) return true;
    if (editingShared) {
      return handleCaptionSave(newCaption);
    }
    beginAutosave();
    const targetPostId = post.id;
    const map = platformCaptionsRef.current;
    const next = { ...map };
    if (!newCaption.trim() || newCaption.trim() === (post.content ?? '').trim()) {
      delete next[activePlatform!]; // re-inherit the master caption
    } else {
      next[activePlatform!] = newCaption;
    }
    beginPlatformCaptionsCommit(next);
    const { error } = await captionSaveSerializer.run(
      post.updated_at,
      async (expectedUpdatedAt) => {
        const response = await postService.updatePost(post.id, {
          platform_captions: next,
          expectedUpdatedAt,
        });
        return { result: response, updatedAt: response.data?.updated_at };
      },
    );
    settlePlatformCaptionsCommit(targetPostId, !error, next);
    if (error) {
      endAutosave();
      if (error.code === 'CONFLICT') {
        toast.error('This post was edited elsewhere', {
          description: 'Reload to see the latest before saving your changes.',
        });
      } else if (isLockedEditError(error)) {
        showLockedEditToast(LOCKED_CAPTION_TOAST_DESCRIPTION);
      } else {
        toast.error('Failed to save caption');
      }
      return false;
    }
    endAutosave();
    setCaptionSavedAt(Date.now());
    refetch();
    invalidatePostSurfaces();
    return true;
  };
  // The debounce effect that TRIGGERS a save is declared further up (before
  // this component's early "loading" returns — see `activeCaptionSaveRef`
  // near `captionDraft`'s own declaration; Rules of Hooks forbids a hook
  // call after a conditional return). This line is what makes that ref
  // useful: a plain assignment, not a hook, so — unlike a hook — it's fine
  // that it only runs on renders that reach past the loading returns.
  activeCaptionSaveRef.current = handleActiveCaptionSave;

  // Flush a pending, still-debouncing caption edit before switching platform
  // tabs. Without this, typing then switching tabs inside the 900ms debounce
  // window let the `activeCaptionKey` effect (see `captionDraft`'s own
  // declaration) reset `captionDraft` to the NEW tab's source, silently
  // discarding the old tab's unsaved edit — the debounce timer restarts
  // tracking the reset value, which trivially equals `activeCaptionSource`,
  // so the save-effect's "nothing to save" guard swallows it with no toast.
  // Calling the save HERE, before `setActivePlatform`, closes over the OLD
  // `activePlatform`/`activeCaptionSource` (this function reassigns every
  // render, see the line above), so it saves to the correct target.
  const handlePlatformTabChange = (next: string): void => {
    if (captionDraft !== activeCaptionSource) {
      void activeCaptionSaveRef.current?.(captionDraft);
    }
    userPickedPlatformRef.current = true;
    setActivePlatform(next);
  };

  // One-click fixes off the quality chip. Both branches route into writers
  // that ALREADY exist — the caption goes through handleActiveCaptionSave (so
  // per-platform overrides are honored) and the ratio through
  // handleAspectRatioChange (so it joins the queued media_keys commit rather
  // than opening a fourth writer that could race the clobber guard).
  const handleApplyQualityFix = async (fix: QualityFix) => {
    if (fix.captionReplacement != null) {
      await handleActiveCaptionSave(fix.captionReplacement);
      return;
    }
    const ratio = fix.perPlatformPatch?.set?.aspectRatio;
    if (ratio) await handleAspectRatioChange(ratio);
  };

  const handleSaveDraft = async (): Promise<boolean> => {
    const saved = await handleActiveCaptionSave(captionDraft);
    if (saved) toast.success('Draft saved');
    return saved;
  };

  const handleResetPlatformCaption = async () => {
    if (!activePlatform) return;
    const targetPostId = post.id;
    const map = platformCaptionsRef.current;
    if (!(activePlatform in map)) return;
    const next = { ...map };
    delete next[activePlatform];
    beginPlatformCaptionsCommit(next);
    const { error } = await postService.updatePost(post.id, { platform_captions: next });
    settlePlatformCaptionsCommit(targetPostId, !error, next);
    if (error) {
      if (isLockedEditError(error)) showLockedEditToast(LOCKED_CAPTION_TOAST_DESCRIPTION);
      else toast.error('Failed to reset caption');
    } else {
      setCaptionSavedAt(Date.now());
      refetch();
    }
  };

  // F-132/F-136: persist a media-list mutation (add / remove / reorder / edit)
  // then fan invalidation out to EVERY surface that renders this post's media —
  // the post detail (refetch), the calendar + drafts queue (['posts']), the
  // project workbench slots (['slot-posts'] / ['unbound-plan-posts']), and the
  // artifacts_index-backed library grid (['library']). invalidatePostSurfaces
  // covers the full set (the earlier ['media*']-only version left the project
  // and library grids stale — F-130 cross-surface gap).
  const handleMediaChange = async (
    nextUrls: string[],
    uploaded?: { mediaType: 'image' | 'video'; coverImageUrl: string | null },
  ) => {
    // Normalize media_type via the canonical inferMediaType helper (authored to
    // the publisher's rule) so the persisted type matches exactly what ships:
    // 0 → null (clear), single clip → 'video', single image → 'image',
    // 2+ → 'carousel', and a video anywhere in the list → 'video'
    // (video-before-carousel). Replaces the prior inline regex that only
    // checked slot 0 and could mis-tag an [image, clip] list as 'carousel'.
    const nextMediaType: 'video' | 'image' | 'carousel' | null =
      nextUrls.length === 0 ? null : inferMediaType(nextUrls);
    const firstSourceChanged = (post.media_urls?.[0] ?? null) !== (nextUrls[0] ?? null);
    const nextChildren = reconcileChildMediaUrls(
      readChildMediaState(post.media_urls ?? [], (post as { media_keys?: unknown }).media_keys),
      nextUrls,
    );
    const result = await enqueueMediaKeysCommit(
      {
        transformKeys: (keys) => ({ ...keys, childManifest: nextChildren }),
        extraUpdates: {
          media_urls: nextUrls,
          media_type: nextMediaType,
          ...(firstSourceChanged
            ? {
                // Never carry a poster across source identity. A direct video
                // upload returns its real poster metadata; Library/reorder paths
                // clear the old projection and let target materialization choose
                // provider-default until a matching poster is resolved.
                cover_image_url: uploaded?.mediaType === 'video' ? uploaded.coverImageUrl : null,
              }
            : {}),
        },
      },
      [],
    );
    if (!result.ok) {
      const failure = result as Extract<CommitResult, { ok: false }>;
      if (isLockedEditError(failure.error)) showLockedEditToast();
      else toast.error('Failed to save media');
      throw new Error('Failed to save media');
    }
    refetch();
    invalidatePostSurfaces();
  };

  // Persist the inline title edit. Empty string clears back to derived
  // (we store NULL so the brand+format fallback re-takes over). Skips
  // the round-trip when the value is unchanged.
  const handleTitleSave = async (next: string) => {
    setTitleDraft(null);
    const trimmed = next.trim();
    const current = explicitTitle ?? '';
    if (trimmed === current.trim()) return;
    const persistValue = trimmed.length === 0 ? null : trimmed;
    beginAutosave();
    const { error } = await postService.updatePost(post.id, {
      title: persistValue as never,
    });
    endAutosave();
    if (error) toast.error('Failed to save title');
    else {
      refetch();
      // Every sibling mutation in this file (duplicate, media change, …)
      // invalidates the shared calendar-tab-posts/posts surfaces after a
      // write; this one only ever called refetch() (this single post's own
      // query), so a renamed draft kept showing its old derived title on
      // the Posts list until an unrelated refetch or a hard reload caught
      // it up. Reported live, 2026-09-16: renaming to "Test Draft" and
      // going back still showed "Untitled post" in the list.
      invalidatePostSurfaces();
    }
  };

  // F-133: move a scheduled post back to draft from the inspector. The post
  // hasn't been posted to any platform yet (the cron publishes at scheduled_at),
  // so this is a pure state flip — it leaves the calendar's scheduled view and
  // returns to the drafts queue, staying linked to its project. refetch() so
  // the inspector's CTA flips from "Reschedule" back to "Approve & schedule".
  const handleUnschedule = () => {
    if (isPreviewAccount) {
      showInfoToast(PREVIEW_ACCOUNT_MESSAGE);
      return;
    }
    unschedulePost(post.id, { onSuccess: () => refetch() });
  };

  const requestDelete = () => {
    if (isPreviewAccount) {
      showInfoToast(PREVIEW_ACCOUNT_MESSAGE);
      return;
    }
    setConfirmDeleteOpen(true);
  };

  // Retry a failed post by re-entering the exact same pipeline a normal
  // schedule uses: flip back to status='scheduled' with scheduled_at=now, so
  // the next process-scheduled-posts/claim_due_posts cron tick (it claims any
  // scheduled row whose due time has passed) re-attempts the real dispatch.
  // No separate retry endpoint needed -- this is the same schedulePost()
  // mutation "Reschedule" already uses.
  const handleRetry = () => {
    schedulePost(
      { id: post.id, scheduledAt: new Date().toISOString() },
      { onSuccess: () => refetch() },
    );
  };

  const handleRepostAfterCheck = async (platform: string) => {
    await Promise.resolve(
      publishNow({
        postId: post.id,
        platform: platform as PlatformId,
        confirmRepostPlatforms: [platform],
      }),
    ).catch(() => undefined);
    refetch();
    invalidatePostSurfaces();
  };

  // A sibling of a target being checked: what is owed goes out, and the checked one is not in the request.
  const handleSendOwed = async () => {
    await Promise.resolve(
      publishNow({
        postId: post.id,
        platform: sendablePlatforms as PlatformId[],
        sendingOwedPlatforms: unresolvedTargets.length > 0,
      }),
    ).catch(() => undefined);
    refetch();
    invalidatePostSurfaces();
  };

  const handleSendUnsent = async () => {
    if (!partial) return;
    await Promise.resolve(
      publishNow({
        postId: post.id,
        platform: partial.unsent.map((entry) => entry.platform) as PlatformId[],
      }),
    ).catch(() => undefined);
    refetch();
    invalidatePostSurfaces();
  };

  const handleReconnect = () => {
    window.dispatchEvent(new CustomEvent('mora:openSettings', { detail: 'integrations' }));
  };

  // Attach/detach a platform. The media_keys portion (clearing a detached
  // platform's stale "paused" flag) routes through the SAME serialized commit
  // queue as every other media_keys writer (review finding #5) — previously
  // this wrote media_keys straight from the render-closure `post` snapshot
  // and could race/clobber a placement/aspect/framing commit on the same
  // JSONB column. The `platforms` column rides the same updatePost call via
  // extraUpdates; a pure attach (no media_keys change) still serializes so it
  // can't interleave with another commit's refetch→write window. Detaching
  // also drops that platform's `platform_captions` entry in the SAME write
  // (P0-5) — an override can otherwise outlive its platform and keep
  // publishing text the editor can no longer show or reach.
  const handleTogglePlatform = async (platformId: string) => {
    const targetPostId = post.id;
    const current = platformSelectionRef.current;
    const removing = current.includes(platformId);
    const next = removing ? current.filter((p) => p !== platformId) : [...current, platformId];
    platformSelectionRef.current = next;
    setPlatforms(next);
    platformSelectionCommitsRef.current += 1;
    // Read/write the caption ref (never `post.platform_captions` — that's
    // the stale-snapshot bug this block fixes), and mutate it BEFORE the
    // await so a second detach/save fired before this write lands still
    // builds on top of THIS one's result.
    const previousCaptions = platformCaptionsRef.current;
    const hasOrphanedCaption = removing && platformId in previousCaptions;
    // Adding a platform: seed its caption from whatever is currently on
    // screen rather than leaving it to the read-time `resolvePlatformCaption`
    // fallback (override ?? shared) alone. The fallback IS correct once the
    // shared-caption write has landed and refetched — but that's an async
    // round trip, and a fast "type → click + → click the new tab" sequence
    // can outrun it, showing the new tab blank until the refetch catches up
    // (live-reproduced 2026-08-17: DB had the right `content` and
    // `platforms`, yet the just-added tab briefly rendered empty). Reading
    // `captionDraft`/`activeCaptionSource` (client memory, not server state)
    // and writing it explicitly closes that race and matches the ask
    // directly: the new platform inherits the caption now, and typing on its
    // own tab later still overrides it via the existing override mechanism.
    const seedCaption = !removing
      ? ((editingShared && activeCaptionIsDirty ? captionDraft : activeCaptionSource) || '').trim()
      : '';
    const shouldSeedCaption =
      !removing && seedCaption.length > 0 && !(platformId in previousCaptions);
    const nextCaptions = (() => {
      if (hasOrphanedCaption) {
        const dropped = { ...previousCaptions };
        delete dropped[platformId];
        return dropped;
      }
      if (shouldSeedCaption) return { ...previousCaptions, [platformId]: seedCaption };
      return previousCaptions;
    })();
    const captionsChanged = hasOrphanedCaption || shouldSeedCaption;
    if (captionsChanged) beginPlatformCaptionsCommit(nextCaptions);
    const result = await enqueueMediaKeysCommit(
      removing
        ? {
            // Detaching also clears any stale "paused" flag for it, so a
            // later re-attach starts enabled — applied to FRESH keys.
            transformKeys: (keys) => withPlatformEnabled(keys, platformId, true),
            extraUpdates: {
              platforms: next,
              ...(hasOrphanedCaption ? { platform_captions: nextCaptions } : {}),
            },
          }
        : {
            extraUpdates: {
              platforms: next,
              ...(shouldSeedCaption ? { platform_captions: nextCaptions } : {}),
            },
          },
      [platformId],
    );
    if (captionsChanged) settlePlatformCaptionsCommit(targetPostId, result.ok, nextCaptions);
    if (platformSelectionPostIdRef.current === targetPostId) {
      if (result.ok) {
        confirmedPlatformSelectionRef.current = next;
      }
      platformSelectionCommitsRef.current = Math.max(0, platformSelectionCommitsRef.current - 1);
      if (platformSelectionCommitsRef.current === 0) {
        const settled = result.ok ? next : confirmedPlatformSelectionRef.current;
        platformSelectionRef.current = settled;
        setPlatforms(settled);
      }
    }
    notifyCommitFailure(result, 'Failed to update platforms');
  };

  // Per-platform enable/disable ("attached but paused") — writes the
  // media_keys.disabledPlatforms sidecar through the shared commit queue
  // (review finding #5): the toggle DIRECTION comes from what the user sees
  // (the rendered disabled set), but the WRITE merges into fresh server keys
  // inside the serialized run. The platform stays in post.platforms (keeping
  // its caption/crop overrides); the dispatcher skips paused platforms at
  // send.
  const disabledPlatforms = getDisabledPlatforms(
    (post as { media_keys?: { disabledPlatforms?: unknown } }).media_keys,
  );
  const handleTogglePlatformEnabled = async (platformId: string) => {
    const nextEnabled = disabledPlatforms.includes(platformId); // currently paused → enable
    const result = await enqueueMediaKeysCommit(
      { transformKeys: (keys) => withPlatformEnabled(keys, platformId, nextEnabled) },
      [platformId],
    );
    notifyCommitFailure(result, 'Failed to update platforms');
  };

  // Local-only "see the vision" preview for an UNCONNECTED platform — a
  // creator can open, say, the TikTok tab and see their caption/media
  // rendered as a TikTok post without that ever becoming a real send
  // target. Deliberately never touches `platforms`/media_keys/any persisted
  // field: it's pure client state, reset per post, and excluded by
  // construction from every publish-time computation below because those
  // all read `platforms`, never this. Toggling preview OFF for the active
  // tab falls back to a real platform if one exists.
  const handleTogglePreviewPlatform = (platformId: string) => {
    setPreviewPlatforms((prev) => {
      const isPreviewing = prev.includes(platformId);
      const next = isPreviewing ? prev.filter((p) => p !== platformId) : [...prev, platformId];
      if (!isPreviewing) {
        userPickedPlatformRef.current = true;
        setActivePlatform(platformId);
      } else if (activePlatform === platformId) {
        userPickedPlatformRef.current = true;
        setActivePlatform(pickDefaultPlatform(platforms));
      }
      return next;
    });
  };

  // Per-platform staggered scheduling state. `platform_schedules` (jsonb) may
  // give each enabled platform its own send time + lifecycle; a null column is
  // the byte-for-byte unchanged path (every platform inherits the row time +
  // status). `enabled` is the send set (attached minus the paused sidecar).
  const platformSchedules = parsePlatformSchedules(
    (post as { platform_schedules?: unknown }).platform_schedules,
  );
  const failureInfo = isFailed
    ? explainPostFailure(
        post.error_message ??
          Object.values(platformSchedules).find((entry) => entry?.error_message)?.error_message ??
          null,
        failedPlatformsAtOpen.length > 0 ? failedPlatformsAtOpen : platforms,
      )
    : null;
  // The authoritative per-platform delivery record. Passed to every
  // effectivePlatformStatus call below so a chip can only say "published" with
  // positive proof for THAT platform — without it the helper falls back to the
  // row status and paints every enabled platform as delivered.
  const platformEvidence = (post as { platform_response?: unknown }).platform_response ?? null;
  const rowScheduledAt = exactTarget?.scheduledAt ?? post.scheduled_at ?? null;
  // The active tab's effective time + status, and whether it's locked (live).
  const activePlatformStatus = activePlatform
    ? effectivePlatformStatus(activePlatform, platformSchedules, status, platformEvidence)
    : null;
  const activePlatformTime = activePlatform
    ? effectiveSendTime(activePlatform, platformSchedules, rowScheduledAt)
    : null;
  const activeHasOwnTime = activePlatform
    ? platformSchedules[activePlatform]?.scheduled_at != null
    : false;
  const activePlatformLocked = activePlatform
    ? isPlatformLockedHelper(activePlatform, platformSchedules, status)
    : false;
  // Half-sent: the row is `partially_scheduled` (not draft/scheduled, so
  // isEditable is false), yet its still-pending platforms must stay editable
  // while its live platforms go read-only. Show the editor for those rows too,
  // and lock the caption only when the ACTIVE platform is live.
  const isInFlightPartial = status === 'partially_scheduled';
  const captionEditorVisible = isEditable || isInFlightPartial;
  // A preview-only tab (handleTogglePreviewPlatform) shows what the post
  // WOULD look like on an unconnected platform, but it is never attached —
  // editing here would write a per-platform caption/crop override for a
  // platform that isn't part of the post, orphaned data with nothing to
  // attach it to. Caption + reframe both gate on this; the media manager
  // and the publish footer are untouched since they already operate on the
  // real platform set, never the active tab.
  const isPreviewOnlyActiveTab = !!activePlatform && !platforms.includes(activePlatform);
  const activeCaptionEditable =
    !isPreviewOnlyActiveTab &&
    !activePlatformLocked &&
    (isEditable || activePlatformStatus === 'scheduled');
  // Task A5 follow-through (QA 2026-07-11): every readiness call MUST carry
  // the connection's account_type — evaluatePlatformReadiness fails CLOSED on
  // an absent value, so omitting it showed "Stories require a professional
  // account" even for a confirmed BUSINESS connection while PlacementPicker
  // (which passed it) allowed Story — the exact pill/picker disagreement A5
  // was built to prevent.
  const accountTypeFor = (platformId: string): string | null =>
    (
      connectedAccounts.find((account) => account.platform === platformId)?.rawData as
        { account_type?: string | null } | undefined
    )?.account_type ?? null;
  // ONE evaluation path for every platform (P1-2) — a single map covering
  // every ATTACHED platform (including paused ones), each scored with that
  // platform's REAL rowStatus/platformStatus/retryCount/hasUnsavedCaption.
  // Previously the active-chip call and the per-platform array call passed
  // different inputs: the array hard-coded rowStatus to draft/scheduled,
  // platformStatus to 'draft', and retryCount to 0, so it could never surface
  // the lifecycle states (`locked_live`, `retrying`, `terminal_failed`) the
  // active chip's real inputs could — the two could disagree on the exact
  // same platform. Building one map both callers read from makes that
  // structurally impossible, and keeps this the ONLY call site of
  // evaluatePlatformReadiness in this file. Not memoized (can't be — this
  // runs after the `!post` early return above, so a `useMemo` here would be a
  // conditionally-called hook); evaluatePlatformReadiness is pure/synchronous
  // over already-loaded data, so recomputing it per render for a handful of
  // platforms is free.
  const platformReadinessMap: Record<string, PostingReadinessResult> = {};
  // Preview-only platforms (handleTogglePreviewPlatform) get a readiness
  // entry too, purely so their tab renders the SAME honest "not connected"
  // badge/tooltip an attached-but-unconnected platform already gets —
  // nothing downstream of this map ever reads a key that isn't also in
  // enabledSendPlatforms (derived from `platforms` alone), so this can't
  // leak a preview platform into anything publish-related.
  for (const platformId of [...platforms, ...previewPlatforms]) {
    platformReadinessMap[platformId] = withUnsupportedPublishBlock(
      evaluatePlatformReadiness({
        platform: platformId,
        caption:
          platformId === activePlatform
            ? activeCaption
            : resolvePlatformCaption(post.content, platformCaptions, platformId),
        mediaUrls: post.media_urls ?? [],
        // Derive the KIND from the live url list, not the stored media_type —
        // a legacy row can carry media_type:'video' with 2 urls, and rule (a)
        // (2026-07-11) says >1 media IS a carousel everywhere. The dispatcher
        // applies the same clamp server-side, so readiness/preview/publish
        // can no longer disagree.
        mediaType: hasMedia ? inferMediaType((post.media_urls ?? []).filter(Boolean)) : 'text',
        connected: Boolean(connectedAccounts.find((account) => account.platform === platformId)),
        disabled: disabledPlatforms.includes(platformId),
        hasUnsavedCaption: platformId === activePlatform && activeCaptionIsDirty,
        rowStatus: status,
        platformStatus: effectivePlatformStatus(
          platformId,
          platformSchedules,
          status,
          platformEvidence,
        ),
        retryCount: platformSchedules[platformId]?.retry_count ?? post.retry_count ?? 0,
        videoDurationSeconds: post.video_duration_seconds ?? null,
        perPlatform: perPlatformOverride[platformId] ?? null,
        tiktokCreatorInfo: platformId === 'tiktok' ? tiktokCreator.info : null,
        tiktokVideoFacts: platformId === 'tiktok' ? tiktokVideoFacts : null,
        accountType: accountTypeFor(platformId),
      }),
    );
  }
  // THE crop this platform will actually publish, resolved once for every
  // reader below (the quality check, the preview, the ratio picker).
  //
  // It has to be one expression because they used to disagree. Choosing a crop
  // writes `media_keys.childManifest[child].renditions[platform:placement]`,
  // but the picker and the aspect check read `perPlatform[platform]` — a shape
  // nothing has set since per-child renditions landed, and which
  // `placementPatch` actively unsets. So the preview (which reads the child
  // first) showed a correct 4:5 while the pill still said "Original" and the
  // "Image shape" warning never cleared, no matter how many times the author
  // pressed the fix button. Same value, three readers, two sources of truth.
  //
  // Child first, then the per-platform entry as the legacy fallback: ~27 posts
  // predate the manifest and still carry their ratio there.
  const perPlatformAspect = activePlatform
    ? (() => {
        const child = readChildMediaState(
          post.media_urls ?? [],
          (post as { media_keys?: unknown }).media_keys,
        )[carouselActiveIndex];
        const destination = destinationKeyFor(activePlatform);
        return (
          child?.renditions[destination]?.aspectRatio ??
          perPlatformOverride[activePlatform]?.aspectRatio
        );
      })()
    : undefined;

  // gradeArtifact Stage 0 for the ACTIVE platform — the deterministic,
  // zero-model-call checks (format, aspect, caption length, link, fold,
  // commerce) run here on every render, so what the chip reports is the post
  // as it stands right now rather than as it was at generation time. Cheap
  // enough to sit beside the readiness loop above: same shape, same inputs,
  // no network and no image probe of its own — `sourceRatio` is the SAME
  // state the crop signal already measured, so the aspect check reuses that
  // one probe instead of adding a second.
  const activeQualityVerdict = activePlatform
    ? gradeArtifact({
        platform: activePlatform,
        caption: activeCaption,
        mediaUrls: post.media_urls ?? [],
        mediaType: hasMedia ? inferMediaType((post.media_urls ?? []).filter(Boolean)) : 'text',
        placementOverride: perPlatformOverride[activePlatform]?.placement ?? null,
        // The crop the author actually chose, wherever it is stored. Reading
        // `perPlatform` alone meant checkAspect never saw the fix its own
        // button had just applied, so "Image shape" could not be cleared.
        aspectOverride: perPlatformAspect ?? null,
        sourceRatio,
        hasCommerceContext: Boolean(
          post.source_kind !== LINKEDIN_AGENT_SOURCE_KIND &&
          (post.shopify_product_id || post.source_url || post.source_article_id),
        ),
      })
    : null;
  // `activePlatform` is corrected to `platforms[0]` by an effect ABOVE that
  // only runs post-paint (`setActivePlatform` inside a `useEffect`), so for
  // one frame right after the active platform is detached it can still hold
  // the id that just left `platforms` — `platformReadinessMap` (built from
  // `platforms`) has no entry for it. Falling back to `platforms[0]`'s entry
  // keeps the chip showing SOMETHING that one frame instead of vanishing
  // (review round 2, MINOR 5), rather than adding a second
  // `evaluatePlatformReadiness` call site for the miss case.
  const activeReadiness = activePlatform
    ? (platformReadinessMap[activePlatform] ??
      (platforms[0] ? (platformReadinessMap[platforms[0]] ?? null) : null))
    : null;
  const activeReadinessIssues = [
    ...(activeReadiness?.blockingIssues ?? []),
    ...(activeReadiness?.warningIssues ?? []),
  ];
  const reconciledQuality =
    captionEditorVisible && activeQualityVerdict && activePlatform
      ? reconcileVerdictWithReadiness(activeQualityVerdict, activeReadinessIssues)
      : null;
  const railLines = railLinesFor(reconciledQuality, activeReadinessIssues);
  // Publish gating stays scoped to SEND-eligible platforms (attached minus
  // paused) on purpose — a paused platform never blocks (or is required by)
  // the publish action, only the map behind it is now complete.
  const scheduleReadinessResults: PostingReadinessResult[] = enabledSendPlatforms
    .map((platformId) => platformReadinessMap[platformId])
    .filter((result): result is PostingReadinessResult => result != null);
  const scheduleReadinessIssue = scheduleReadinessResults
    .flatMap((result) => result.blockingIssues)
    .at(0);

  const handleSetPlatformTime = (platform: string, scheduledAt: string) => {
    setPlatformTimeMutation.mutate(
      { id: post.id, platform, scheduledAt },
      { onSuccess: () => refetch() },
    );
  };
  const handleClearPlatformTime = (platform: string) => {
    clearPlatformTimeMutation.mutate({ id: post.id, platform }, { onSuccess: () => refetch() });
  };

  // v6 in-place caption iteration — the 3-axis fix (spec §5.1, scenarios a+l).
  // Reads the ACTIVE caption (dirty draft first, else the resolved active
  // source — NOT post.content), targets the ACTIVE platform (NOT platforms[0]),
  // and writes + undoes through handleActiveCaptionSave (NOT raw
  // updatePost({content})), so per-platform overrides are honored. Caption
  // tweaks NEVER open chat — they always run the cheap Flash-Lite rewrite here.
  //
  // Branching on captionEditModeForStatus (spec §5.3, Task 4):
  //   'apply'  (draft / failed / etc.) → instant save + Undo toast (unchanged)
  //   'stage'  (scheduled) → rewrite is staged in captionRevision state; the
  //             inline Original⇄Revised compare (Keep/Revert) handles commit.
  //
  // Returns whether the edit actually LANDED (staged, or saved to the row).
  // handleToneSelect below is the only caller that reads it, and it has to:
  // persisting `tone_override` after a rewrite that failed or was never saved
  // would leave the column claiming a voice the caption does not have, and the
  // clause above would then keep re-asserting that wrong voice on every
  // subsequent edit.
  const runCaptionEdit = async (instruction: string, actionId = 'freeform'): Promise<boolean> => {
    if (!post) return false;
    // READER 2 of `tone_override`. Every verb in this toolbar carried a
    // generic voice: choosing a tone and then clicking "Shorten" washed the
    // chosen voice straight back out, which is what made the old
    // fire-and-forget tone menu feel like it never stuck. A tone action is
    // excluded because its own instruction already IS the voice — appending
    // the clause would state it twice, and on a tone SWITCH the clause would
    // still carry the OUTGOING tone (state updates after the write) and fight
    // the instruction. `resolveToneInstruction` falls back to the brand voice,
    // so this is also the first time the brand voice reaches these verbs at
    // all — /api/content/rewrite loads no brand context server-side.
    const toneClause = resolveToneInstruction({ toneOverride, brandTone });
    const finalInstruction =
      toneClause && !actionId.startsWith('tone:') ? `${instruction} ${toneClause}` : instruction;
    setOperatingAction(actionId);
    // Set from INSIDE the write path, never inferred from "nothing threw":
    // runCaptionEdit_ owns its own try/catch and reports failure as a toast,
    // so the only honest signal that a rewrite reached the row is the save
    // itself returning true.
    let landed = false;
    try {
      if (captionEditModeForStatus(post.status) === 'stage') {
        // Staged path — resolve original, rewrite, stage; do NOT save yet.
        const originalCaption = (activeCaptionIsDirty ? captionDraft : activeCaptionSource) ?? '';
        if (!originalCaption.trim()) {
          toast.error('Add a caption to rewrite first');
          return false;
        }
        if (!session?.access_token) {
          toast.error('Sign in required');
          return false;
        }
        try {
          const revised = await rewriteCaption({
            original: originalCaption,
            instruction: finalInstruction,
            accessToken: session.access_token,
            platform: toRewritePlatform(activePlatform),
          });
          // Stage the compare — do NOT call handleActiveCaptionSave yet.
          setCaptionRevision({ original: originalCaption, revised });
          landed = true;
        } catch (err) {
          toast.error(friendlyMessageWithRetry(err));
        }
      } else {
        // Instant path (draft / failed / pending_review / etc.) — apply + Undo.
        await runCaptionEdit_({
          activeCaptionSource,
          captionDraft,
          activeCaptionIsDirty,
          activePlatform,
          instruction: finalInstruction,
          accessToken: session?.access_token,
          rewriteCaption,
          // Same function, wrapped only to observe the outcome — the Undo
          // action inside runCaptionEdit_ calls this again long after we have
          // returned, which is harmless: `landed` is read once, immediately.
          saveCaption: async (text: string) => {
            const saved = await handleActiveCaptionSave(text);
            landed = landed || saved;
            return saved;
          },
          toast,
        });
      }
    } finally {
      setOperatingAction(null);
    }
    return landed;
  };

  const handleCustomPromptSubmit = (prompt: string) => {
    void runCaptionEdit(prompt);
  };

  // Task 16 — one-shot "Rewrite for each platform" fan-out. Master-tab-only
  // (gated in resolveCaptionQuickActions on `editingShared`), fires the same
  // per-platform rewrite as the platform tab's own "Rewrite for X" pill, in
  // parallel, across every SEND-ELIGIBLE platform (`enabledSendPlatforms` —
  // NOT the raw `platforms` attached set: a paused platform never publishes,
  // so rewriting its caption would be a paid AI call for nothing usable) —
  // skipping any platform `hasPlatformOverride` already reports as
  // hand-customized (no N-way undo exists for a silent overwrite, so skip is
  // the only safe default). Every successful platform's result lands in ONE
  // merged `platform_captions` write via `saveOverrides` below, using the
  // SAME begin/settlePlatformCaptionsCommit machinery
  // `handleActiveCaptionSave`'s per-platform branch already uses — this is a
  // bulk trigger for that existing write path, not a new one. Control flow
  // itself lives in `runCaptionFanOut` (dependency-injected, unit-tested) so
  // the skip/partial-failure toast contract doesn't require mounting this
  // 3,900-line component.
  const handleFanOutRewrite = async () => {
    if (!post) return;
    const masterCaption =
      (editingShared && activeCaptionIsDirty ? captionDraft : post.content) ?? '';
    // READER 3 of `tone_override`. The per-platform instruction is composed
    // INSIDE runCaptionFanOut (captionPlatformRewriteInstruction), so the tone
    // clause is appended by wrapping the injected rewrite service rather than
    // widening that module's deps — one seam, and every one of the N parallel
    // calls gets the clause without runCaptionFanOut learning about tone at
    // all. Without it a fan-out silently reverts every platform to a generic
    // voice, which is the same "it never stuck" failure as the verbs above.
    const fanOutToneClause = resolveToneInstruction({ toneOverride, brandTone });
    setOperatingAction('fan-out');
    try {
      await runCaptionFanOut({
        masterCaption,
        platforms: enabledSendPlatforms,
        // A function, not a snapshot — re-read right before the merged write
        // (see run-caption-fan-out.ts) so a commit that lands on a DIFFERENT
        // platform tab while the N parallel rewrite calls are in flight isn't
        // clobbered by a merge against a stale pre-fan-out snapshot.
        getPlatformCaptions: () => platformCaptionsRef.current,
        sharedContent: post.content,
        accessToken: session?.access_token,
        platformLabel,
        rewriteCaption: fanOutToneClause
          ? (args) =>
              rewriteCaption({ ...args, instruction: `${args.instruction} ${fanOutToneClause}` })
          : rewriteCaption,
        saveOverrides: async (nextCaptions) => {
          const targetPostId = post.id;
          beginPlatformCaptionsCommit(nextCaptions);
          const { error } = await postService.updatePost(post.id, {
            platform_captions: nextCaptions,
            expectedUpdatedAt: post.updated_at ?? undefined,
          });
          settlePlatformCaptionsCommit(targetPostId, !error, nextCaptions);
          if (error) {
            // CONFLICT means someone/something else already saved this post
            // (very plausible right here: editing the master caption then
            // immediately firing the fan-out blurs the textarea, which
            // commits its own save first and bumps `updated_at` out from
            // under this call's `expectedUpdatedAt`) — mirrors
            // handleActiveCaptionSave's CONFLICT branch above so the same
            // class of failure gets the same honest "reload" advice instead
            // of a useless "try again" (retrying replays the same stale
            // token and conflicts again).
            if (error.code === 'CONFLICT') return { ok: false, reason: 'conflict' };
            if (isLockedEditError(error)) {
              showLockedEditToast(LOCKED_CAPTION_TOAST_DESCRIPTION);
              return { ok: false, reason: 'locked' };
            }
            return { ok: false, reason: 'unknown' };
          }
          setCaptionSavedAt(Date.now());
          refetch();
          invalidatePostSurfaces();
          return { ok: true };
        },
        toast: {
          success: (msg) => toast.success(msg),
          error: (msg) => toast.error(msg),
          info: (msg) => toast(msg),
        },
      });
    } finally {
      setOperatingAction(null);
    }
  };

  // The tone control's write half. Deliberately NOT gated on
  // `expectedUpdatedAt`: the rewrite above has already saved the caption and
  // bumped `updated_at`, so the `post` snapshot this closure holds is stale by
  // construction and an optimistic gate here would conflict with our OWN
  // write, every single time. That is safe for this column in a way it is not
  // for `content` — `tone_override` is a scalar the user just chose outright,
  // not a read-modify-write merge that could clobber someone else's text
  // (`handleResetPlatformCaption` above writes unguarded for the same reason).
  const persistToneOverride = async (nextTone: string | null) => {
    const { error } = await postService.updatePost(post.id, { tone_override: nextTone });
    if (error) {
      if (isLockedEditError(error)) {
        showLockedEditToast(LOCKED_CAPTION_TOAST_DESCRIPTION);
      } else {
        // Named honestly rather than as a generic "failed": the caption DID
        // change, so a bare error would leave the user unsure which half
        // happened. Saying which one stuck tells them what to do next.
        toast.error('Caption rewritten, but the tone was not saved', {
          description: 'Later edits will use the previous tone until you pick again.',
        });
      }
      return;
    }
    refetch();
    invalidatePostSurfaces();
  };

  const handleToneSelect = async (toneId: string, instruction: string) => {
    const landed = await runCaptionEdit(instruction, `tone:${toneId}`);
    if (!landed) return;
    await persistToneOverride(toneId === BRAND_TONE_OPTION_ID ? null : toneId);
  };

  // "Back to brand voice" — a rewrite IN the brand voice plus a clear, not a
  // silent column reset: clearing alone would leave the caption sitting in the
  // preset the user just walked away from while the row claimed otherwise.
  // With no brand voice to return to, the control still offers "Clear tone",
  // and there is no honest instruction to send, so only the column changes.
  const handleToneClear = async () => {
    const brandInstruction = toneInstructionFor({ toneId: BRAND_TONE_OPTION_ID, brandTone });
    if (!brandInstruction) {
      await persistToneOverride(null);
      return;
    }
    const landed = await runCaptionEdit(brandInstruction, `tone:${BRAND_TONE_OPTION_ID}`);
    if (!landed) return;
    await persistToneOverride(null);
  };

  // Scheduled-only Original⇄Revised commit handlers (spec §5.3).
  // The revised caption is NOT yet persisted (unlike Task 2's instant path) —
  // Keep commits it now; Revert is a PURE STATE DISCARD (no DB write).
  //
  // Why Revert needs no save: the 'stage' path in runCaptionEdit only writes to
  // captionRevision state — it never touches captionDraft or persisted data. The
  // editor already shows the persisted original. Calling handleActiveCaptionSave
  // with the original would be a spurious network write of already-persisted data
  // that could surface a failure toast for an operation the user saw as "never mind".
  //
  // Keep reads as skill-purple (commit action); Revert is the neutral exit (never
  // skill-purple). Keep flows through handleActiveCaptionSave; Revert does not.
  const handleKeepRevision = async () => {
    if (!captionRevision) return;
    // Commit the revised caption — it was staged but not yet persisted.
    const kept = await handleActiveCaptionSave(captionRevision.revised);
    if (kept) {
      setCaptionRevision(null);
      toast.success('Revision kept');
    } else {
      toast.error('Keep failed — caption not saved');
    }
  };
  const handleRevertRevision = () => {
    if (!captionRevision) return;
    // Pure state discard — the staged revision is dropped and the compare panel
    // disappears. captionDraft was never updated during staging so the editor
    // already shows the persisted original. No DB write needed.
    setCaptionRevision(null);
    toast('Revision discarded');
  };

  const previewKind = platformToKind(activePlatform);

  // Route media to the right presentation-shell props — video-first posts render <video>,
  // not an <img>. Declared before the reframe block, which reads isVideoFirst.
  const media = resolveMediaProps({
    ...post,
    media_keys: (post as { media_keys?: MediaKeysBag }).media_keys,
  });
  const hasVideoMedia = media.mediaItems.some((item) => item.kind === 'video');
  const isSingleVideo = media.mediaItems.length === 1 && hasVideoMedia;

  // Reposition (focal) — saved value for the active platform, plus the live drag
  // value so the preview moves under the cursor before we persist on release.
  // Plain functions (not hooks) so they can live below the loading early-return.
  // pendingFraming sits between the live drag value and the committed saved
  // value: it echoes a framing commit at CALL time (the override map itself
  // only updates inside the serialized queue run), so drag-release doesn't
  // snap back and rapid keyboard nudges accumulate from the latest target.
  const safeCarouselActiveIndex = Math.min(
    Math.max(0, carouselActiveIndex),
    Math.max(0, media.mediaItems.length - 1),
  );
  const destinationKey = activePlatform ? destinationKeyFor(activePlatform) : null;
  const childStates = readChildMediaState(
    post.media_urls ?? [],
    (post as { media_keys?: unknown }).media_keys,
  );
  const savedFocal = activePlatform
    ? {
        ...perPlatformOverride[activePlatform],
        ...(destinationKey && childStates[safeCarouselActiveIndex]?.renditions[destinationKey]
          ? childStates[safeCarouselActiveIndex].renditions[destinationKey]
          : {}),
      }
    : undefined;
  // Focal coordinates only have meaning inside a creator-selected crop frame.
  // Do not expose a drag affordance for native media: without this guard a
  // benign reposition silently persisted a crop command based on the platform
  // recommendation, recreating the preview/dispatch divergence we are fixing.
  const hasExplicitCrop = Boolean(savedFocal?.aspectRatio);
  const activeFocalX = dragFocal?.x ?? pendingFraming?.focalX ?? savedFocal?.focalX ?? 0.5;
  const activeFocalY = dragFocal?.y ?? pendingFraming?.focalY ?? savedFocal?.focalY ?? 0.5;
  const activeZoom = zoomDraft ?? pendingFraming?.zoom ?? savedFocal?.zoom ?? 1;
  const isRepositioning = dragFocal != null;
  const isZooming = zoomDraft != null;
  // Shows the same "you're reframing" affordances (rule-of-thirds grid, hides
  // the reposition hint) whether the drag or the zoom slider is the active input.
  const isReframing = isRepositioning || isZooming;
  // The dispatcher rejects an explicit crop whenever ANY child is video.
  // Guard the editor on the same condition; checking only the first child
  // exposed a reframe control for [image, video] carousels that could never
  // be materialized safely.
  const canReframe =
    isEditable && !isPreviewOnlyActiveTab && hasMedia && !hasVideoMedia && hasExplicitCrop;
  // Custom framing = anything off the centre-fill default. Drives Reset + the
  // "this is your saved post" cue + the Apply-to-all offer.
  const isFramed = activeFocalX !== 0.5 || activeFocalY !== 0.5 || Math.abs(activeZoom - 1) > 0.001;
  // Cap = the zoom at which the on-screen preview hits native source resolution
  // (computeMaxZoom's contract) — past this the CSS transform:scale visibly
  // softens the image. Needs both the source's real pixel size (probed on
  // load) and the frame's live rendered size (tracked via ResizeObserver).
  // Null until both have resolved AND there's real headroom to zoom into
  // (>1.01 — below that a slider would have no throw, Subtraction), so the
  // slider only appears once its bound is both trustworthy and worth showing.
  const rawMaxZoom =
    canReframe &&
    sourceRatio &&
    sourceNaturalWidth &&
    frameSize &&
    frameSize.w > 0 &&
    frameSize.h > 0
      ? computeMaxZoom({
          sourceW: sourceNaturalWidth,
          sourceH: sourceNaturalWidth / sourceRatio,
          frameRatio: frameSize.w / frameSize.h,
          renderedFrameW: frameSize.w,
        })
      : null;
  const maxZoom = rawMaxZoom != null && rawMaxZoom > 1.01 ? rawMaxZoom : null;
  const nudgeFocal = (dx: number, dy: number) => {
    const nx = Math.min(1, Math.max(0, activeFocalX + dx));
    const ny = Math.min(1, Math.max(0, activeFocalY + dy));
    void commitFraming({ focalX: nx, focalY: ny });
  };
  const handleReframeKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!canReframe) return;
    const step = e.shiftKey ? 0.1 : 0.02;
    if (e.key === 'ArrowLeft') return (e.preventDefault(), nudgeFocal(-step, 0));
    if (e.key === 'ArrowRight') return (e.preventDefault(), nudgeFocal(step, 0));
    if (e.key === 'ArrowUp') return (e.preventDefault(), nudgeFocal(0, -step));
    if (e.key === 'ArrowDown') return (e.preventDefault(), nudgeFocal(0, step));
  };

  const handleReframePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!canReframe) return;
    // Only drag the PHOTO, never the chrome (header, action row, carousel
    // carets) — a bare `tagName === 'IMG'` check also matches the small header
    // avatar rendered inside this same wrapper, computing overflow from its
    // 32px box and snapping the photo on any drag starting near it (verified
    // live). `data-media-slot-photo` is set only on MediaSlot's own frame image.
    const target = e.target as HTMLElement;
    if (target.tagName !== 'IMG' || !target.hasAttribute('data-media-slot-photo')) return;
    const rect = target.getBoundingClientRect();
    // Pannable overflow = the part of the photo cropped OUT of the frame on the
    // bound axis (object-cover). Dividing the drag by this — not the full image
    // width — makes the photo track the cursor 1:1 (native feel) instead of
    // crawling. The other axis has 0 overflow, so it doesn't move.
    const imgRatio = sourceRatio ?? rect.width / Math.max(1, rect.height);
    const frameRatio = rect.width / Math.max(1, rect.height);
    let overflowX = 0;
    let overflowY = 0;
    if (frameRatio > imgRatio) {
      overflowY = rect.width / imgRatio - rect.height; // width-bound → vertical crop
    } else {
      overflowX = rect.height * imgRatio - rect.width; // height-bound → horizontal crop
    }
    // Zoom enlarges the photo, adding overflow on both axes.
    const z = Math.max(1, activeZoom);
    overflowX = overflowX * z + rect.width * (z - 1);
    overflowY = overflowY * z + rect.height * (z - 1);
    e.currentTarget.setPointerCapture(e.pointerId);
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseX: activeFocalX,
      baseY: activeFocalY,
      overflowX,
      overflowY,
    };
    setDragFocal({ x: activeFocalX, y: activeFocalY });
  };

  const handleReframePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const st = dragState.current;
    if (!st) return;
    // Move 1:1 with the cursor on the cropped axis (overflow); axes with no crop
    // (overflow ~0) stay put. Dragging right reveals the photo's left edge.
    const nextX =
      st.overflowX > 2
        ? Math.min(1, Math.max(0, st.baseX - (e.clientX - st.startX) / st.overflowX))
        : st.baseX;
    const nextY =
      st.overflowY > 2
        ? Math.min(1, Math.max(0, st.baseY - (e.clientY - st.startY) / st.overflowY))
        : st.baseY;
    setDragFocal({ x: nextX, y: nextY });
  };

  const handleReframePointerUp = () => {
    const st = dragState.current;
    dragState.current = null;
    setDragFocal((cur) => {
      if (st && cur && (cur.x !== st.baseX || cur.y !== st.baseY)) {
        commitFraming({ focalX: cur.x, focalY: cur.y });
      }
      return null;
    });
  };

  const handleResetFraming = () => {
    setZoomDraft(null);
    void commitFraming({ focalX: 0.5, focalY: 0.5, zoom: 1 });
  };
  const handleApplyFramingToAll = () => {
    // Framing can only move within a frame, so a platform with no crop set has
    // nothing to apply. It used to be given one — a fabricated 1:1 the author
    // never chose. Skipping is correct, but skipping silently is not: the
    // optimistic drag simply reverted under a toast claiming success.
    const selected = selectedChildForPost();
    const child = readChildMediaState(
      post.media_urls ?? [],
      (post as { media_keys?: unknown }).media_keys,
    ).find((item) => item.childId === selected?.childId);
    const eligible = platforms.filter((platform) => child?.renditions[destinationKeyFor(platform)]);
    const skipped = platforms.filter((platform) => !eligible.includes(platform));

    if (eligible.length === 0) {
      toast.error('Choose a crop on the other platforms first — there is no frame to position in.');
      return;
    }
    void commitFraming({ focalX: activeFocalX, focalY: activeFocalY, zoom: activeZoom }, eligible);
    if (skipped.length > 0) {
      toast.success(
        `Framing applied to ${eligible.length} platform${eligible.length === 1 ? '' : 's'}. ` +
          `${skipped.join(', ')} ${skipped.length === 1 ? 'has' : 'have'} no crop set.`,
      );
      return;
    }
    toast.success('Framing applied to all platforms');
  };

  // MediaKind for the placement resolver — the same video signal the preview
  // itself uses (media.isVideoFirst), extended with carousel/text so
  // resolvePlacement's platform × placement × mediaKind intersection has
  // what it needs. No media at all → 'text' (never resolves a chrome row).
  // Rule (a), 2026-07-11 preview↔publish coherence: >1 media of ANY mix is a
  // carousel — the IG publisher's >1-media branch ships a CAROUSEL container
  // regardless of placement, so a video+image post must never preview as a
  // Reel (Reel disappears from the picker via PLACEMENT_MEDIA_KINDS).
  const totalMediaCount = (post.media_urls ?? []).filter(Boolean).length;
  const mediaKind: MediaKind = !hasMedia
    ? 'text'
    : totalMediaCount > 1
      ? 'carousel'
      : media.isVideoFirst
        ? 'video'
        : 'image';

  // Task A5: per-CONNECTION publishable ceiling for the active platform —
  // narrower than (or equal to) the static PUBLISHABLE_PLACEMENTS table when
  // the connection can't actually publish to every statically-publishable
  // placement (e.g. an Instagram connection that isn't a confirmed
  // Business/Creator account can't publish Stories even though Story is
  // globally publishable). `rawData` is the raw `user_connections` row
  // (`usePlatformIntegrations`'s `ConnectedAccount.rawData`), which carries
  // `account_type` — an unset/unknown value fails CLOSED (see
  // connectionSupportsPlacement), never assumed Business.
  const activeConnectionAccountType = activePlatform ? accountTypeFor(activePlatform) : null;
  const connectionPlacements = activePlatform
    ? connectionPublishablePlacements(activePlatform, activeConnectionAccountType)
    : [];

  // Effective placement for the preview (Task A3, narrowed per-connection by
  // Task A5): an explicit stored override (PlacementPicker) wins when it's a
  // valid row for this platform+mediaKind AND within the per-connection
  // publishable ceiling — `isPlacementOverrideHonored` is the SAME check
  // PlacementPicker's trigger label uses, so the big preview stage and the
  // picker trigger can never disagree about whether an override is honored
  // (e.g. a stored Story override on a non-Business connection must not
  // silently preview Story chrome while the picker says "ignored").
  // Otherwise resolvePlacement falls back to the exact legacy derivation
  // this ternary used to inline — video-first → the platform's chrome
  // surface (Reel/Short/Story-shaped preview), else the primary (feed)
  // surface — so unmigrated posts render pixel-identical to before A3.
  const storedPlacement = activePlatform
    ? exactTarget?.platform === activePlatform
      ? (exactTarget.placement as Placement)
      : perPlatformOverride[activePlatform]?.placement
    : undefined;
  const effectivePlacementOverride =
    activePlatform &&
    isPlacementOverrideHonored(activePlatform, mediaKind, storedPlacement, connectionPlacements)
      ? storedPlacement
      : undefined;
  const previewRow = activePlatform
    ? resolvePlacement({
        platform: activePlatform,
        mediaKind,
        override: effectivePlacementOverride,
      })
    : undefined;
  const effectiveKind = previewRow?.previewKind ?? previewKind;
  const hasChromePreview = !!previewRow?.hasChrome && hasMedia;
  // `showSafeZoneOverlay` is computed further down, once the rendered frame
  // ratio is known — see the note beside `effectiveMediaRatio`.

  // Advisory crop signal: only when NO override is set and the source falls
  // outside the placement's known accept window. Native platform rendering is
  // provider-controlled, so this is a warning—not a claim that Mora will crop.
  // An override means the user chose the crop → no warning.
  const hasOverride = !!perPlatformAspect;
  const cropSignal =
    !hasVideoMedia && hasMedia ? cropSignalFor(sourceRatio, previewRow, hasOverride) : null;
  const videoOffRatio =
    isSingleVideo && hasMedia && previewRow != null && sourceRatio != null
      ? edgeCropFraction(sourceRatio, previewRow.acceptMin, previewRow.acceptMax).fraction >=
        MINOR_CROP_THRESHOLD
      : false;
  // Reframe & Fit (image only): make the recommended framing explicit. This is
  // a creator request, not a statement about how a provider will render native
  // media; the delivery pipeline still owns rendition readiness. Reversible
  // via the picker.
  const handleReframeToFit = () => {
    if (hasVideoMedia || !previewRow || !activePlatform) return;
    handleAspectRatioChange(previewRow.recommendedRatio);
    toast.success(`Crop framing selected for ${platformLabel(activePlatform)}`);
  };

  // Fit the preview to its ACTUAL stage, not a fixed worst-case viewport
  // budget. The previous `(85vh - 320px)` allowance treated every replica as
  // Facebook/LinkedIn chrome, shrinking a 4:5 Instagram card to ~234px on a
  // 720px screen and making 9:16 players even smaller. The left preview pane
  // is a size container, so `cqh` measures the space this exact inspector has;
  // each replica reserves only its own non-media chrome and otherwise grows to
  // the width its actual stage permits. If a long caption exceeds that estimate, the
  // stage scrolls rather than silently making the preview tiny.
  // Stabilized against the load-flash: sourceRatio resets to null the instant
  // new media is set (see the probe effect above) and only resolves once the
  // hidden probe <img> finishes downloading. Falling straight through to a
  // hardcoded `1` (square) in that gap made the preview render at the WRONG
  // width for one paint, then pop to the real (often taller) aspect once the
  // probe resolved — a visible "loads small, then the dialog expands" flash
  // (caught in live review, 2026-07-01). Retaining the last genuinely-known
  // ratio across that gap keeps the preview's size stable instead of
  // committing to a guess it immediately has to correct.
  // A saved ratio is an explicit crop command. Without one, preview the media
  // Mora will actually dispatch: the source image (or the video's declared
  // aspect). Matrix recommendations are menu advice only; using one here made
  // LinkedIn show a cropped 4:5 frame while its adapter uploaded the original.
  const childAspectOverrides = childStates.map(
    (child) => (destinationKey ? child.renditions[destinationKey]?.aspectRatio : undefined) ?? null,
  );
  const hasMixedChildAspectOverrides =
    childAspectOverrides.some(Boolean) && new Set(childAspectOverrides.filter(Boolean)).size > 1;
  const uniformChildAspect =
    childAspectOverrides.length > 0 &&
    childAspectOverrides.every((ratio) => ratio === childAspectOverrides[0])
      ? childAspectOverrides[0]
      : null;
  // An explicit destination crop intentionally applies one selected geometry
  // to every child. For native carousels, hand the presentation shell each child's
  // intrinsic ratio instead, so switching from a 4:5 slide to a landscape
  // slide changes the preview frame rather than continuing to show slide one.
  const nativeCarouselAspectRatios =
    hasMixedChildAspectOverrides || !perPlatformAspect
      ? media.mediaItems.map(
          (item) =>
            (destinationKey && item.renditions?.[destinationKey]?.aspectRatio) ??
            sourceRatiosByUrl[item.url] ??
            (item.url === probeUrl
              ? sourceRatio
              : item.kind === 'video'
                ? parseAspectValue(post.video_aspect_ratio)
                : null),
        )
      : undefined;
  const nativeActiveAspectRatio = nativeCarouselAspectRatios?.length
    ? (nativeCarouselAspectRatios[safeCarouselActiveIndex] ?? null)
    : sourceRatio;
  // The scalar is intentionally omitted for native image media. The
  // presentation shell then resolves the active child's intrinsic ratio from
  // imageAspectRatios. A saved scalar remains a deliberate uniform destination
  // crop and overrides every child.
  const previewAspectRatio =
    uniformChildAspect ??
    (hasMixedChildAspectOverrides ? null : perPlatformAspect) ??
    (isSingleVideo ? (post.video_aspect_ratio ?? null) : nativeActiveAspectRatio);
  const previewPropAspectRatio =
    uniformChildAspect ??
    (hasMixedChildAspectOverrides ? undefined : perPlatformAspect) ??
    (isSingleVideo ? (post.video_aspect_ratio ?? null) : undefined);
  const rawEffectiveMediaRatio =
    typeof previewAspectRatio === 'number'
      ? previewAspectRatio
      : (parseAspectValue(previewAspectRatio) ?? sourceRatio);
  // NOTE: this can't be captured in a useEffect — this line sits after the
  // component's early-return guards (line ~568), and a hook may never be
  // introduced after a conditional return (rules-of-hooks). A plain render-
  // body assignment is the accepted pattern here: it's idempotent per input
  // (writing the same value twice on a StrictMode double-render is harmless)
  // and only affects what a LATER render reads, never this render's output.
  if (rawEffectiveMediaRatio != null) {
    lastKnownMediaRatioRef.current = rawEffectiveMediaRatio;
  }
  const effectiveMediaRatio = rawEffectiveMediaRatio ?? lastKnownMediaRatioRef.current;

  // Safe-zone bands are 9:16 fractions. They are meaningful when the thing they
  // are painted over is a 9:16 stage — which is a property of the RENDERED
  // FRAME, not of the preview kind and not of the matrix's `hasChrome` flag.
  //
  // Two failure modes to avoid, and an earlier revision of this fix traded one
  // for the other:
  //   • `hasChrome` alone painted bands over ANY box, including a 1.78
  //     landscape one, because facebook-reel/story are `hasChrome: true` but
  //     route to the FacebookPost feed card.
  //   • Gating on the replica kind instead then deleted the bands for those two
  //     placements ENTIRELY — but their matrix rows are videoOnly with
  //     acceptMin === acceptMax === 0.5625, so their box genuinely is 9:16 in
  //     the normal case and the guidance was correct. That over-correction
  //     removed the most specific safeZones data in the file.
  //
  // So: show them over a dedicated full-bleed stage (where the article itself is
  // 9:16 regardless of what the media does inside it), OR wherever the rendered
  // frame actually is 9:16.
  const NINE_BY_SIXTEEN = 9 / 16;
  const frameIsVertical = Math.abs(effectiveMediaRatio - NINE_BY_SIXTEEN) < 0.02;
  const safeZonesApply =
    hasChromePreview &&
    !!effectiveKind &&
    (rendersFullBleedStage(effectiveKind) || frameIsVertical);
  const showSafeZoneOverlay = safeZonesApply && showSafeZones;
  // Regression fix (2026-07-25, founder-reported): a prior change moved
  // zoomSliderRow and controlsStrip OFF the pane's bottom edge and INTO this
  // same centered/scrolling column as the card (see the `preview-stage`
  // container below) — so they now consume real `cqh` height too, but this
  // calc still only reserved space for the container's own padding. The card
  // sized itself as if it had the whole column, pushed the actual rows past
  // 100cqh, and the pane started scrolling on ordinary desktop heights.
  // These booleans are HOISTED from (must stay identical to) the render
  // conditions for zoomSliderRow / controlsStrip further down this file —
  // duplicating the arithmetic there instead of sharing these would let the
  // reserve and the render drift apart again.
  //
  // canReframeZoom = the toggle's own visibility condition (is there real
  // zoom headroom to expose at all) — it lives INSIDE controlsStrip, so it
  // never adds a row of its own; only the SLIDER (gated additionally on
  // zoomPanelOpen) reserves one, and only while it's actually open.
  const canReframeZoom = canReframe && maxZoom != null;
  const showsZoomRow = canReframeZoom && zoomPanelOpen;
  const showsFramingControlsRow = Boolean(
    activePlatform &&
    (hasMedia ||
      hasChromePreview ||
      cropSignal ||
      videoOffRatio ||
      (canReframe && isFramed) ||
      destinationOptionsForActivePlatform.length > 1) &&
    (isEditable || isInFlightPartial),
  );
  // Row heights below are real, not filler — they're what actually renders
  // in the centered column under the card, and each is 0 unless its row is
  // present (a caption-only post with no crop signal, safe zones, or zoom
  // headroom should still get the full height the card is entitled to).
  // These MUST track the actual stage container, and the two shells differ:
  // mobile is `gap-3 px-4 py-6` (line ~4535), desktop is `gap-2 px-4 py-4`
  // (line ~4589). Hardcoding the desktop numbers under-reserved 16px plus 4px
  // per rendered row on mobile, so the pane scrolled where it should have fit
  // exactly. That was slack while the media box had no height and could never
  // approach `100cqh`; it stops being slack the moment the media renders.
  const PREVIEW_STAGE_VERTICAL_PADDING = isMobile ? 48 : 32; // py-6 vs py-4
  const PREVIEW_STAGE_ROW_GAP = isMobile ? 12 : 8; // gap-3 vs gap-2
  const ZOOM_ROW_HEIGHT = 16; // zoomSliderRow's tallest child is the Slider thumb (h-4)
  // The strip is TALLER on mobile and this constant used to ignore that — its
  // own comment said "on desktop" and nobody acted on it. Mobile buttons are
  // `size-9` (36px) not `size-7` (28px), and the row adds `pb-2.5` (10px):
  // ~46px against a 28px reserve, i.e. ~18px still unreserved on exactly the
  // viewport the padding/gap fix above was meant to correct.
  const CONTROLS_STRIP_HEIGHT = isMobile ? 46 : 28; // size-9 + pb-2.5 vs size-7
  const extraStageRows = (showsZoomRow ? 1 : 0) + (showsFramingControlsRow ? 1 : 0);

  /**
   * 🔴 THE ROWS ARE MEASURED NOW. The constants above are only a FIRST GUESS.
   *
   * Everything under the card — the crop warning, the placement and ratio
   * pickers, the zoom slider — lives inside this height-constrained column, so
   * the card has to leave room for it. That room used to be a sum of
   * hand-maintained pixel constants, each of which had to track a class string
   * somewhere else in this 4,960-line file. The comments above record three
   * separate incidents where one drifted and the pane silently started
   * scrolling; the founder reported a fourth on 2026-08-21 — "you can't even
   * see it on this page, which is a bit weird."
   *
   * A fourth instance of one bug is not four bugs, it is one mechanism. So the
   * constants stop being the answer and become the pre-measurement estimate:
   * `stageExtrasRef` wraps the real rows, a `ResizeObserver` reports what they
   * actually occupy, and the reserve follows reality from the first frame
   * after mount. A constant being wrong is no longer a defect — it is a guess
   * that gets corrected before anyone sees it.
   *
   * Kept as an estimate rather than starting at 0 so the card does not size
   * itself for a full column and then visibly shrink one frame later.
   */
  const estimatedExtrasHeight =
    extraStageRows * PREVIEW_STAGE_ROW_GAP +
    (showsZoomRow ? ZOOM_ROW_HEIGHT : 0) +
    (showsFramingControlsRow ? CONTROLS_STRIP_HEIGHT : 0);

  // `100cqh` comes from the mobile/desktop preview-stage container below.
  const previewStageReservedHeight =
    PREVIEW_STAGE_VERTICAL_PADDING +
    (measuredExtrasHeight ?? estimatedExtrasHeight) +
    platformPreviewChromeHeight(effectiveKind);
  // The stage passes sizeMode="fill", which deliberately bypasses
  // PlatformPreview's own `platformNaturalMaxWidth` wrapper so the card can be
  // driven by available height instead. That left width derived ONLY from
  // height x aspect-ratio, so a wide frame (1.91:1 on Facebook/LinkedIn) grew
  // to ~1000px on a tall viewport — roughly twice the real feed, with caption
  // lines running ~170 characters. Fold the platform's own cap back into the
  // same min() so the height-driven sizing still applies, bounded by a width
  // the platform would actually render.
  const previewNaturalCap = effectiveKind ? platformNaturalMaxWidth(effectiveKind) : null;
  // The height-driven term is allowed to shrink the card, but only down to a
  // width where the platform's own chrome still renders honestly. Below roughly
  // 320px a feed card stops being a preview and starts being a caricature:
  // Facebook's Like/Comment/Share collide into each other, and Instagram's
  // caption wraps to a column two or three words wide. A preview that lies
  // about the platform is worse than a pane that scrolls, so past this floor we
  // stop shrinking and let `overflow-y-auto` do its job — the founder's
  // "shouldn't overflow unless absolutely necessary" cuts both ways.
  // Narrow-by-design frames (Pinterest 260, TikTok/Reels 300) would never reach
  // 320, so the floor is clamped to their own cap rather than inflating them.
  const previewLegibleFloor = Math.min(320, previewNaturalCap ?? 320);
  const previewMaxWidth = `min(100%, ${previewNaturalCap ? `${previewNaturalCap}px, ` : ''}max(${previewLegibleFloor}px, calc((100cqh - ${previewStageReservedHeight}px) * ${Math.max(0.3, effectiveMediaRatio).toFixed(3)})))`;

  // ============================================================================
  // Section building blocks — defined once, then composed into TWO layouts:
  //   • Desktop (md+): the 2-pane grid (preview stage | editing rail).
  //   • Mobile: a SINGLE vertical scroll with ONE header + ONE pinned footer —
  //     the desktop grid naively stacked on a phone produced duplicate headers,
  //     a 34vh box that chopped the media, and the caption rendered twice. The
  //     mobile shell shows the preview first, then the editing sections, in one
  //     coherent scroll (the standard mobile composer pattern).
  // ============================================================================

  const previewBlock = effectiveKind ? (
    <div
      ref={previewFrameRef}
      onPointerDown={handleReframePointerDown}
      onPointerMove={handleReframePointerMove}
      onPointerUp={handleReframePointerUp}
      onPointerCancel={handleReframePointerUp}
      onKeyDown={handleReframeKeyDown}
      tabIndex={canReframe ? 0 : undefined}
      role={canReframe ? 'group' : undefined}
      aria-label={
        canReframe
          ? `Reframe image for ${platformLabel(activePlatform)} — drag or arrow keys`
          : undefined
      }
      style={{
        maxWidth: hasMedia
          ? previewMaxWidth
          : previewNaturalCap
            ? `${previewNaturalCap}px`
            : undefined,
      }}
      className={cn(
        'w-full touch-none rounded-xl',
        ARTIFACT_PREVIEW_CARD,
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        canReframe
          ? isRepositioning
            ? '[&_img]:cursor-grabbing'
            : '[&_img]:cursor-grab'
          : undefined,
      )}
    >
      <EditorPlatformPreview
        kind={effectiveKind}
        sizeMode="fill"
        article={linkedInArticleCard(post) ?? pastedLink.card}
        brandName={previewBrandName}
        username={previewHandle}
        avatarUrl={previewAvatarUrl}
        brandColor={brandKit?.primary_color ?? undefined}
        caption={activeCaption}
        timestamp={isLiveOrPartial ? (post.published_at ?? undefined) : undefined}
        imageUrl={media.imageUrl}
        imageUrls={media.imageUrls}
        mediaItems={media.mediaItems}
        imageAspectRatios={nativeCarouselAspectRatios}
        carouselActiveIndex={safeCarouselActiveIndex}
        onCarouselActiveIndexChange={setCarouselActiveIndex}
        videoUrl={media.videoUrl}
        videoPoster={media.videoPoster}
        tiktokMediaKind={isVideoPost ? 'video' : 'photo'}
        tiktokSound={tiktokPreviewSound}
        tiktokCoverLabel={tiktokPreviewCoverLabel}
        // Blank, not omitted: MediaSlot falls back to its own default hint
        // text whenever `emptyHint` is nullish, so leaving this undefined
        // would still render "Add media to preview" underneath — exactly
        // stacked on top of the interactive "Add image or video" button
        // below, which already labels this same empty state. That doubled,
        // ghosted text was reported live, 2026-09-16. Only pass real hint
        // copy when the button isn't there to speak for the slot.
        emptyHint={isEditable ? '' : 'Add media to preview'}
        focalX={!hasVideoMedia && hasExplicitCrop ? activeFocalX : undefined}
        focalY={!hasVideoMedia && hasExplicitCrop ? activeFocalY : undefined}
        zoom={!hasVideoMedia && hasExplicitCrop ? activeZoom : undefined}
        aspectRatio={previewPropAspectRatio}
        soundCredit={
          activePlatform === 'instagram' && perPlatformOverride.instagram?.audio
            ? [perPlatformOverride.instagram.audio.name, perPlatformOverride.instagram.audio.artist]
                .filter(Boolean)
                .join(' · ')
            : undefined
        }
        mediaOverlay={
          <>
            {!hasMedia && isEditable ? (
              <button
                type="button"
                className="absolute inset-0 z-20 flex items-center justify-center bg-transparent"
                aria-label="Add image or video"
                onClick={() => postMediaManagerRef.current?.openMediaPicker('upload')}
              >
                <span className="rounded-full border border-border/60 bg-background/85 px-3 py-1.5 text-detail font-medium text-foreground shadow-sm transition-colors hover:bg-background">
                  Add image or video
                </span>
              </button>
            ) : null}
            {showSafeZoneOverlay ? <SafeZoneOverlay previewKind={effectiveKind} /> : null}
            {/* Rule-of-thirds grid — appears while repositioning OR while
                        the zoom slider is being dragged, like Instagram's crop
                        screen. */}
            {isReframing ? (
              // White lines over the photo (must read on any image) — the
              // justified media-overlay exception, matching SafeZoneOverlay's
              // inline rgba rather than a theme token.
              <div className="pointer-events-none absolute inset-0 z-10">
                <div
                  className="absolute inset-y-0 left-1/3 w-px"
                  style={{ backgroundColor: 'rgba(255,255,255,0.45)' }}
                />
                <div
                  className="absolute inset-y-0 left-2/3 w-px"
                  style={{ backgroundColor: 'rgba(255,255,255,0.45)' }}
                />
                <div
                  className="absolute inset-x-0 top-1/3 h-px"
                  style={{ backgroundColor: 'rgba(255,255,255,0.45)' }}
                />
                <div
                  className="absolute inset-x-0 top-2/3 h-px"
                  style={{ backgroundColor: 'rgba(255,255,255,0.45)' }}
                />
              </div>
            ) : null}
            {/* Discoverability: a subtle hint that the photo can be
                        dragged — shown only when it's actually croppable (so it's
                        never misleading) and not while actively reframing.
                        `cropSignal` used to gate this too, but `cropSignalFor`
                        always returns null once an explicit override exists
                        (see its own doc comment: "the user already chose an
                        override → null"), and `canReframe` REQUIRES
                        `hasExplicitCrop` — so `cropSignal` was structurally
                        always null here. Combined with `isFramed` (only true
                        AFTER a drag), the hint could never show on a fresh
                        explicit crop — exactly the moment a creator most needs
                        to be told the photo is draggable. Verified live,
                        2026-09-16: an explicit 4:5 crop on a wide group photo
                        rendered with zero reposition affordance. */}
            {canReframe && !isReframing ? (
              <div className="pointer-events-none absolute inset-x-0 bottom-2 z-10 flex justify-center">
                <span className="rounded-full bg-background/75 px-2 py-0.5 text-micro font-medium text-foreground/75 backdrop-blur-sm">
                  Drag to reposition
                </span>
              </div>
            ) : null}
          </>
        }
      />
    </div>
  ) : !activePlatform && isEditable ? (
    <PlatformPlugMenu
      platforms={platforms}
      onTogglePlatform={handleTogglePlatform}
      trigger={
        <button
          type="button"
          className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border/60 bg-muted/30 px-6 py-8 text-detail text-muted-foreground hover:border-border hover:text-foreground transition-colors data-[state=open]:border-foreground/40 data-[state=open]:bg-muted/40"
        >
          <span className="text-foreground/80">Pick a platform</span>
          <span className="text-detail text-muted-foreground/80">
            Add at least one platform to preview this post
          </span>
        </button>
      }
    />
  ) : (
    <p className="text-detail text-muted-foreground">
      {activePlatform
        ? `Preview for ${platformLabel(activePlatform)} isn't supported yet.`
        : 'No platform selected'}
    </p>
  );

  // Zoom slider — disclosed by the "Reposition & zoom" toggle in
  // controlsStrip rather than standing chrome (founder review, 2026-07-25:
  // an always-visible slider next to a directly-draggable preview was
  // confusing AND permanently ate vertical space in an already
  // height-constrained pane; framing is occasional, not standing chrome).
  // Sits directly under the photo (not in controlsStrip's horizontal-scroll
  // toolbar row; a slider needs drag room a packed row of chips can't give
  // it). canReframeZoom is already false when there's no real headroom to
  // zoom into (Subtraction — don't show a lever with no throw), so the
  // toggle that reveals this never appears with nothing behind it.
  // Committing on release (not on every tick) keeps this on the identical
  // save/undo/apply-to-all path as drag and keyboard nudge, and avoids
  // spamming the persist queue mid-drag.
  const zoomSliderRow = showsZoomRow ? (
    <div
      className="flex w-full items-center gap-2 px-1 animate-in fade-in-0 slide-in-from-top-1 duration-200"
      style={hasMedia ? { maxWidth: previewMaxWidth } : undefined}
    >
      <MagnifyingGlassIcon
        className="size-3.5 shrink-0 text-muted-foreground"
        weight="regular"
        aria-hidden
      />
      <Slider
        value={[activeZoom]}
        min={1}
        max={maxZoom}
        step={0.01}
        onValueChange={([v]) => setZoomDraft(v)}
        onValueCommit={([v]) => {
          setZoomDraft(null);
          void commitFraming({ zoom: v });
        }}
        aria-label={activePlatform ? `Zoom for ${platformLabel(activePlatform)}` : 'Zoom'}
        className="flex-1"
        // Default track (bg-accent/30) AND bg-muted both measured as
        // near-invisible against this pane's near-white bg-background in
        // this theme (verified live via computed-style + screenshot, not a
        // hunch — both are pale grays too close to the pane's own rgb(252)).
        // bg-foreground/15 is derived from the theme's actual foreground
        // color, so it's guaranteed visible against bg-background in any
        // theme (light or dark) instead of hoping two independent tokens
        // happen to contrast.
        trackClassName="bg-foreground/15"
        rangeClassName="bg-foreground"
      />
    </div>
  ) : null;

  // Bottom inline control strip — per-platform FRAMING controls only (crop /
  // safe zones / ratio). Shared by both layouts; sits under the preview. The
  // platform switcher itself moved to the right rail (see platformTabStrip):
  // as a row of ~20px circles down here it collapsed attached / focused /
  // paused onto one unlabelled glyph, and the measured result was that
  // `platform_captions` was `{}` on all 1,729 prod posts — a fully-built
  // per-platform caption feature nobody could find the door to.
  //
  // Does anything in here actually render? If not, the strip would be a lone
  // platform icon anchoring nothing, so it stays out of the layout entirely.
  // (showsFramingControlsRow is hoisted above previewStageReservedHeight —
  // see the comment there — so this condition can't drift from that reserve.)
  const controlsStrip = showsFramingControlsRow ? (
    // The active-platform anchor icon that used to open this cluster is
    // gone (founder review): the tab strip above already states the active
    // platform in words at eye level, and the preview chrome itself is
    // platform-specific — the icon anchored nothing that wasn't already
    // said twice. Its divider went with it.
    // Desktop: constrained to the SAME maxWidth as the preview card
    // (previewMaxWidth) and centered under it — the card itself is
    // centered in the stage, so a same-width row of controls centers to
    // match; left-aligning it created an asymmetry against a centered
    // card, which is what made this cluster look like leftover chrome
    // pinned to the pane's corner rather than controls FOR the card
    // (founder review). Mobile keeps its prior full-bleed, left-aligned
    // strip unchanged.
    // overflow-x-auto: this cluster has no natural wrap point and would
    // silently bleed past a 375px mobile viewport with no way to reach the
    // rest — it scrolls horizontally instead, same convention as every other
    // pill/chip row in the app (MediaStudioDialog's action row, etc).
    <div
      className={cn(
        'flex items-center gap-2 overflow-x-auto no-scrollbar',
        isMobile ? 'w-full px-4 pb-2.5 pt-0' : 'w-full justify-center px-1',
      )}
      style={!isMobile && hasMedia ? { maxWidth: previewMaxWidth } : undefined}
    >
      {activePlatform && (cropSignal || videoOffRatio) ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={
                cropSignal
                  ? `${platformLabel(activePlatform)} may display about ${cropSignal.percent}% of this photo outside its preferred frame. Activate to choose crop framing.`
                  : `${platformLabel(activePlatform)} may crop or letterbox this video.`
              }
              onClick={
                cropSignal
                  ? handleReframeToFit
                  : () =>
                      toast(
                        `${platformLabel(activePlatform)} prefers 9:16 — the platform may crop or letterbox this video`,
                      )
              }
              className={cn(
                // border/40 in the idle state — Placement/AspectRatio beside
                // this row are outline Buttons (a visible border by default);
                // a bare borderless circle here read as a stray unstyled
                // icon rather than a peer control in the same row (founder
                // review). Active/pressed states already earn a filled
                // background, so the border stays subtle rather than doubled.
                'inline-flex size-9 md:size-7 shrink-0 items-center justify-center rounded-full border border-border/40 transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                cropSignal?.tier === 'major' || videoOffRatio
                  ? 'text-warning hover:bg-warning/10'
                  : 'text-muted-foreground hover:bg-muted/40',
              )}
            >
              <WarningIcon className="size-4" weight="regular" aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">
            {cropSignal
              ? `${platformLabel(activePlatform)} may display ~${cropSignal.percent}% outside its preferred frame — choose crop framing`
              : `${platformLabel(activePlatform)} may crop or letterbox this video`}
          </TooltipContent>
        </Tooltip>
      ) : null}
      {/* Gated on the SAME predicate as the overlay itself. Gating the button on
          `hasChromePreview` while the overlay used a narrower condition left a
          live control that toggled `aria-pressed`, lit its active background,
          and painted nothing. */}
      {safeZonesApply ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-pressed={showSafeZones}
              aria-label={showSafeZones ? 'Hide safe zones' : 'Show safe zones'}
              onClick={() => setShowSafeZones((v) => !v)}
              className={cn(
                // border/40 in the idle state — Placement/AspectRatio beside
                // this row are outline Buttons (a visible border by default);
                // a bare borderless circle here read as a stray unstyled
                // icon rather than a peer control in the same row (founder
                // review). Active/pressed states already earn a filled
                // background, so the border stays subtle rather than doubled.
                'inline-flex size-9 md:size-7 shrink-0 items-center justify-center rounded-full border border-border/40 transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                showSafeZones
                  ? 'bg-foreground/[0.06] text-foreground'
                  : 'text-muted-foreground hover:bg-muted/40',
              )}
            >
              <FrameCornersIcon className="size-4" weight="regular" aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">Safe zones</TooltipContent>
        </Tooltip>
      ) : null}
      {activePlatform && isEditable ? (
        <DestinationAccountPicker
          options={destinationOptionsForActivePlatform}
          value={activeDestinationOverrideId}
          resourceValue={activeDestinationResourceId}
          onChange={handleDestinationAccountChange}
          isSaving={isSavingDestinationAccount}
        />
      ) : null}
      {hasMedia ? (
        <>
          {/* Placement sits LEFT of AspectRatioPicker — it's the parent
                  decision (which surface this post publishes to); ratio
                  options react to it, not the other way around. */}
          <PlacementPicker
            platform={activePlatform}
            mediaKind={mediaKind}
            value={perPlatformOverride[activePlatform]?.placement}
            onChange={commitPlacement}
            isSaving={isSavingActivePlatform}
            publishablePlacements={connectionPlacements}
          />
          {!hasVideoMedia ? (
            <AspectRatioPicker
              platform={activePlatform}
              placement={previewRow?.placement}
              mediaKind={mediaKind}
              // Same resolution the preview and the aspect check use, so the
              // pill can no longer read "Original" over a cropped preview.
              value={perPlatformAspect ?? undefined}
              onChange={handleAspectRatioChange}
              isSaving={isSavingActivePlatform}
            />
          ) : null}
        </>
      ) : null}
      {/* Reposition & zoom toggle — same cluster, same sizing/variant as the
          safe-zone toggle above (size-9 mobile / size-7 desktop, rounded-full,
          aria-pressed) so it reads as one row of controls rather than a
          bolted-on extra. Covers BOTH the drag-to-reposition already live on
          the photo and the zoom slider it reveals — a magnifier glyph would
          have implied zoom only. */}
      {canReframeZoom ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-pressed={zoomPanelOpen}
              aria-label={zoomPanelOpen ? 'Hide reposition & zoom' : 'Reposition & zoom'}
              onClick={() => setZoomPanelOpen((open) => !open)}
              className={cn(
                // border/40 in the idle state — Placement/AspectRatio beside
                // this row are outline Buttons (a visible border by default);
                // a bare borderless circle here read as a stray unstyled
                // icon rather than a peer control in the same row (founder
                // review). Active/pressed states already earn a filled
                // background, so the border stays subtle rather than doubled.
                'inline-flex size-9 md:size-7 shrink-0 items-center justify-center rounded-full border border-border/40 transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                zoomPanelOpen
                  ? 'bg-foreground/[0.06] text-foreground'
                  : 'text-muted-foreground hover:bg-muted/40',
              )}
            >
              <ArrowsOutCardinalIcon className="size-4" weight="regular" aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">Reposition & zoom</TooltipContent>
        </Tooltip>
      ) : null}
      {/* Reframe controls — only the actions that matter once you've
                  moved the photo: a clearly-labelled Reset, and (multi-platform)
                  Apply-to-all. The repositioning itself is a direct drag on the
                  photo (no slider/handle to decode). */}
      {canReframe && isFramed ? (
        <>
          <button
            type="button"
            onClick={handleResetFraming}
            className="inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-1 text-detail font-medium text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ArrowCounterClockwiseIcon className="size-3.5" weight="regular" aria-hidden />
            Reset crop
          </button>
          {isMultiPlatform ? (
            <button
              type="button"
              onClick={handleApplyFramingToAll}
              className="shrink-0 rounded-md px-1.5 py-1 text-detail font-medium text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Apply to all
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  ) : null;

  // Header — Header Law shape (2026-07-03): ← back circle (always visible —
  // Inspector dialogs have no sidebar fallback for dismissal) + centered/
  // truncated title (brand identity, editable) + right-side status meta and
  // an exempt "back to project" CTA when the rail was opened from a project.
  // shrink-0 so it stays pinned at the top of the mobile single-scroll column.
  // Shared verbatim between the mobile and desktop layouts below.
  const headerBar = (
    // border-b is desktop-only: on mobile the Preview/Edit toggle row directly
    // below carries its own border-b, so a header border here stacked TWO faint
    // rules ~56px apart (live review — "too many lines"). Desktop has no toggle
    // row, so the header keeps its border as the frame's top-chrome separator
    // (parity with the image/video/document dialogs, Hard Rule 9).
    <div className="relative flex h-12 shrink-0 items-center gap-2 px-4 md:border-b md:border-border/40">
      <div className="z-10 flex shrink-0 items-center">
        <CircleIconButton
          size="header"
          ariaLabel="Back"
          className="header-nav-btn rounded-full"
          onClick={onClose}
        >
          <ArrowLeftIcon weight="regular" />
        </CircleIconButton>
      </div>

      <div className="page-title-centered pointer-events-none min-w-0 md:flex md:flex-1 md:justify-start">
        <div className="pointer-events-auto min-w-0">
          {breadcrumbs?.length ? (
            <InspectorBreadcrumb breadcrumbs={breadcrumbs} fallbackTitle={headerTitle} />
          ) : titleDraft != null ? (
            <input
              ref={titleInputRef}
              type="text"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={() => void handleTitleSave(titleDraft)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleTitleSave(titleDraft);
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  setTitleDraft(null);
                }
              }}
              placeholder={headerTitle}
              aria-label="Edit post title"
              autoFocus
              className="min-w-0 truncate rounded-md border border-foreground/20 bg-background px-1.5 py-0.5 text-sm font-medium text-foreground/90 outline-none focus-visible:border-foreground/40"
            />
          ) : (
            <h2
              className={cn(
                'group/title min-w-0 cursor-text rounded-md px-1.5 py-0.5 -mx-1.5 -my-0.5 text-sm font-medium text-foreground/90 hover:bg-muted/40 transition-colors',
                // Overflow fades out on the right (no "…" ellipsis) — the canonical
                // Mora long-text-overflow treatment (see DESIGN-PATTERNS §Text overflow).
                TEXT_FADE_RIGHT,
              )}
              onClick={() => {
                if (!isEditable) return;
                setTitleDraft(explicitTitle ?? '');
                queueMicrotask(() => titleInputRef.current?.focus());
              }}
              onKeyDown={(e) => {
                if (!isEditable || (e.key !== 'Enter' && e.key !== ' ')) return;
                e.preventDefault();
                setTitleDraft(explicitTitle ?? '');
                queueMicrotask(() => titleInputRef.current?.focus());
              }}
              role={isEditable ? 'button' : undefined}
              tabIndex={isEditable ? 0 : undefined}
              title={isEditable ? 'Click to edit title' : undefined}
            >
              {headerTitle}
            </h2>
          )}
        </div>
      </div>

      <div className="z-10 ml-auto flex shrink-0 items-center gap-2">
        {/* Labeled CTA — exempt from the ≤2 icon count (Header Law), same as
            Products' "Create Content" / Calendar's "+Post". */}
        {backToProject ? (
          <button
            type="button"
            onClick={backToProject.onReturn}
            className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-detail font-medium text-muted-foreground hover:bg-muted/40 hover:text-foreground"
            aria-label={`Back to ${backToProject.name}`}
          >
            <ArrowLeftIcon className="size-3" weight="regular" />
            {backToProject.name}
          </button>
        ) : null}
        {/* TEAM-1: "Shared · {teammate}" — only when a team member is viewing
            a post a different teammate authored (see useTeamAuthorNames).
            Hidden below sm: the header row is already tight (back circle +
            title + status, sometimes + backToProject), and this is
            supplementary context, not a primary status. */}
        {postAuthorLabel ? (
          <SharedByChip authorName={postAuthorLabel} className="hidden sm:inline-flex" />
        ) : null}
        {/* Status only — no date. "Draft · planned Jul 6, 12:00 AM" overran the
            mobile title row (live review). The planned/scheduled date+time has
            an honest home in the Edit rail's "Planned for" / "Scheduled for"
            row (its editable control), so the header stays identity + status.
            Doubles as the autosave indicator: while any caption/media/
            platform/title write is in flight (isAutosaving — one shared
            counter, so overlapping writes don't flicker it off early) this
            swaps to "Saving" + a pulsing ellipsis, then reverts to the normal
            status dot + label the moment the last one settles. The pill
            container (not bare text) is what the status previously lacked —
            unwrapped text this far from anything else in the header read as
            orphaned chrome rather than a deliberate status object. */}
        <span
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-muted/40 px-2 py-1 text-detail text-muted-foreground/80 font-normal"
          aria-live="polite"
        >
          {isAutosaving ? (
            <>
              <span>Saving</span>
              <span className="inline-flex items-center gap-0.5" aria-hidden>
                <span
                  className="size-1 rounded-full bg-current animate-pulse"
                  style={{ animationDelay: '0ms' }}
                />
                <span
                  className="size-1 rounded-full bg-current animate-pulse"
                  style={{ animationDelay: '200ms' }}
                />
                <span
                  className="size-1 rounded-full bg-current animate-pulse"
                  style={{ animationDelay: '400ms' }}
                />
              </span>
            </>
          ) : (
            <>
              <span
                className={cn(
                  'size-1.5 shrink-0 rounded-full',
                  STATUS_DOT_COLORS[status] || 'bg-muted-foreground/40',
                )}
                aria-hidden
              />
              <span>{STATUS_LABELS[status] || status}</span>
            </>
          )}
        </span>
      </div>
    </div>
  );

  // ── Platform tab strip ────────────────────────────────────────────────────
  // THE platform control. It used to be a row of ~20px circular icon buttons
  // in the preview pane's bottom-left, which collapsed three orthogonal states
  // (attached / focused / paused) onto one unlabelled glyph. The measured
  // result: `platform_captions` was `{}` on ALL 1,729 prod posts, including
  // 164 multi-platform ones on the team's own accounts — a fully-built
  // per-platform caption feature with 0% adoption, because nothing said these
  // were tabs or that each one held its own caption. A real tab strip, in
  // words, sitting directly above the caption it governs, is the fix.
  //
  // Every ATTACHED platform gets a tab, paused ones included — a paused
  // platform is still part of this post and still has a caption to read.
  // `sort` is stable, so paused tabs fall to the end while the send set keeps
  // its attached order. Preview-only platforms (never attached) ride along
  // at the end, after every real tab — they're a "peek", not part of the
  // post, and shouldn't compete with real tabs for the front of the strip.
  const orderedPlatformTabs = [...platforms, ...previewPlatforms].sort((a, b) => {
    const aIsPreview = !platforms.includes(a);
    const bIsPreview = !platforms.includes(b);
    if (aIsPreview !== bIsPreview) return Number(aIsPreview) - Number(bIsPreview);
    return Number(disabledPlatforms.includes(a)) - Number(disabledPlatforms.includes(b));
  });
  // Labels through N=4, icon-only from N=5. N≤4 covers the entire launch ICP
  // (Sarah IG+Pinterest, Mara LinkedIn, Julien IG+TikTok+Shorts), so the
  // common case must read as words; the icon-only fallback exists so an N=8
  // franchise row degrades instead of overflowing, and its tooltip carries
  // the name.
  const platformTabsAreIconOnly = orderedPlatformTabs.length >= 5;
  // A tablist with one tab is not a tab — there is nothing to switch to, so
  // full tab clothing (underline, selected state, role="tab") dresses up a
  // choice that doesn't exist and reads heavy sitting alone. At N=1 the strip
  // becomes a plain header; the control appears when it has a job.
  const platformStripIsTablist = orderedPlatformTabs.length >= 2;
  const platformHasRawOverride = (platformId: string): boolean => {
    const raw = platformCaptions[platformId];
    return typeof raw === 'string' && raw.trim().length > 0;
  };
  const platformTabOptions = orderedPlatformTabs.map((platformId) => {
    const readiness = platformReadinessMap[platformId];
    const deliveryStatus = effectivePlatformStatus(
      platformId,
      platformSchedules,
      status,
      platformEvidence,
    );
    const ownTime = platformSchedules[platformId]?.scheduled_at ?? null;
    const isPaused = disabledPlatforms.includes(platformId);
    const isPreviewTab = !platforms.includes(platformId);
    // Full state in words. The badge is one glyph and the dim is one opacity —
    // neither can say "custom caption, ready, sends at 11:30". This can.
    const overrideWord = platformHasRawOverride(platformId) ? 'custom caption' : null;
    const readinessWord = isPreviewTab
      ? 'preview only — not posting'
      : readiness
        ? READINESS_META[readiness.state].label.toLowerCase()
        : null;
    const timeWord = ownTime ? `sends ${format(new Date(ownTime), 'MMM d, h:mm a')}` : null;
    const tooltip = [platformLabel(platformId), overrideWord, readinessWord, timeWord]
      .filter((part): part is string => part != null)
      .join(' · ');
    // Silence means ready for a screen reader too, so the spoken name names
    // the state only when it ISN'T the healthy default — otherwise every tab
    // in a healthy post would announce three extra words to say nothing.
    const spokenState = [
      overrideWord,
      isPreviewTab || (readiness && readiness.state !== 'ready') ? readinessWord : null,
      timeWord,
    ].filter((part): part is string => part != null);
    return {
      value: platformId,
      icon: getPlatformBrandIcon(platformId),
      label: platformTabsAreIconOnly ? undefined : platformLabel(platformId),
      // Paused is a whole-tab treatment, never a badge — it describes the tab
      // rather than reporting an event on it. `dimmed` is not `disabled`: a
      // paused platform's caption is still readable.
      dimmed: isPaused,
      // A dim reaches sighted users and nobody else, and a Radix tooltip is
      // only associated while open — so the state a screen reader would
      // otherwise miss goes in the accessible name. Composed from the same
      // READINESS_META lookup the tooltip uses, i.e. read through
      // `platformReadinessMap`, so it can't drift from the badge beside it.
      ariaLabel:
        spokenState.length > 0
          ? [platformLabel(platformId), ...spokenState].join(' · ')
          : undefined,
      // The badge exists to report on tabs you are NOT looking at, so it is
      // gated on there BEING other tabs. At N=1 the issue line below already
      // states the problem in a sentence, and a dot beside it would be the
      // same problem said twice — the double-encoding the whole design avoids.
      //
      // Also gated on `isPaused`, which makes "paused is never a badge"
      // structural rather than incidental: readiness short-circuits to
      // `paused` → no badge, but `effectivePlatformStatus` is computed
      // independently and falls back to the ROW status when
      // `platform_response` is null, so a paused platform on a published row
      // (legacy rows, or any path that doesn't persist per-platform evidence)
      // earned a green ✓ for a send it was explicitly excluded from.
      badge:
        readiness && !isPaused && platformStripIsTablist
          ? platformTabBadge(resolvePlatformTabBadgeKind(readiness.state, deliveryStatus))
          : null,
      tooltip,
    };
  });
  // Note there is deliberately NO divergence marker here. "Has a custom
  // caption" is not a glanceable emergency, and the "Custom · reset"
  // affordance beside the caption already says it — in words, at the exact
  // moment it's relevant.
  const SoloPlatformBrandIcon = getPlatformBrandIcon(activePlatform);
  const platformTabStrip = activePlatform ? (
    <div className="flex items-center gap-2 border-b border-border/40">
      {platformStripIsTablist ? (
        <SegmentedToggle
          value={activePlatform}
          onValueChange={handlePlatformTabChange}
          options={platformTabOptions}
          variant="tabs"
          // Real WAI-ARIA tablist semantics: role="tab"/aria-selected, roving
          // tabindex, Left/Right/Home/End. Up/Down stay unclaimed because the
          // reframe stage owns arrow-nudging the image — the two never collide
          // because they live in different subtrees and this only fires from a
          // focused tab.
          semantics="tabs"
          size="sm"
          className="min-w-0 flex-1 gap-4"
        />
      ) : (
        // N=1: a heading, not a control. Spelled out rather than abbreviated —
        // "IG" is jargon and reads worse than the word it saves four
        // characters on.
        <h3 className="flex min-w-0 flex-1 items-center gap-2 py-2 text-sm font-medium text-foreground">
          <SoloPlatformBrandIcon className="size-3 shrink-0" />
          <span className="truncate">{platformLabel(activePlatform)}</span>
        </h3>
      )}
      {/* `isInFlightPartial` (status `partially_scheduled`) is editable here by
          design — the caption editor stays open for the platforms still
          pending — and this menu is the ONLY way to detach or pause one of
          them, so gating on `isEditable` alone would strand a half-sent post
          with a caption it can still edit and a platform set it can't. */}
      {isEditable || isInFlightPartial ? (
        // The trailing [+] is NOT a tab, so it sits outside the tablist —
        // a non-tab child of role="tablist" is invalid ARIA. Native title,
        // because DropdownMenuTrigger asChild can't wrap a Radix Tooltip.
        <PlatformPlugMenu
          platforms={platforms}
          onTogglePlatform={handleTogglePlatform}
          disabledPlatforms={disabledPlatforms}
          onToggleEnabled={handleTogglePlatformEnabled}
          previewPlatforms={previewPlatforms}
          onTogglePreview={handleTogglePreviewPlatform}
          trigger={
            <button
              type="button"
              aria-label="Add or remove platforms"
              title="Add or remove platforms"
              className="inline-flex size-7 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <PlusIcon className="size-3.5" weight="bold" />
            </button>
          }
        />
      ) : null}
    </div>
  ) : null;

  // Per-platform TIMING affordance — a quiet "Post at a different time" under
  // the active platform's editor (Phase 3, Rev 2). The schedule popover stays
  // ONE shared picker; this is where a single platform gets its own send time.
  // Shown for a scheduled OR planned-draft (draft + calendar scheduled_at)
  // multi-platform post whose active platform is enabled + still pending (a
  // locked/live platform can't be rescheduled). Task 15 audit: the gate used
  // to require status === 'scheduled' only, which made the control invisible
  // on a planned draft even though setPlatformScheduleTime never checks the
  // row's DB status — a real, silently-buried capability (spec §8.1).
  const activePlatformEnabled = activePlatform
    ? enabledSendPlatforms.includes(activePlatform)
    : false;
  const showPlatformTiming =
    !!activePlatform &&
    activePlatformEnabled &&
    !activePlatformLocked &&
    isMultiPlatform &&
    (status === 'scheduled' || isPlannedDraft) &&
    !!rowScheduledAt;
  const perPlatformTiming = showPlatformTiming ? (
    activeHasOwnTime && activePlatformTime ? (
      <div className="flex items-center gap-2">
        <StatusBadge tone="info" icon={<ClockIcon className="size-3" weight="regular" />}>
          {platformLabel(activePlatform!)} posts{' '}
          {format(new Date(activePlatformTime), 'MMM d, h:mm a')}
        </StatusBadge>
        <button
          type="button"
          aria-label={`Clear ${platformLabel(activePlatform!)}'s separate time`}
          onClick={() => handleClearPlatformTime(activePlatform!)}
          disabled={clearPlatformTimeMutation.isPending}
          className="inline-flex size-5 shrink-0 items-center justify-center rounded-full text-muted-foreground/70 transition-colors hover:bg-muted/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
        >
          <XIcon className="size-3" weight="bold" />
        </button>
      </div>
    ) : (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 -mx-1.5 text-detail font-medium text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ClockIcon className="size-3.5" weight="regular" />
            {/* Names the platform explicitly — this sets a per-platform
                override, a different control from the shared "Planned for"
                date below (spec: schedule popover stays ONE shared picker).
                Both being bare "pick a date" buttons with no scope word read
                as duplicate entry points for the same thing (founder review);
                naming the platform is the one-word fix that makes the two
                controls' different jobs legible without merging them. */}
            Post {platformLabel(activePlatform!)} at a different time
          </button>
        </PopoverTrigger>
        {/* Same picker as every other scheduling surface. This used to mount
            `<DatePicker>` — which is a *trigger button plus its own Popover* —
            inside this Popover, so the panel opened showing a lone "July 14th,
            2026" button and the real grid only appeared in a second popover
            stacked on top of the first. */}
        <PopoverContent
          align="start"
          className="w-auto max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-0"
        >
          {/* Every schedule popover opens with the same two-line header: what
              this picker controls, and what it does NOT. Three unlabelled
              calendars in one panel was the whole reason it read as "which one
              is which". */}
          <div className="border-b border-border/40 px-3 py-2.5">
            <p className="text-caption font-medium text-foreground">
              {platformLabel(activePlatform!)} only
            </p>
            <p className="text-detail leading-snug text-muted-foreground">
              Every other platform keeps the post&apos;s shared time.
            </p>
          </div>
          <SchedulePicker
            value={activePlatformTime ? new Date(activePlatformTime) : undefined}
            pendingTime={platformOverrideTime}
            onPendingTimeChange={setPlatformOverrideTime}
            onDateChange={(newDate) => {
              if (!activePlatform) return;
              handleSetPlatformTime(activePlatform, newDate.toISOString());
            }}
            onTimeChange={(newDate) => {
              if (!activePlatform) return;
              handleSetPlatformTime(activePlatform, newDate.toISOString());
            }}
            minDate={new Date()}
            markedDates={scheduledDates}
            timeLabel="Send time"
          />
        </PopoverContent>
      </Popover>
    )
  ) : null;

  // AI chips (3 verbs + Tone + fan-out) — fused into the composer's own
  // `quickActions` slot (HANDOFF-EDITOR-UNIFICATION-2026-08-12 §8.9/§8.14
  // P2). These used to sit attached to the caption box's own toolbar, with
  // the freeform prompt below, separated by the readiness sentence and the
  // quality pill — two controls answering one job ("rewrite this caption"),
  // in two places. Copying the arrangement the standalone image editor
  // already ships (MediaStudioDialog.tsx's `quickActions` usage): chips
  // render directly on top of the textarea, inside the same rounded
  // container, as one control. Chips stay the fast path; the textarea
  // beneath is the escape hatch for anything the fixed verbs don't cover.
  const captionAiQuickActions = isEditable ? (
    <div aria-live="polite" className="flex flex-wrap items-center gap-x-1 gap-y-1">
      {operatingAction ? (
        <Spinner size="sm" className="shrink-0" aria-label="Rewriting caption" />
      ) : (
        <SparkleIcon
          className="size-3 shrink-0 text-skill/70"
          weight="duotone"
          aria-hidden="true"
        />
      )}
      {captionQuickActions
        .filter((action) => action.kind !== 'fan-out')
        .map((action) =>
          action.kind === 'tone-menu' ? (
            // The trigger keeps `action.label` until a tone is actually
            // CHOSEN — a post inheriting the brand voice has not had a tone
            // picked on it, and naming one would be a small lie the user
            // cannot undo.
            <ToneControl
              key={action.actionId}
              brandTone={brandTone}
              value={toneOverride}
              onSelect={(toneId, instruction) => {
                void handleToneSelect(toneId, instruction);
              }}
              onClearOverride={() => {
                void handleToneClear();
              }}
              disabled={captionChipIsDisabled(operatingAction)}
              pendingToneId={
                operatingAction?.startsWith('tone:') ? operatingAction.slice('tone:'.length) : null
              }
              open={toneOpen}
              onOpenChange={setToneOpen}
              triggerLabel={toneLabelFor(toneOverride) ?? action.label}
            />
          ) : (
            <button
              key={action.actionId}
              type="button"
              disabled={captionChipIsDisabled(operatingAction)}
              onClick={() => void runCaptionEdit(action.instruction, action.actionId)}
              className={cn(QUIET_ACTION, 'h-7 px-1 text-detail font-medium')}
            >
              {captionChipIsSpinning(action.actionId, operatingAction) ? (
                <Spinner size="sm" className="shrink-0" aria-hidden="true" />
              ) : null}
              {action.label}
            </button>
          ),
        )}
      <button
        type="button"
        disabled={captionChipIsDisabled(operatingAction)}
        onClick={() => setDescribeOpen(true)}
        className={cn(QUIET_ACTION, 'h-7 px-1 text-detail font-normal text-muted-foreground')}
      >
        Describe…
      </button>
      {activeIsCustomized && activePlatform ? (
        <button
          type="button"
          onClick={() => void handleResetPlatformCaption()}
          className={cn(
            QUIET_ACTION,
            'ml-auto h-7 px-1 text-detail font-normal text-muted-foreground/70',
          )}
          title="Reset to the shared caption"
        >
          Custom · reset
        </button>
      ) : null}
    </div>
  ) : null;

  const fanOutAction = captionQuickActions.find((action) => action.kind === 'fan-out');
  const sharingPlatforms = enabledSendPlatforms.filter(
    (platformId) =>
      platformId !== activePlatform &&
      !hasPlatformOverride(post.content, platformCaptions, platformId),
  );
  // Mora's help lives inside the caption, on the line with the counter (founder, 3 Nov 2026: the separate box
  // under the caption was "garbage"). It appears once there is text to work on; Describe turns the same line into
  // a text box. The fan-out is said as what it is: which channels share this caption.
  const captionHasText = Boolean(activeCaption?.trim());
  // Draft it for me (founder, 3 Nov 2026): on an empty caption, Mora writes one from the post's own image (a
  // video's cover frame) in the post's brand voice, for the open channel, through the same vision endpoint Media
  // Studio uses, then saves it like a typed caption.
  const handleDraftCaption = async () => {
    const assetUrl = firstVisualMediaUrl(post.media_urls);
    const token = session?.access_token;
    if (!assetUrl || !token || draftingCaption) return;
    const platform = activePlatform ?? platforms[0] ?? 'instagram';
    setDraftingCaption(true);
    try {
      const captions = await suggestCaptionsBatch({
        assetUrl,
        coverImageUrl: (post as { cover_image_url?: string | null }).cover_image_url ?? null,
        kind: isVideoPost ? 'video' : 'image',
        platforms: [platform],
        brandKitId: post.brand_kit_id ?? null,
        accessToken: token,
      });
      const text = captions[platform]?.trim();
      if (!text) {
        toast.error(
          isVideoPost
            ? 'Mora needs a cover image to write from this video.'
            : 'Mora could not write a caption from this image.',
        );
        return;
      }
      setCaptionDraft(text);
      await handleActiveCaptionSave(text);
    } catch {
      toast.error('Mora could not write a caption. Try again.');
    } finally {
      setDraftingCaption(false);
    }
  };
  const draftOffer =
    isEditable && activeCaptionEditable && !captionHasText && hasMedia ? (
      <button
        type="button"
        disabled={draftingCaption || isOperating}
        onClick={() => void handleDraftCaption()}
        className="inline-flex h-7 items-center gap-1.5 rounded-full bg-skill/[0.07] px-2.5 text-detail font-medium text-skill-text transition-colors hover:bg-skill/15 disabled:opacity-60"
      >
        {draftingCaption ? (
          <Spinner size="sm" className="shrink-0" aria-hidden="true" />
        ) : (
          <SparkleIcon className="size-3 shrink-0" weight="duotone" aria-hidden="true" />
        )}
        {draftingCaption ? 'Writing a caption' : 'Draft it for me'}
      </button>
    ) : null;
  const captionFooter =
    isEditable && activeCaptionEditable && !captionHasText ? (
      draftOffer
    ) : isEditable && activeCaptionEditable && captionHasText ? (
      describeOpen ? (
        <form
          className="flex items-center gap-1.5"
          onSubmit={(event) => {
            event.preventDefault();
            const text = composerDraft.trim();
            if (!text) return;
            handleCustomPromptSubmit(text);
            setComposerDraft('');
            setDescribeOpen(false);
          }}
        >
          <SparkleIcon
            className="size-3 shrink-0 text-skill/70"
            weight="duotone"
            aria-hidden="true"
          />
          <input
            autoFocus
            aria-label="Describe a change to this caption"
            placeholder="Describe a change to this caption"
            value={composerDraft}
            onChange={(event) => setComposerDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                setDescribeOpen(false);
              }
            }}
            disabled={isOperating}
            className="h-7 min-w-0 flex-1 bg-transparent text-detail text-foreground outline-none placeholder:text-muted-foreground/60"
          />
          <button
            type="button"
            onClick={() => setDescribeOpen(false)}
            className={cn(QUIET_ACTION, 'h-7 px-1 text-detail font-normal text-muted-foreground')}
          >
            Cancel
          </button>
          <Button
            type="submit"
            size="sm"
            variant="flat"
            className="h-7 bg-skill/10 px-2.5 text-skill hover:bg-skill/20"
            disabled={isOperating || !composerDraft.trim()}
          >
            Rewrite
          </Button>
        </form>
      ) : (
        captionAiQuickActions
      )
    ) : null;
  const fanOutLine =
    isEditable && captionHasText && fanOutAction && sharingPlatforms.length > 0 ? (
      <p className="flex flex-wrap items-center gap-x-1.5 text-detail text-muted-foreground">
        <span>
          {joinPlatformNames(sharingPlatforms.map(platformLabel))}{' '}
          {sharingPlatforms.length === 1 ? 'uses' : 'use'} this caption too.
        </span>
        <button
          type="button"
          disabled={captionChipIsDisabled(operatingAction)}
          onClick={() => void handleFanOutRewrite()}
          className="inline-flex items-center gap-1 text-foreground/90 underline decoration-border underline-offset-2 hover:text-foreground disabled:opacity-50"
        >
          {captionChipIsSpinning(fanOutAction.actionId, operatingAction) ? (
            <Spinner size="sm" className="shrink-0" aria-hidden="true" />
          ) : null}
          Write one for each
        </button>
      </p>
    ) : null;

  // Composer — chips + freeform caption-edit input, fused (spec §5.1 +
  // HANDOFF-EDITOR-UNIFICATION-2026-08-12 §8.14 P2). The placeholder itself
  // ("Describe a change to this caption…", scoped to kind="post" in
  // MediaStudioCustomPromptInput) carries the scope now — a separate label
  // above duplicated it and read as unexplained text sitting on the
  // composer. NO border-t (its own rounded card border is the boundary).
  //
  // It flows with the rail rather than being pinned to the bottom of it.
  // Pinned, it sat ~400px below the textarea with ~280px of void between, so
  // the panel read unfinished AND it was genuinely unclear which of the two
  // caption surfaces actually writes the caption. It is ABOUT the caption, so
  // it belongs against it. No horizontal padding here — the rail and mobile
  // containers already supply px-4.
  const composerBlock =
    isEditable && activePlatform === 'linkedin' ? (
      <LinkedInTagsField
        postId={post.id}
        caption={captionDraft}
        value={linkedInMentions}
        onChange={setLinkedInMentions}
      />
    ) : isEditable && activePlatform === 'tiktok' && hasMedia && !isPreviewOnlyActiveTab ? (
      <TikTokSettingsCard
        mediaKind={isVideoPost ? 'video' : 'photo'}
        value={readTikTokSettings(perPlatformOverride)}
        onChange={(next) => void commitTikTokSettings(next)}
        creatorInfo={tiktokCreator.info}
        isRefreshing={tiktokCreator.isRefreshing}
        infoState={tiktokCreator.state}
        photoCount={post.media_urls?.length ?? 1}
        disabled={activePlatformLocked}
      />
    ) : isEditable && (activePlatform === 'instagram' || activePlatform === 'facebook') ? (
      <MetaPublishOptions
        platform={activePlatform}
        entry={perPlatformOverride[activePlatform]}
        placement={previewRow?.placement}
        mediaKind={mediaKind}
        hasLink={/https?:\/\/\S+/i.test(captionDraft)}
        disabled={isSavingActivePlatform}
        onChange={(next) => void commitPublishOptions(next)}
      />
    ) : null;

  // Readiness, said once, directly above the publish button (founder, 3 Nov 2026): the first problem as a sentence
  // and the per-channel checks chip sit together in the footer instead of between the caption and the media.
  // When the open channel cannot post as it stands (a Reel outside Instagram's length, or no connection) and
  // another channel can, the readiness line offers the + menu's own switch in place (founder, 3 Nov 2026). It
  // pauses the channel for this post only; its caption and crop stay, and the + menu turns it back on.
  const skipCode = activeReadinessIssues.find((issue) =>
    [
      'instagram_reel_too_short',
      'instagram_reel_too_long',
      'instagram_story_too_short',
      'instagram_story_too_long',
      'platform_not_connected',
    ].includes(issue.code),
  )?.code;
  const skipOffer =
    isEditable &&
    activePlatform &&
    skipCode &&
    !disabledPlatforms.includes(activePlatform) &&
    enabledSendPlatforms.filter((platformId) => platformId !== activePlatform).length > 0 ? (
      <span className="flex basis-full flex-wrap items-center gap-x-2 gap-y-1 text-detail text-muted-foreground">
        <button
          type="button"
          onClick={() => void handleTogglePlatformEnabled(activePlatform)}
          className="rounded-full border border-border/60 px-2.5 py-0.5 font-medium text-foreground hover:bg-muted/50"
        >
          Post without {platformLabel(activePlatform)}
        </button>
        <span>
          {skipCode === 'platform_not_connected'
            ? `or connect ${platformLabel(activePlatform)}. `
            : 'or replace the video. '}
          You can switch it back on in the + menu.
        </span>
      </span>
    ) : null;
  const readinessBlock =
    railLines.length > 0 || (reconciledQuality && activePlatform) ? (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {railLines.length > 0 ? (
          <div className="space-y-0.5">
            <p
              aria-live="polite"
              className={cn(
                'text-detail',
                railLines[0].blocked ? DESTRUCTIVE_ON_SURFACE : 'text-warning',
              )}
            >
              {railLines[0].message}
              {railLines.length > 1 && !readinessIssuesExpanded ? (
                <>
                  {' '}
                  <button
                    type="button"
                    onClick={() => setReadinessIssuesExpanded(true)}
                    className="underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    (+{railLines.length - 1} more)
                  </button>
                </>
              ) : null}
            </p>
            {readinessIssuesExpanded
              ? railLines.slice(1).map((line) => (
                  <p
                    key={line.key}
                    className={cn(
                      'text-detail',
                      line.blocked ? DESTRUCTIVE_ON_SURFACE : 'text-warning',
                    )}
                  >
                    {line.message}
                  </p>
                ))
              : null}
          </div>
        ) : null}

        {/* "N checks passed" — the only place the user can SEE that anything was
          verified. It sits here, post-level in the rail beneath the caption
          and the readiness sentence, and deliberately NOT in the media control
          row beside Placement / Aspect ratio: those are per-asset controls, and
          a verdict about the caption, the link and the fold reads as a stray
          toolbar button among them. The pair is complementary and neither
          replaces the other — the sentence above answers "what do I fix
          first", this answers "what was checked at all", which is the whole
          point (a silent pass is indistinguishable from no check having run).
          Rendered OUTSIDE the media guard on purpose: a text-only post still
          gets caption, link and fold checks, and hiding the chip on those
          would silently kill it on every caption-only post. Gated on
          `captionEditorVisible` — the same condition that decides whether the
          caption is still editable — because every fix in the popover WRITES,
          and offering "1 to fix" on a post that already went out is an action
          the row will refuse. */}
        {reconciledQuality && activePlatform ? (
          <div className="ml-auto flex">
            <QualityChip
              verdict={reconciledQuality}
              storedVerdict={
                // `quality` is a `Json` column, so the cast has to go through
                // `unknown`; the optional chain is what actually keeps a legacy
                // or malformed bag from throwing, not the type.
                (post.quality as unknown as QualityBag | null)?.byPlatform?.[activePlatform] ?? null
              }
              onApplyFix={(fix) => {
                void handleApplyQualityFix(fix);
              }}
              isApplyingFix={isSavingActivePlatform}
            />
          </div>
        ) : null}
        {skipOffer}
      </div>
    ) : null;

  // Editing sections — caption editor, media manager, schedule, metrics. The
  // scroll container is supplied by each layout (desktop rail vs mobile shell).
  // On a tab that is not the affected platform's, the footer is about that tab. These sit beside the status lines, in
  // the rail and on the phone's preview, so the author can always reach them.
  const outcomeActions =
    unresolvedTargets.length > 0 ? (
      <>
        {isLiveOrPartial && !activeUnresolved ? (
          <RepostAfterCheckAction
            targets={unresolvedTargets}
            isSending={isPublishing}
            onConfirm={handleRepostAfterCheck}
          />
        ) : null}
        {sendablePlatforms.length > 0 ? (
          <UnsentPlatformsAction
            platformLabels={sendablePlatforms.map(platformLabel)}
            sentLabels={[]}
            isSending={isPublishing}
            onConfirm={handleSendOwed}
          />
        ) : null}
      </>
    ) : null;

  const railSections = (
    <>
      {/* Preview-only tab banner (handleTogglePreviewPlatform) — the caption
          below is read-only on purpose (activeCaptionEditable), so this is
          the one place that says why, and the only route back to a real
          connection. */}
      {isPreviewOnlyActiveTab && activePlatform ? (
        <div className="flex items-center gap-2.5 rounded-lg border border-dashed border-border/60 bg-muted/20 px-3 py-2">
          <PlugsConnectedIcon className="size-4 shrink-0 text-muted-foreground" weight="regular" />
          <p className="min-w-0 flex-1 text-detail text-muted-foreground">
            Previewing how this post would look on {platformLabel(activePlatform)}. Nothing here is
            saved or sent until you connect it.
          </p>
          <Button
            size="sm"
            variant="neutral"
            className="h-7 shrink-0"
            onClick={() => {
              window.dispatchEvent(
                new CustomEvent('mora:openSettings', {
                  detail: { section: 'integrations', highlight: activePlatform },
                }),
              );
            }}
          >
            Connect
          </Button>
        </div>
      ) : null}

      {/* Caption editor — primary content. Always renders. */}
      <div className="space-y-1.5">
        {/* No "CAPTION" heading: a big textarea with a character counter,
            directly under the platform header, is self-evidently the caption —
            the word did no work. What remains is the one thing neither the
            textarea nor the strip can say, whether this platform's text is its
            own or the shared master. Kept for a SINGLE remaining platform that
            still carries a real override (P0-5): detaching down to one
            platform must never hide an override the user can no longer reach
            any other way. */}
        {/* "Custom · reset" rides in the composer's `quickActions` row
            (captionAiQuickActions, below) rather than in a reserved-height
            row of its own. A dedicated row was `h-4` even when empty — which
            it is for every post that has no per-platform override — so the
            default case paid a permanent unexplained gap between the
            platform tabs and the caption card just to stop the rail jumping
            when the flag DID appear. Hosting it inside a row that already
            exists in both states removes the gap and the jump. */}
        {captionEditorVisible ? (
          <div className="relative">
            {/* `key` forces a fresh draft when the active platform tab
                    changes so the editor never shows a stale platform's text.
                    `platforms` is narrowed to the active one so the character
                    counter reflects THAT platform's limit, not the tightest.
                    `editable` false = the active platform is live (half-sent):
                    the caption goes read-only ("view what went live"). */}
            <CaptionDisplay
              key={editingShared ? 'shared' : activePlatform}
              caption={activeCaption}
              onChange={setCaptionDraft}
              onCommit={(nextCaption) => {
                void handleActiveCaptionSave(nextCaption);
              }}
              platforms={isMultiPlatform && activePlatform ? [activePlatform] : platforms}
              activePlatform={isMultiPlatform && activePlatform ? activePlatform : undefined}
              editable={activeCaptionEditable}
              footer={captionFooter}
              // No `toolbar` here — the AI verbs + Tone used to live in this
              // box's own toolbar slot. They now render as `captionAiQuickActions`
              // inside the composer below, fused with the freeform prompt into
              // one control (see the comment at that definition).
              accessToken={session?.access_token}
            />
            {captionSavedAt != null && activeCaptionEditable ? (
              <span
                aria-live="polite"
                className="pointer-events-none absolute bottom-2 left-3 inline-flex items-center gap-1 text-detail text-success"
              >
                <CheckIcon className="size-3" weight="bold" />
                Saved
              </span>
            ) : null}
          </div>
        ) : !isPublished && post.content ? (
          // Fully published, not partial: the left preview already renders
          // this exact caption inside the styled post mockup. Repeating it
          // here as plain text just duplicated it a few inches to the right —
          // once a post is fully live, the rail's job is showing what
          // happened (analytics), not what was said.
          <p className="text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap break-words">
            {post.content}
          </p>
        ) : null}
        {/* Scheduled-only Original⇄Revised compare (spec §5.3, Task 4).
              Drafts skip this — they get instant-apply + Undo toast (runCaptionEdit
              'apply' path). For scheduled posts the rewrite is staged first; this
              panel offers explicit Keep / Revert before the user moves on.
              Stacked layout (never side-by-side) so it holds on mobile too.
              Pending revisions clear when the active caption context changes
              (see the activeCaptionKey effect above). */}
        {isEditable && captionRevision ? (
          <div
            className="space-y-2 rounded-lg border border-dashed border-skill/30 bg-skill/[0.04] p-3"
            aria-label="Compare original and revised caption"
          >
            <p className="flex items-center gap-1.5 text-detail font-medium text-skill/80">
              <SparkleIcon className="size-3.5 shrink-0" weight="duotone" />
              Revised — keep or revert
            </p>
            <div className="space-y-2">
              <div>
                <p className="text-micro font-medium uppercase tracking-wide text-muted-foreground/70">
                  Original
                </p>
                <p className="line-clamp-3 whitespace-pre-wrap text-detail text-muted-foreground">
                  {captionRevision.original}
                </p>
              </div>
              <div>
                <p className="text-micro font-medium uppercase tracking-wide text-skill/70">
                  Revised
                </p>
                <p className="line-clamp-3 whitespace-pre-wrap text-detail text-foreground">
                  {captionRevision.revised}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="flat"
                className="bg-skill/10 text-skill hover:bg-skill/20"
                onClick={() => void handleKeepRevision()}
              >
                <CheckIcon className="size-3.5" weight="bold" />
                Keep
              </Button>
              <Button
                type="button"
                size="sm"
                variant="neutral"
                onClick={() => void handleRevertRevision()}
              >
                <ArrowCounterClockwiseIcon className="size-3.5" weight="bold" />
                Revert
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      {fanOutLine}

      {composerBlock}

      {/* The ACTIVE platform's first problem, as a sentence, plus the quality
          pill below it — both now rendered UNDER the fused chips+freeform
          composer rather than between the chips and the freeform prompt.
          Before the P2 fusion (HANDOFF-EDITOR-UNIFICATION-2026-08-12 §8.9/
          §8.14) this readiness sentence and the quality pill were the two
          things wedged between the two caption-editing controls; now that
          the controls are one, they read as status directly under the tool
          that produced them, not a detour between two doors to the same
          room.
          A readiness chip here would be a third copy of the platform name
          (strip, chip, caption label) and would badge the healthy default;
          the strip already carries per-platform state, and silence there
          already means ready. What a badge can never do is say WHAT to fix,
          so only the sentence belongs in the rail. Rendered only when there
          IS an issue: an always-mounted live region would still claim a
          `space-y` gap while empty, and a polite region announces the
          node's insertion just as well. */}
      {failureInfo ? (
        <div className="space-y-0.5">
          <p className="text-detail text-destructive">{failureInfo.sentence}</p>
          {failureInfo.detail && failureInfo.kind !== 'timeout' ? (
            <FailureDetailsLink detail={failureInfo.detail} />
          ) : null}
        </div>
      ) : null}

      {outcomeActions}

      {partial ? (
        <div className="space-y-1.5">
          <p className="text-detail text-warning">{partialLine}</p>
          {!activeUnsent && unresolvedTargets.length === 0 ? (
            <UnsentPlatformsAction
              platformLabels={partial.unsent.map((entry) => platformLabel(entry.platform))}
              sentLabels={partial.sent.map(platformLabel)}
              isSending={isPublishing}
              onConfirm={handleSendUnsent}
            />
          ) : null}
        </div>
      ) : null}

      {/* Per-platform timing — "Post at a different time" for the active
              platform, or its time chip (✕ clears just this platform). */}
      {perPlatformTiming}

      {/* Media manager — F-132 human-in-the-loop media affordances.
              Add / remove / reorder media + per-image edit & variation
              (in-place via the unified /api/generate-image edit path). Hidden
              entirely once fully published — the left preview already shows
              the live media inline, so a read-only thumbnail strip here was
              the same asset shown twice for no reason. Partial posts (some
              platforms still pending) keep it: those DO still need editing. */}
      {isEditable || isInFlightPartial || (hasMedia && !isPublished) ? (
        <PostMediaManager
          hasLinkCard={Boolean(linkedInArticleCard(post))}
          ref={postMediaManagerRef}
          mediaUrls={post.media_urls ?? []}
          childIds={childStates.map((child) => child.childId)}
          selectedIndex={safeCarouselActiveIndex}
          onSelectChild={setCarouselActiveIndex}
          editable={(isEditable || isInFlightPartial) && !activePlatformLocked}
          onChange={handleMediaChange}
          accessToken={session?.access_token}
          userId={session?.user?.id}
          aspectRatio={perPlatformAspect}
          postId={post.id}
          status={status}
          mediaKeys={(post as { media_keys?: unknown }).media_keys ?? {}}
          expectedUpdatedAt={(post as { updated_at?: string }).updated_at}
          brandKit={brandKit?.brand_guidelines ?? null}
          commitMediaKeys={commitMediaKeysTransform}
          altTexts={childStates.map((child) => child.altText)}
          onAltTextChange={handleAltTextChange}
          allowDocuments={platforms.includes('linkedin')}
        />
      ) : null}

      {videoCoverEditorEnabled && isVideoPost && activePlatform ? (
        coverResolutionIssue ? (
          <div className="flex min-h-14 items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/[0.04] px-3 py-2.5">
            <WarningCircleIcon className="size-5 shrink-0 text-destructive" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <p className="text-caption font-medium text-foreground">
                Cover destination unavailable
              </p>
              <p className="text-detail text-muted-foreground">{coverResolutionIssue}</p>
              <Button variant="link" size="chip" className="mt-1 px-0" asChild>
                <a href="/settings/integrations">Manage accounts</a>
              </Button>
            </div>
          </div>
        ) : (
          <TargetCoverEditor
            targetId={coverTargetId}
            videoUrl={post.media_urls?.[0] ?? null}
            durationSeconds={post.video_duration_seconds}
            onReview={() => setCoverEditorOpen(true)}
            onSummaryChange={setTargetCoverSummary}
          />
        )
      ) : null}

      {/* AI refine actions moved into the ✨ popover in the caption header
              above (progressive disclosure) — no permanent chip row here. */}

      {/* Draft planning date — calendar placement, not publish scheduling. */}
      {status === 'draft' ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-lg border border-border/40 bg-muted/20 px-3 py-2 text-left text-detail text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground/80 disabled:opacity-60"
              disabled={isPlanningDraft}
            >
              <CalendarIcon className="size-3.5 shrink-0" />
              <span className="min-w-0 flex-1">
                {plannedDraftDate ? (
                  <>
                    Planned for{' '}
                    <span className="font-medium text-foreground/85">
                      {format(plannedDraftDate, 'MMM d, yyyy h:mm a')}
                    </span>
                  </>
                ) : (
                  <span className="font-medium text-foreground/85">Plan on calendar</span>
                )}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent
            side={isMobile ? undefined : 'left'}
            align={isMobile ? 'start' : 'end'}
            className="w-auto max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-0"
          >
            <div className="border-b border-border/40 px-3 py-2.5">
              <p className="text-caption font-medium text-foreground">
                {plannedDraftDate ? 'Move on calendar' : 'Plan on calendar'}
              </p>
              <p className="text-detail leading-snug text-muted-foreground">
                Stays a draft — this doesn&apos;t publish it.
              </p>
            </div>
            <SchedulePicker
              value={plannedDraftDate ?? undefined}
              pendingTime={draftPlanningTime}
              onPendingTimeChange={setDraftPlanningTime}
              onDateChange={(newDate) => {
                planDraft(
                  { id: post.id, scheduledAt: newDate.toISOString() },
                  { onSuccess: () => refetch() },
                );
              }}
              onTimeChange={(newDate) => {
                planDraft(
                  { id: post.id, scheduledAt: newDate.toISOString() },
                  { onSuccess: () => refetch() },
                );
              }}
              minDate={new Date()}
              markedDates={scheduledDates}
              disabled={isPlanningDraft}
              timeLabel="Draft time"
              suggestion={
                suggestedTime
                  ? {
                      ...suggestedTime,
                      onApply: () =>
                        planDraft(
                          { id: post.id, scheduledAt: suggestedTime.at.toISOString() },
                          { onSuccess: () => refetch() },
                        ),
                    }
                  : null
              }
              onAutoSchedule={() => handleAutoSchedule(makeAutoScheduleWrite(planDraft))}
              autoScheduleHint={autoScheduleHint}
              onUpdateAutoScheduleSettings={() => openSettings('automations')}
            />
          </PopoverContent>
        </Popover>
      ) : null}

      {/* Scheduled date — picker for scheduled, static for published */}
      {post.scheduled_at && status === 'scheduled' ? (
        /* Readout, not a second picker. This row used to open its own copy of
           the schedule calendar, which the "Reschedule publish" panel in the
           footer already owns — same write, same date, two grids. The one in
           the footer wins because it is the only one that checks per-platform
           readiness first; this states the answer and points at it. */
        <div className="flex w-full items-center gap-2 rounded-lg border border-border/40 bg-muted/30 px-3 py-2 text-detail text-muted-foreground">
          <CalendarIcon className="size-3.5 shrink-0" />
          <span className="min-w-0 flex-1">
            Scheduled for{' '}
            <span className="font-medium text-foreground/85">
              {format(new Date(post.scheduled_at), 'MMM d, yyyy h:mm a')}
            </span>
          </span>
          <button
            type="button"
            onClick={() => {
              setPublishActionMode('schedule_publish');
              setPublishActionPanel('schedule_publish');
            }}
            className="shrink-0 rounded px-1 font-medium text-muted-foreground/80 transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Change
          </button>
        </div>
      ) : null}

      {isLiveOrPartial && post.published_at ? (
        <div className="flex items-center gap-2 text-detail text-muted-foreground">
          <CalendarIcon className="size-3.5" />
          Published {format(new Date(post.published_at), 'MMM d, yyyy h:mm a')}
        </div>
      ) : null}

      {/* "Sales from this post" — above the F-134 engagement metrics on
          purpose: money is the answer the creator came for, and likes are the
          proxy they settle for. Same `isLiveOrPartial` gate as the block
          below, since a draft has nothing to attribute yet; the card renders
          null on its own when the account has no Shopify store, so there is no
          second guard here to keep in sync. */}
      {isLiveOrPartial ? <PostRevenueCard postId={post.id} /> : null}

      {/* F-134: prefer the real post_metrics_daily aggregate; fall back to
              the social_posts.metrics jsonb; otherwise show an honest hint so
              the user knows analytics land once the platform reports them.
              Multi-platform: the aggregate line stays as a consolidated
              summary (matches Buffer/Hootsuite's "totals up top") while the
              grid below follows the SAME platform tab the caption/preview
              already switch on — one tab strip drives everything, no second
              analytics-only switcher to learn. */}
      {isLiveOrPartial ? (
        hasDailyMetrics ? (
          <div className="space-y-2">
            {metricsCoverage.totalDestinations > 0 ? (
              <p className="text-detail text-muted-foreground/70">
                {metricsCoverage.reportingDestinations} of {metricsCoverage.totalDestinations}{' '}
                published destinations reporting
              </p>
            ) : null}
            {isMultiPlatform ? (
              <p className="text-detail text-muted-foreground/70">
                Across all platforms:{' '}
                {Object.entries(dailyMetrics)
                  .filter(([key]) => key in METRIC_LABELS)
                  .map(
                    ([key, value]) =>
                      `${formatMetricNumber(value)} ${METRIC_LABELS[key].toLowerCase()}`,
                  )
                  .join(' · ')}
              </p>
            ) : null}
            {activePlatform && dailyMetricsByPlatform[activePlatform] ? (
              <>
                <MetricsGrid metrics={dailyMetricsByPlatform[activePlatform]} />
                <DestinationMetrics
                  destinations={metricDestinations}
                  metricsByDestination={dailyMetricsByDestination}
                  platform={activePlatform}
                />
              </>
            ) : isMultiPlatform ? (
              <p className="text-detail text-muted-foreground/70">
                No analytics reported yet for {platformLabel(activePlatform ?? '')}.
              </p>
            ) : (
              <MetricsGrid metrics={dailyMetrics} />
            )}
          </div>
        ) : metrics && Object.keys(metrics).length > 0 ? (
          <MetricsGrid metrics={metrics} />
        ) : (
          <p className="text-detail text-muted-foreground/70">
            Analytics will appear here once the platform reports them — usually within a day of
            publishing.
          </p>
        )
      ) : null}
    </>
  );

  // Footer — secondary icon cluster + status-aware primary CTA (Post now /
  // Save draft / View live). shrink-0 so it pins to the bottom of both layouts.
  const footer = targetId ? (
    <div className="border-t border-border/60 bg-background/60 px-4 py-3 text-detail text-muted-foreground">
      Account-specific preview · Publishing and scheduling stay read-only here until Mora can
      address this exact target.
    </div>
  ) : studioPanel ? (
    <div className="border-t border-border/60 bg-background/60">{studioPanel}</div>
  ) : (
    <div className="border-t border-border/60 bg-background/60">
      {readinessBlock ? <div className="px-3 pt-2.5">{readinessBlock}</div> : null}
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
        {/* Secondary actions collapse into ONE overflow — duplicate / delete /
                save-to-project were three loud icons competing with the primary
                CTA. A single ⋯ (the same pattern as the draft-row menu) keeps the
                footer calm so the publish action reads as THE action. */}
        <div className="flex items-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {/* bg-foreground/[0.06] at rest (not just on hover) — three bare
                dots with a fully-transparent circle read as unstyled text,
                not a tappable icon button. Matches the always-visible
                background the safe-zones toggle above uses when active. */}
              <CircleIconButton
                size="sm"
                ariaLabel="More actions"
                tooltip="More"
                className="bg-foreground/[0.06]"
              >
                <DotsThreeIcon weight="bold" />
              </CircleIconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              side="top"
              className="w-48"
              onCloseAutoFocus={(event) => {
                const pending = afterMenuActionRef.current;
                if (!pending) return;
                afterMenuActionRef.current = null;
                event.preventDefault();
                if (pending === 'project') setSaveProjectOpen(true);
                else setTitleDraft(explicitTitle ?? '');
              }}
            >
              {isEditable && !breadcrumbs?.length ? (
                <DropdownMenuItem
                  onSelect={() => {
                    afterMenuActionRef.current = 'rename';
                  }}
                  className={MENU_ITEM_MOBILE}
                >
                  <PencilSimpleIcon className="size-3.5 shrink-0" weight="regular" />
                  Rename
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem
                onSelect={() => void handleDuplicate()}
                className={MENU_ITEM_MOBILE}
              >
                <CopyIcon className="size-3.5 shrink-0" weight="regular" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  afterMenuActionRef.current = 'project';
                }}
                className={MENU_ITEM_MOBILE}
              >
                <CardsThreeIcon className="size-3.5 shrink-0" weight="regular" />
                Add to project
              </DropdownMenuItem>
              {canSendPostForApproval(status) && approvalCandidate && post.brand_kit_id ? (
                <DropdownMenuItem
                  onSelect={() => setTimeout(() => setApprovalOpen(true), 0)}
                  className={MENU_ITEM_MOBILE}
                >
                  <SealCheckIcon className="size-3.5 shrink-0" weight="regular" />
                  Send for approval
                </DropdownMenuItem>
              ) : null}
              {canSeePostPerformance(status) ? (
                <DropdownMenuItem asChild className={MENU_ITEM_MOBILE}>
                  <Link to={POST_PERFORMANCE_HREF}>
                    <ChartLineUpIcon className="size-3.5 shrink-0" weight="regular" />
                    See performance
                  </Link>
                </DropdownMenuItem>
              ) : null}
              {status === 'scheduled' || isFailed ? (
                <DropdownMenuItem
                  disabled={isUnscheduling}
                  aria-disabled={isPreviewAccount || undefined}
                  title={isPreviewAccount ? PREVIEW_ACCOUNT_MESSAGE : undefined}
                  onSelect={() => handleUnschedule()}
                  className={MENU_ITEM_MOBILE}
                >
                  <CalendarXIcon className="size-3.5 shrink-0" weight="regular" />
                  Move to drafts
                </DropdownMenuItem>
              ) : null}
              {isEditable || isFailed ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    variant="destructive"
                    aria-disabled={isPreviewAccount || undefined}
                    title={isPreviewAccount ? PREVIEW_ACCOUNT_MESSAGE : undefined}
                    onSelect={requestDelete}
                    className={MENU_ITEM_MOBILE}
                  >
                    <TrashIcon className="size-3.5 shrink-0" weight="regular" />
                    Delete
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
          {/* Controlled save-to-project picker, opened from the ⋯ item above.
                The 0-size span is just the popover anchor near the overflow. */}
          <SaveToProjectMenu
            artifactId={post.id}
            artifactKind="social_post"
            open={saveProjectOpen}
            onOpenChange={setSaveProjectOpen}
          >
            <span aria-hidden className="pointer-events-none size-0" />
          </SaveToProjectMenu>
          <Confirm
            open={confirmDeleteOpen}
            onOpenChange={setConfirmDeleteOpen}
            variant="destructive"
            title="Delete post?"
            description="This permanently removes the post. A post already published stays live on the platform."
            confirmLabel="Delete"
            onConfirm={handleDelete}
          />
          {approvalCandidate && post.brand_kit_id ? (
            <NewApprovalDialog
              open={approvalOpen}
              onOpenChange={setApprovalOpen}
              workspaceId={post.brand_kit_id}
              initialPost={approvalCandidate}
            />
          ) : null}
        </div>
        {(() => {
          // Right primary CTA. Published → "View live" (real permalink).
          // Otherwise ONE split button: primary "Post now" + a caret that
          // opens the publish schedule date-picker. Replaces the old two competing
          // CTAs ("Post now" + "Approve & schedule") — there's no approval
          // step, so "Approve" was confusing, and two buttons read as noise.
          // F-131: "View live" opens the REAL https permalink (from
          // platform_response), never the bare media id.
          const platformResults = (
            post as {
              platform_response?: Array<{ platform?: string; permalink?: string | null }>;
            }
          ).platform_response;
          const isHttp = (u: unknown): u is string =>
            typeof u === 'string' && /^https?:\/\//.test(u);
          // ONLY the active platform's own permalink. The previous
          // `?? platformResults.find(isHttp)` fallback meant that on a
          // partially-published post — Instagram delivered, LinkedIn failed —
          // selecting the LinkedIn tab still showed a green "View live" that
          // opened the INSTAGRAM post. The user is shown another platform's
          // content as proof their failed post is live.
          const liveUrl = Array.isArray(platformResults)
            ? (platformResults.find((r) => r.platform === activePlatform && isHttp(r.permalink))
                ?.permalink ?? null)
            : null;
          if (activeUnresolved || (unresolvedTargets.length > 0 && !isLiveOrPartial)) {
            return (
              <RepostAfterCheckAction
                targets={unresolvedTargets}
                isSending={isPublishing}
                onConfirm={handleRepostAfterCheck}
              />
            );
          }
          if (isLiveOrPartial && isHttp(liveUrl)) {
            const nextSibling = sessionSiblings[0];
            const nextIndexLabel = nextSibling
              ? nextSibling.scheduled_at
                ? format(new Date(nextSibling.scheduled_at), 'EEE, MMM d · h:mm a')
                : 'draft, not scheduled yet'
              : null;
            return (
              <div className="min-w-0 flex-1 basis-56">
                <PublishedMoment
                  platformLabel={getPlatformLabel(activePlatform ?? '')}
                  postedAtLabel={
                    post.published_at ? format(new Date(post.published_at), 'h:mm a') : null
                  }
                  liveUrl={liveUrl}
                  celebrate={celebratedPostId === post.id && status === 'published'}
                  next={
                    nextSibling && nextIndexLabel
                      ? {
                          label: nextIndexLabel,
                          onOpen: () =>
                            inspectorOpenSubject(
                              { kind: 'post', postId: nextSibling.id },
                              {
                                presentation: inspectorPresentation,
                                breadcrumbs: inspectorBreadcrumbs.length
                                  ? [
                                      ...inspectorBreadcrumbs.slice(0, -1),
                                      { label: postBreadcrumbLabel(nextSibling) },
                                    ]
                                  : undefined,
                              },
                            ),
                        }
                      : null
                  }
                  backToChatHref={
                    originSessionId
                      ? `/?resumeSession=${encodeURIComponent(originSessionId)}`
                      : null
                  }
                />
              </div>
            );
          }
          if (partial && activeUnsent && unresolvedTargets.length === 0) {
            return (
              <UnsentPlatformsAction
                platformLabels={partial.unsent.map((entry) => platformLabel(entry.platform))}
                sentLabels={partial.sent.map(platformLabel)}
                isSending={isPublishing}
                onConfirm={handleSendUnsent}
              />
            );
          }
          if (isFailed) {
            const cta = STATUS_PRIMARY_CTA[status as 'failed' | 'failed_connection_removed'];
            const PrimaryIcon = cta.Icon;
            const needsReconnect =
              status === 'failed_connection_removed' || failureInfo?.action === 'reconnect';
            return (
              <Button
                variant="neutral"
                size="sm"
                disabled={isScheduling}
                onClick={needsReconnect ? handleReconnect : handleRetry}
              >
                {needsReconnect ? (
                  <PlugsConnectedIcon className="size-3.5" weight="regular" />
                ) : (
                  <PrimaryIcon className="size-3.5" weight="bold" />
                )}
                {needsReconnect ? 'Reconnect' : cta.label}
              </Button>
            );
          }
          if (!isEditable) return null;
          const coverBlocker =
            videoCoverEditorEnabled && isVideoPost
              ? coverTargetsQuery.isLoading
                ? 'Preparing destination cover options.'
                : (coverResolutionIssue ??
                  coverReadinessIssue ??
                  (targetCoverSummary?.blocking ? targetCoverSummary.issue : null))
              : null;
          // Gate on "at least one enabled platform can send", not the focused
          // tab — `post_now` now fans out to every enabled platform, so a
          // blocked ACTIVE platform must not disable posting the other two
          // that are ready. `scheduleReadinessResults` is already the
          // enabled-platform slice of the one shared `platformReadinessMap`.
          const canPublishActive = Boolean(
            enabledSendPlatforms.length > 0 &&
            scheduleReadinessResults.some((result) => result.canPublish) &&
            !coverBlocker,
          );
          const canSchedule = canPublishActive;
          const scheduleSkippedLines = scheduleReadinessResults
            .filter((result) => !result.canPublish)
            .map(
              (result) =>
                result.blockingIssues[0]?.message ??
                readinessFallbackTitle(result) ??
                `${platformLabel(result.platform)} isn't ready to schedule.`,
            );
          const scheduleLabel = status === 'scheduled' ? 'Reschedule' : 'Schedule to publish';
          const disabledActionTitle =
            coverBlocker ??
            (enabledSendPlatforms.length === 0
              ? 'Choose at least one platform before posting'
              : !canPublishActive
                ? 'No platform is ready to post.'
                : undefined);
          // Multi-platform post_now preview: which enabled platforms will
          // actually send, and — for the ones that won't — a short, named
          // reason (paused, or the same blocking issue the tab chip shows).
          const postNowReadyPlatforms = enabledSendPlatforms.filter(
            (platformId) => platformReadinessMap[platformId]?.canPublish,
          );
          const postNowExcludedReasons = platforms
            .map((platformId) =>
              excludedPostNowReason(
                platformId,
                platformReadinessMap[platformId],
                disabledPlatforms.includes(platformId),
              ),
            )
            .filter((reason): reason is string => reason != null);
          // More than 3 excluded reads as a wall of red text rather than a
          // quiet caveat — name the first two and roll the rest into a count.
          const POST_NOW_MAX_EXCLUDED_LINES = 3;
          const postNowExcludedLines =
            postNowExcludedReasons.length > POST_NOW_MAX_EXCLUDED_LINES
              ? [
                  ...postNowExcludedReasons.slice(0, 2),
                  `and ${postNowExcludedReasons.length - 2} more.`,
                ]
              : postNowExcludedReasons;
          const postNowTitle =
            postNowReadyPlatforms.length === 1
              ? `Post to ${platformLabel(postNowReadyPlatforms[0])} now?`
              : postNowReadyPlatforms.length > 1
                ? `Post to ${postNowReadyPlatforms.length} platforms now?`
                : 'Post now?';
          const disabledScheduleTitle =
            scheduleReadinessIssue?.message ??
            scheduleReadinessResults.map(readinessFallbackTitle).find((title) => title != null) ??
            (enabledSendPlatforms.length === 0
              ? 'Choose at least one platform before scheduling'
              : undefined) ??
            disabledActionTitle;
          // The primary CTA used to render enabled, in the strongest weight on
          // the screen, directly beneath a red sentence saying the platform is
          // not connected — two controls on one screen contradicting each other,
          // and the button could not possibly do what it said. When nothing can
          // send AND a missing connection is why, the CTA becomes the action that
          // will actually work. Every OTHER blocker (no media, retrying, paused)
          // deliberately keeps the existing two-step: the trigger opens a panel
          // that NAMES the blocker and holds a disabled confirm. Disabling the
          // trigger there was tried and reverted — it removed the only place the
          // reason is spelled out, which is worse than the thing it fixed.
          const unconnectedPlatform = !canPublishActive
            ? scheduleReadinessResults.find((result) =>
                result.blockingIssues.some((issue) => issue.code === 'platform_not_connected'),
              )?.platform
            : undefined;
          const actionMeta: Record<
            PublishActionMode,
            {
              label: string;
              /** Spoken name, when it should be more specific than the visible
               *  label. Screen-reader users reach this button without the rail's
               *  surrounding context, so the platform is worth naming there even
               *  where it would be redundant on screen. */
              ariaLabel?: string;
              description: string;
              Icon: typeof PaperPlaneTiltIcon;
            }
          > = {
            post_now: {
              // Stays a bare verb on screen. Naming the platform here was pure
              // redundancy — the strip, the preview chrome and the whole rail
              // are already that platform — and it advertised a temporary
              // single-platform limit as though it were information, while
              // making the primary action the widest object in the footer.
              label: 'Post now',
              // Both follow the button's real blast radius, which is every
              // enabled platform — not the one in focus. Naming a single
              // platform here contradicted the popover directly beneath it.
              ariaLabel: !canPublishActive
                ? 'Post now'
                : postNowReadyPlatforms.length > 1
                  ? `Post to ${postNowReadyPlatforms.length} platforms now`
                  : `Post to ${platformLabel(postNowReadyPlatforms[0] ?? activePlatform)} now`,
              description:
                postNowReadyPlatforms.length > 1
                  ? `Publish to ${postNowReadyPlatforms.length} platforms immediately`
                  : `Publish to ${platformLabel(postNowReadyPlatforms[0] ?? activePlatform)} immediately`,
              Icon: PaperPlaneTiltIcon,
            },
            schedule_publish: {
              label: status === 'scheduled' ? 'Reschedule publish' : 'Schedule publish',
              description: 'Pick a publish date and time',
              Icon: CalendarIcon,
            },
          };
          const activeAction = actionMeta[publishActionMode];
          const ActiveActionIcon = activeAction.Icon;

          if (activeCaptionIsDirty) {
            return (
              <Button
                variant="neutral"
                size="sm"
                disabled={isOperating}
                onClick={() => {
                  void handleSaveDraft();
                }}
              >
                <CheckIcon className="size-3.5" weight="bold" />
                Save draft
              </Button>
            );
          }

          return (
            <div className="flex min-w-0 items-center justify-end gap-2">
              <Popover
                open={publishActionPanel != null}
                onOpenChange={(open) => setPublishActionPanel(open ? publishActionMode : null)}
              >
                <ButtonGroup className="overflow-hidden rounded-full">
                  {unconnectedPlatform ? (
                    <Button
                      variant="neutral"
                      size="sm"
                      className="h-8"
                      aria-label={`Connect ${platformLabel(unconnectedPlatform)}`}
                      disabled={isPublishing}
                      title={`${platformLabel(unconnectedPlatform)} is not connected for publishing. Connect it to publish this post.`}
                      onClick={handleReconnect}
                    >
                      <PlugsConnectedIcon className="size-3.5" weight="regular" />
                      <span>Connect {platformLabel(unconnectedPlatform)}</span>
                    </Button>
                  ) : (
                    <PopoverTrigger asChild>
                      <Button
                        variant="neutral"
                        size="sm"
                        className="h-8"
                        aria-label={activeAction.ariaLabel ?? activeAction.label}
                        disabled={isPublishing}
                        title={activeAction.description}
                        onClick={() => setPublishActionPanel(publishActionMode)}
                      >
                        {isPublishing ? (
                          <ClockIcon className="size-3.5" weight="regular" />
                        ) : (
                          <ActiveActionIcon className="size-3.5" weight="regular" />
                        )}
                        <span>{isPublishing ? 'Posting...' : activeAction.label}</span>
                      </Button>
                    </PopoverTrigger>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="neutral"
                        size="sm"
                        className="h-8 px-2"
                        aria-label="Change publish action"
                        disabled={isPublishing}
                        title="Change publish action"
                      >
                        <CaretDownIcon className="size-3.5" weight="bold" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-72 p-1">
                      <DropdownMenuGroup>
                        {/* A CHECK on the selected row, not a Radix radio dot —
                          see select-pill.tsx and TOOLBAR-DOCTRINE.md §12.3. */}
                        <DropdownMenuItem
                          className="items-start justify-between gap-3 py-2"
                          disabled={Boolean(unconnectedPlatform)}
                          onSelect={() => {
                            setPublishActionMode('post_now');
                            setPublishActionPanel(null);
                          }}
                        >
                          <span className="min-w-0">
                            <span className="block text-caption font-medium text-foreground">
                              Post now
                            </span>
                            <span className="block text-detail leading-snug text-muted-foreground">
                              {unconnectedPlatform
                                ? `Connect ${platformLabel(unconnectedPlatform)} first`
                                : 'You confirm first; it goes to every channel that is ready'}
                            </span>
                          </span>
                          {publishActionMode === 'post_now' && (
                            <CheckIcon
                              className="size-3.5 shrink-0 text-muted-foreground"
                              weight="bold"
                              aria-hidden
                            />
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="items-start justify-between gap-3 py-2"
                          disabled={Boolean(unconnectedPlatform)}
                          onSelect={() => {
                            setPublishActionMode('schedule_publish');
                            setPublishActionPanel(null);
                          }}
                        >
                          <span className="min-w-0">
                            <span className="block text-caption font-medium text-foreground">
                              {scheduleLabel}
                            </span>
                            <span className="block text-detail leading-snug text-muted-foreground">
                              {unconnectedPlatform
                                ? `Connect ${platformLabel(unconnectedPlatform)} first`
                                : "Pick a day and time, or take Mora's suggestion"}
                            </span>
                          </span>
                          {publishActionMode === 'schedule_publish' && (
                            <CheckIcon
                              className="size-3.5 shrink-0 text-muted-foreground"
                              weight="bold"
                              aria-hidden
                            />
                          )}
                        </DropdownMenuItem>
                      </DropdownMenuGroup>
                      {status === 'scheduled' ? (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuGroup>
                            <DropdownMenuItem
                              variant="destructive"
                              disabled={isUnscheduling}
                              onSelect={() => {
                                handleUnschedule();
                                setPublishActionPanel(null);
                              }}
                            >
                              <CalendarXIcon className="size-3.5 shrink-0" weight="regular" />
                              Move to drafts
                            </DropdownMenuItem>
                          </DropdownMenuGroup>
                        </>
                      ) : null}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </ButtonGroup>
                {/* The Schedule panel hands its padding to SchedulePicker (which
                  owns a padded grid + a footer rail); Post now is plain copy and
                  keeps its own. Widths differ because the two panels hold
                  different things — a 288px calendar column, or a paragraph. */}
                <PopoverContent
                  align="end"
                  className={cn('p-0', publishActionPanel === 'schedule_publish' ? 'w-72' : 'w-80')}
                >
                  {publishActionPanel === 'post_now' ? (
                    <div className="space-y-3 p-3">
                      <div className="space-y-1">
                        <p className="text-caption font-medium text-foreground">{postNowTitle}</p>
                        <p className="text-detail leading-snug text-muted-foreground">
                          This posts immediately and can&apos;t be undone.
                        </p>
                        {videoCoverEditorEnabled && isVideoPost && targetCoverSummary ? (
                          <p className="text-detail leading-snug text-muted-foreground">
                            Cover · {targetCoverSummary.label}
                          </p>
                        ) : null}
                        {postNowExcludedLines.map((line, index) => (
                          <p
                            key={`${index}-${line}`}
                            className="text-detail leading-snug text-muted-foreground"
                          >
                            {line}
                          </p>
                        ))}
                        {!canPublishActive && disabledActionTitle ? (
                          <p className="text-detail leading-snug text-destructive">
                            {disabledActionTitle}
                          </p>
                        ) : null}
                      </div>
                      <Button
                        variant="neutral"
                        size="sm"
                        className="w-full"
                        disabled={isPublishing || !canPublishActive}
                        onClick={async () => {
                          if (isPostNowSubmittingRef.current) return;
                          isPostNowSubmittingRef.current = true;
                          try {
                            const saved = await handleActiveCaptionSave(captionDraft);
                            if (!saved) return;
                            // Send every enabled platform — PublishService itself
                            // partitions to the ready subset and reports what it
                            // skipped, so this must never pre-filter to just the
                            // ones `postNowReadyPlatforms` already flagged ready.
                            const publishedPostId = post.id;
                            void Promise.resolve(
                              publishNow({
                                postId: publishedPostId,
                                platform: enabledSendPlatforms as PlatformId[],
                              }),
                            )
                              .then((result) => {
                                if (!result) return;
                                if (!result.held && result.outcome === 'all_published') {
                                  setCelebratedPostId(publishedPostId);
                                }
                              })
                              .catch(() => undefined);
                            setPublishActionPanel(null);
                          } finally {
                            isPostNowSubmittingRef.current = false;
                          }
                        }}
                      >
                        <PaperPlaneTiltIcon className="size-3.5" weight="bold" />
                        Post now
                      </Button>
                    </div>
                  ) : null}
                  {publishActionPanel === 'schedule_publish' ? (
                    <>
                      <div className="border-b border-border/40 px-3 py-2.5">
                        <p className="text-caption font-medium text-foreground">{scheduleLabel}</p>
                        <p className="text-detail leading-snug text-muted-foreground">
                          {status === 'draft'
                            ? 'Publishes at this time. To park it without publishing, use “Plan on calendar”.'
                            : 'This is when the post goes live.'}
                        </p>
                        {videoCoverEditorEnabled && isVideoPost && targetCoverSummary ? (
                          <p className="text-detail leading-snug text-muted-foreground">
                            Cover · {targetCoverSummary.label}
                          </p>
                        ) : null}
                        {canSchedule && scheduleSkippedLines.length > 0 ? (
                          <>
                            <p className="text-detail leading-snug text-muted-foreground">
                              {scheduleSkippedLines.length === 1
                                ? 'One platform will be skipped.'
                                : `${scheduleSkippedLines.length} platforms will be skipped.`}
                            </p>
                            {scheduleSkippedLines.map((line) => (
                              <p
                                key={line}
                                className="text-detail leading-snug text-muted-foreground"
                              >
                                {line}
                              </p>
                            ))}
                          </>
                        ) : null}
                        {!canSchedule && disabledScheduleTitle ? (
                          <p className="text-detail leading-snug text-destructive">
                            {disabledScheduleTitle}
                          </p>
                        ) : null}
                      </div>
                      <SchedulePicker
                        value={
                          status === 'scheduled' && post.scheduled_at
                            ? new Date(post.scheduled_at)
                            : undefined
                        }
                        pendingTime={publishScheduleTime}
                        onPendingTimeChange={setPublishScheduleTime}
                        onDateChange={async (newDate) => {
                          const saved = await handleActiveCaptionSave(captionDraft);
                          if (!saved) return;
                          schedulePost(
                            { id: post.id, scheduledAt: newDate.toISOString(), reportSkips: true },
                            { onSuccess: () => refetch() },
                          );
                          setPublishActionPanel(null);
                        }}
                        onTimeChange={async (newDate) => {
                          if (status !== 'scheduled') return;
                          const saved = await handleActiveCaptionSave(captionDraft);
                          if (!saved) return;
                          schedulePost(
                            { id: post.id, scheduledAt: newDate.toISOString(), reportSkips: true },
                            { onSuccess: () => refetch() },
                          );
                        }}
                        minDate={new Date()}
                        markedDates={scheduledDates}
                        disabled={isScheduling || !canSchedule}
                        timeLabel="Publish time"
                        suggestion={
                          suggestedTime && canSchedule
                            ? {
                                ...suggestedTime,
                                onApply: async () => {
                                  const saved = await handleActiveCaptionSave(captionDraft);
                                  if (!saved) return;
                                  schedulePost(
                                    {
                                      id: post.id,
                                      scheduledAt: suggestedTime.at.toISOString(),
                                      reportSkips: true,
                                    },
                                    { onSuccess: () => refetch() },
                                  );
                                  setPublishActionPanel(null);
                                },
                              }
                            : null
                        }
                        onAutoSchedule={async () => {
                          const saved = await handleActiveCaptionSave(captionDraft);
                          if (!saved) return;
                          handleAutoSchedule(makeAutoScheduleWrite(schedulePost));
                          setPublishActionPanel(null);
                        }}
                        autoScheduleHint={autoScheduleHint}
                        onUpdateAutoScheduleSettings={() => openSettings('automations')}
                      />
                    </>
                  ) : null}
                </PopoverContent>
              </Popover>
            </div>
          );
        })()}
      </div>
    </div>
  );

  if (coverEditorOpen && coverTargetId) {
    return (
      <TargetCoverEditor
        targetId={coverTargetId}
        videoUrl={post.media_urls?.[0] ?? null}
        durationSeconds={post.video_duration_seconds}
        focused
        onDone={() => {
          setCoverEditorOpen(false);
          void refetch();
        }}
        onCancel={() => setCoverEditorOpen(false)}
        onSummaryChange={setTargetCoverSummary}
      />
    );
  }

  // ── Mobile: segmented Preview / Edit — NOT a scrolled desktop port ──────────
  // The desktop 2-pane grid (preview stage | editing rail, side by side) has
  // no honest mobile equivalent at 375-430px — the previous approach stacked
  // both panes into ONE long vertical scroll (hero image, then platform/crop
  // controls, then the entire caption/media/schedule rail, then the composer)
  // which read as a cramped desktop port, not a native mobile editor: users
  // had to scroll PAST the photo to find caption tools, and scroll back up
  // to compare the two (live review — "scrolling down is just not good
  // design"). Instagram / Facebook / TikTok's own mobile composers all use
  // the same resolution: a segmented toggle swaps between "the thing you're
  // posting" and "the details about it", each filling the available height
  // on its own. Preview tab = image/video + platform + crop controls,
  // vertically centered, no scroll needed for a single photo. Edit tab =
  // caption + media + schedule + AI tools, which genuinely needs its own
  // scroll (that content was always going to outgrow one screen). The
  // publish footer stays pinned in BOTH tabs — it's the one action that
  // should never require a tab switch to reach. */
  if (isMobile) {
    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden bg-background">
        {headerBar}
        <div
          className={cn(
            'flex shrink-0 justify-center py-2',
            // The strip below carries its own baseline rule; two faint lines
            // ~40px apart is the exact "too many lines" the header border was
            // already dropped for on mobile.
            platformTabStrip ? null : 'border-b border-border/40',
          )}
        >
          <SegmentedToggle
            value={mobileTab}
            onValueChange={setMobileTab}
            options={[
              { value: 'preview', label: 'Preview' },
              { value: 'edit', label: 'Edit' },
            ]}
            variant="default"
            size="sm"
          />
        </div>
        {/* Platform strip, rendered ONCE and pinned under the view-mode toggle
            so it is present in BOTH mobile tabs. It used to live inside the
            Preview tab's controls strip, which meant the Edit tab — where the
            caption actually is — had no platform switcher at all: you edited
            LinkedIn's caption with nothing on screen saying it was LinkedIn's.
            The two rows use DIFFERENT grammars on purpose and must stay that
            way: Preview/Edit is variant="default" (a pill — a VIEW-MODE
            control), the platform strip is variant="tabs" (an underline — a
            WORK-ITEM control). Pill-over-tabs reads as "which view" above
            "which platform"; two stacked pill rows would read as one confused
            control. Please don't harmonise them. */}
        {platformTabStrip ? <div className="shrink-0 px-4">{platformTabStrip}</div> : null}
        {mobileTab === 'preview' ? (
          /*
            Mobile's stage is a flex COLUMN with the scroller inside it, where
            it used to be the scroller itself. That restructure is what lets
            the controls sit outside the scrolling region — see the desktop
            branch's comment for why that, and not a better constant, is the
            fix. Mobile is the branch that benefits most: its strip is 46px,
            not 28, and it is where the old reserve drifted furthest.
          */
          <div
            className={cn(
              'preview-stage flex min-h-0 flex-1 flex-col overscroll-contain',
              ARTIFACT_PREVIEW_STAGE,
            )}
          >
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              <div className="flex min-h-full flex-col items-center justify-center px-4 py-6">
                {previewBlock}
              </div>
            </div>
            <div
              ref={stageExtrasRef}
              className="flex shrink-0 flex-col items-center gap-3 px-4 pb-3"
            >
              {failureInfo ? (
                <p className="text-center text-detail text-destructive">{failureInfo.sentence}</p>
              ) : null}
              {partialLine ? (
                <p className="text-center text-detail text-warning">{partialLine}</p>
              ) : null}
              {outcomeActions ? <div className="w-full space-y-2">{outcomeActions}</div> : null}
              {controlsStrip}
              {zoomSliderRow}
            </div>
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <div className="space-y-6 px-4 pb-4 pt-5">{railSections}</div>
          </div>
        )}
        {footer}
      </div>
    );
  }

  // ── Desktop: full-width header + 2-pane grid (preview stage | editing rail) ──
  // The header spans the FULL frame width (breadcrumb/title left, status + close
  // right), then the two panes sit below it — the same shell chrome the image,
  // video, and document workspace viewers use (and the same layout the mobile
  // post editor already uses above). Previously the header was buried inside the
  // 440px rail, so the post editor was the only artifact type whose frame had no
  // top bar — the left preview pane floated with dead space above it and no
  // frame-level wayfinding/close. One header, one frame (Hard Rule 9 parity).
  //
  // Rail width is tiered, not a flat 440px: this grid renders at every width
  // from 768px up (mobile branch takes over below that), and a flat 440px
  // right column eats ~45-55% of the frame at tablet/narrow-desktop widths
  // (768-1279px) — the editing rail has nowhere near that much content, and
  // the preview pane (which now uses its actual available width) gets
  // crushed against it with barely any breathing room. 440px is only earned
  // back at true desktop widths (xl:, 1280px+) where it reads as the ~30%
  // share it was tuned for. `minmax(320px,1fr)` on the left pane is a floor,
  // not a target — it stops the preview from ever being squeezed thinner
  // than a small phone-mockup needs, forcing the grid to overflow-scroll
  // instead of crushing the pane if the frame is ever narrower than that.
  const sourceHref = sourceArticleHref(
    (post as { source_article_id?: string | null }).source_article_id,
  );
  const planBacklinkHref = planHref(postPlanLink?.projectId);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {headerBar}
      {sourceHref || planBacklinkHref ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-border/40 px-4 py-1.5 text-detail text-muted-foreground">
          {sourceHref ? (
            <Link to={sourceHref} className="inline-flex items-center gap-1 hover:text-foreground">
              <ArticleIcon className="size-3" weight="regular" aria-hidden="true" />
              From a Discover story
            </Link>
          ) : null}
          {planBacklinkHref ? (
            <Link
              to={planBacklinkHref}
              className="inline-flex items-center gap-1 hover:text-foreground"
            >
              <CardsThreeIcon className="size-3" weight="regular" aria-hidden="true" />
              Part of plan {postPlanLink?.planName ?? 'this project'}
            </Link>
          ) : null}
        </div>
      ) : null}
      <div className="grid min-h-0 flex-1 grid-cols-[minmax(320px,1fr)_320px] lg:grid-cols-[minmax(320px,1fr)_380px] xl:grid-cols-[minmax(320px,1fr)_440px]">
        {/* LEFT PANE — preview stage. Platform switcher + crop chips sit inline
            under the asset (no isolated top stripe). The shared canvas token
            gives the platform card a quiet recessed table, matching image/video
            inspection without importing LinkedIn's heavy black lightbox. */}
        <div className={cn('preview-stage relative flex min-h-0 flex-col', ARTIFACT_PREVIEW_STAGE)}>
          {/* Scroll container + min-h-full centering child: centers when it fits,
              scrolls cleanly from the top when it can't. Placement/ratio
              controls render HERE, inside the centered column, not pinned to
              the pane's bottom edge — they shape the card directly above
              them, so `controlsStrip`'s own maxWidth (matched to the card,
              see previewMaxWidth) reads as "controls for this preview"
              instead of floating chrome anchored to nothing (founder
              review). */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="flex min-h-full flex-col items-center justify-center px-4 py-4">
              {previewBlock}
            </div>
          </div>
          {/*
            🔴 THE CONTROLS LIVE OUTSIDE THE SCROLLER, and that is the whole
            fix — not the padding, not the constants.

            They used to be the last children INSIDE the scrolling column, so
            their visibility depended on the card above them being sized
            correctly, which depended on a reserve assembled from five
            hand-maintained pixel constants. Three drift incidents are recorded
            in the comments on `previewStageReservedHeight`; the founder
            reported a fourth on 2026-08-21 — "you can't even see it on this
            page."

            Measuring the rows (which this still does) was not enough, and the
            reason is worth writing down because it is a genuine circular
            constraint rather than another stale number:
            `platformPreviewChromeHeight` returns a FIXED per-platform guess
            for the card's own non-media height — 140 for instagram-post — but
            the real value depends on how many lines the caption wraps to,
            which depends on the card's width, which depends on the media
            height, which depends on this very reserve. Measured live after the
            row fix: still 14px over on a long-caption Instagram post.

            You cannot close a loop like that with a better constant. So the
            row that must never be cut off simply stops living in the region
            that can cut it off. It is now a flex sibling of the scroller: it
            always occupies its real height, the card gets what remains, and if
            the chrome estimate is still a few pixels out, the CARD scrolls
            slightly inside its own scroller instead of the controls
            disappearing.

            It stays directly under the card and still matches its width, so
            the earlier founder review that moved it here from the pane's
            bottom edge — "controls FOR the card, not floating chrome anchored
            to nothing" — is preserved, not reversed.
          */}
          <div ref={stageExtrasRef} className="flex shrink-0 flex-col items-center gap-2 px-4 pb-4">
            {controlsStrip}
            {zoomSliderRow}
          </div>
        </div>

        {/* RIGHT RAIL — editing sections + composer + actions (header now lives
            in the full-width bar above). A prior pass matched this pane's
            background exactly to the left preview's (bg-background, no
            fill), reasoning the border-l should be the only separator. In
            practice that read as one undifferentiated white/dark wash with
            no sense of "this side is the workspace" (founder review, live
            walkthrough) — a very light muted fill, well short of the app
            sidebar's contrast, restores just enough visual separation
            without reversing the border-only intent entirely. */}
        <div className="flex min-h-0 flex-col border-l border-border/40 bg-muted/10">
          {/* Platform tabs sit at the head of the rail, directly above the
              caption card they govern. The rail had ~430px of dead vertical
              space between the schedule row and the composer at 1440x900, so
              this costs nothing — nothing below it was compressed to fit. */}
          {platformTabStrip ? <div className="shrink-0 px-4 pt-3">{platformTabStrip}</div> : null}
          <div className="flex-1 min-h-0 space-y-3 overflow-y-auto px-4 py-3">{railSections}</div>
          {footer}
        </div>
      </div>
    </div>
  );
};
